import { useState, useEffect, useRef, useCallback, useContext, lazy, Suspense, useMemo } from "react";
import { Routes, Route, Link, Navigate, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import TabKeepAlive from "./components/TabKeepAlive";
import { ThemeContext, useTheme, ALL_THEMES, DEFAULT_THEME_ID, getTheme } from "./appContext";
import { applyThemeCSSVars, SMC_THEMES, THEME_SWATCHES, THEME_ICONS } from "./design";
import { mono, EqBox, Card, Label, Stat, Row, Btn } from "./pages/appHelpers";
import EnergyPage from "./pages/EnergyPage";
import PropellantPage from "./pages/PropellantPage";
import OvershootPage from "./pages/OvershootPage";
import CyclePage from "./pages/CyclePage";
import DeadMassPage from "./pages/DeadMassPage";
import AttitudePage from "./pages/AttitudePage";
import MyNewPage from "./pages/MyNewPage";
import ConstantsPage from "./pages/ConstantsPage";
import IonVsEPADCS from "./pages/IonVsEPADCS";
import SatellitePlaybook from "./pages/SatellitePlaybook";
import MissionPlanner from "./pages/MissionPlanner";
import IndiaCoverageAnimation from "./pages/IndiaCoverageAnimation";
import EMSSPhysicsSimulator from "./pages/EMSSPhysicsSimulator";
import PendulumOrbitRise from "./pages/PendulumOrbitRise";
import PendulumPage from "./pages/pendulum";
import PendulumSwinging from "./pages/pendulumSwinging";
import OrbitalPendulumExp from "./pages/orbital_pendulum_exp";
import EPADCSOrbitSim from "./pages/EPADCSOrbitSim";
import EMMAOrbitRaise from "./pages/EMMAOrbitRaise";
import EMSSValidation from "./pages/EMSSValidation";
import EMSCoilDriverPage from "./pages/EMSCoilDriverPage";
import { useCloudSync } from "./useCloudSync";
import { loadPDFJS } from "./pages/pdfUtils";
import CADViewer, { isCADFile } from "./pages/CADViewer";
// Redesign (Lovable): new loading screen. Classic one kept for rollback (smc_design=v1).
import SplashScreenV2 from './components/SplashScreenV2/SplashScreenV2';
import SplashScreenV1 from './components/SplashScreen/SplashScreen';
import ShellHeaderV2 from './components/ShellV2/ShellHeaderV2';
import useSectionBadges from './hooks/useSectionBadges';
import SpaLinkInterceptor from './components/SpaLinks/SpaLinkInterceptor';
import TaskHost from './components/TaskReview/TaskHost';
import TaskReviewBadge from './components/TaskReview/TaskReviewBadge';
import { setTaskStatus, boardStatus } from './utils/taskClosure';
import ReportViewToggle, { useReportView, reportGridColumns } from './components/ReportView/ReportViewToggle';
import { isV2, setDesignVersion } from './design/designFlags';
import { notifPermission, requestNotifPermission } from './utils/notifSupport';
import { tabFromLocation, tabPath, useRoutedTab } from './hooks/useRoutedTab';
import MeetingsPage, { IncomingCallOverlay, useCallListener } from './pages/MeetingsPage';
import MailboxPage from './pages/MailboxPage';
import DailyTasksPage from './pages/DailyTasksPage';
import TeamPortalsPage from './pages/TeamPortalsPage';
import { registerMember } from './pages/CrossTeamShare';
import ChatPage from './pages/ChatPage';
import DepartmentsDashboard from './pages/DepartmentsDashboard';
import DashboardHome, { V2Stat, V2RecordId } from './pages/DashboardHome';
import { portalCode } from './utils/reportId';
import { playNotificationSound, notifSoundEnabled, setNotifSoundEnabled } from './notifUtils';
import AppLock, { PinSetupPanel } from './components/AppLock/AppLock';
import { clearAllCache, SessionCache } from './cache';
import { sha256, writeLog } from './utils/firebaseUtils';
import { PORTAL_CARDS } from './constants/portalCards';
import StartupChecks from './components/StartupChecks/StartupChecks';
import { AdminPanel, AdminBell } from './components/AdminPanel/AdminPanel';
import AuthGate from './components/AuthFlow/AuthFlow';
import ErrorBoundary from './components/ErrorBoundary';
import { onForegroundMessage } from './utils/fcmUtils';
import AnnouncementBanner from './components/AnnouncementBanner/AnnouncementBanner';
import AchievementsPage from './pages/AchievementsPage';
import { useAppConfig } from './hooks/useAppConfig';
import { useWorkSession } from './hooks/useWorkSession';
import DailyLogFAB from './components/DailyLogFAB/DailyLogFAB';
import GlobalAttendanceBar from './components/GlobalAttendanceBar/GlobalAttendanceBar';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AccessRequestsPage from './pages/AccessRequestsPage';
import ReportCard from './components/ReportCard/ReportCard';
import { pushNotificationEvent } from './utils/notificationRouter';
import { APP_VERSION } from './constants/appVersion';
import { useViewAsMembers } from './hooks/useViewAsMembers';
import { useDepartmentsState } from './hooks/useDepartments';
import { buildPreviewUser, VIEW_AS_PORTAL_IDS } from './utils/viewAs';
import DepartmentHeroCover from './components/DepartmentHeroCover/DepartmentHeroCover';

// ═══════════════════════════════════════════════════════════
//  Firebase Web App
// ═══════════════════════════════════════════════════════════
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getFirestore, doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, getDocs,
         collection, onSnapshot, query, orderBy, serverTimestamp, where, limit, increment } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword,
         createUserWithEmailAndPassword, sendEmailVerification,
         signOut as fbSignOut, sendPasswordResetEmail,
         RecaptchaVerifier, PhoneAuthProvider, linkWithCredential,
         GoogleAuthProvider, signInWithPopup, signInWithCredential } from "firebase/auth";
import { actorStamp } from './utils/actorStamp';

// Portal pages — lazy-loaded so main bundle stays small
const MyExperimentsPanel  = lazy(() => import('./pages/MyExperimentsPanel'));
const DailyLogPage        = lazy(() => import('./pages/DailyLog/DailyLogPage'));
const DailyUpdatesPage    = lazy(() => import('./pages/DailyLog/DailyUpdatesPage'));
const ReportCompilerPage  = lazy(() => import('./pages/DailyLog/ReportCompilerPage'));
const ReportsArchivePage  = lazy(() => import('./pages/DailyLog/ReportsArchivePage'));
const QuickPostDrawer     = lazy(() => import('./pages/DailyLog/QuickPostDrawer'));
const MissionPlanningPortal    = lazy(() => import("./pages/MissionPlanningPortal"));
const EmbeddedSystemPortal     = lazy(() => import("./pages/EmbeddedSystemPortal"));
const SystemEngineeringPortal  = lazy(() => import("./pages/SystemEngineeringPortal"));
const OperationsPortal         = lazy(() => import("./pages/OperationsPortal"));
const OpsUploadModal           = lazy(() => import("./pages/OperationsPortal").then(m => ({ default: m.UploadModal })));
const PromptEngineeringPortal  = lazy(() => import("./pages/PromptEngineeringPortal"));
const OBCDevelopmentPage       = lazy(() => import('./pages/OBCDevelopmentPage'));
const InternsPortal            = lazy(() => import('./pages/InternsPortal'));
const DynamicPortal            = lazy(() => import('./pages/DynamicPortal'));
const ExperimentalResultsPortal = lazy(() => import('./pages/ExperimentalResultsPortal'));
const ExperimentWorkspace       = lazy(() => import('./pages/experiments/ExperimentWorkspace'));
const FinanceAdminPortal       = lazy(() => import('./pages/FinanceAdminPortal'));
const GuestReportViewer        = lazy(() => import('./pages/GuestReportViewer'));
const TrackRedirect            = lazy(() => import('./pages/TrackRedirect'));
const AttendancePage           = lazy(() => import('./pages/AttendancePage'));
const MentorDashboard          = lazy(() => import('./pages/MentorDashboard'));
const MissionTimeline          = lazy(() => import('./pages/MissionTimeline'));
const MissionPortalPage        = lazy(() => import('./pages/MissionPortal/MissionPortalPage'));
const MR1TrackerPage           = lazy(() => import('./pages/MR1Tracker/MR1TrackerPage'));
const MR1TrackerPublicView     = lazy(() => import('./pages/MR1Tracker/MR1TrackerPublicView'));
const MyDayPage                = lazy(() => import('./pages/MyDayPage/MyDayPage'));
const MemberRequestsPage       = lazy(() => import('./pages/MemberRequests/MemberRequestsPage'));
const NotificationRulesPage    = lazy(() => import('./pages/NotificationRules/NotificationRulesPage'));
const ReportsRegisterPage      = lazy(() => import('./pages/ReportsRegister/ReportsRegisterPage'));
const CrewDirectory            = lazy(() => import('./pages/CrewDirectory/CrewDirectory'));
// Dev-only layout preview (sample data, no sign-in); dropped from production builds
const DesignPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./dev/DesignPreview')) : null;
const GuestPortal              = lazy(() => import('./pages/GuestPortal'));
const PublicPage               = lazy(() => import('./pages/PublicPage'));
const InternsHub               = lazy(() => import('./pages/InternsHub'));
const BackupStoragePage        = lazy(() => import('./pages/BackupStoragePage'));
const DataRoomPortal           = lazy(() => import('./pages/DataRoomPortal'));
const AdminTeamPage            = lazy(() => import('./pages/AdminTeamPage'));

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyA0eKaIpH4zBydvMxnREbbpFbRMM8M5WgU",
  authDomain: "satellite-operating-procedure.firebaseapp.com",
  projectId: "satellite-operating-procedure",
  storageBucket: "satellite-operating-procedure.firebasestorage.app",
  messagingSenderId: "139571789116",
  appId: "1:139571789116:web:7e7a5410b1685a4e52b239",
  measurementId: "G-LL61KWWQ5T"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);
// Firestore keeps an on-device cache (IndexedDB) so revisiting a page shows
// data instantly; live listeners still sync any changes from the server.
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager(), cacheSizeBytes: 100 * 1024 * 1024 }),
  });
} catch (_) {
  db = getFirestore(app);
}
const auth = getAuth(app);

// ── OTP generator (6 digits) ─────────────────────────────────────────────────
const genOTP = () => Math.floor(100000 + Math.random() * 900000).toString();

// ── Bootstrap Firestore auth config if missing ────────────────────────────────
async function bootstrapAuth() {
  try {
    const ref = doc(db, "app_config", "auth");
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      const adminHash    = await sha256("orbit");
      const mentorHash   = await sha256("mentor2025");
      const embeddedHash = await sha256("coildriver2025");
      const systemHash   = await sha256("systemeng2025");
      const mpHash       = await sha256("missionplanning2025");
      await setDoc(ref, { admin_hash: adminHash, mentor_hash: mentorHash, embedded_hash: embeddedHash, system_hash: systemHash, mp_team_hash: mpHash });
    } else {
      const data = snap.data();
      const updates = {};
      if (!data.embedded_hash) updates.embedded_hash = await sha256("coildriver2025");
      if (!data.system_hash)   updates.system_hash   = await sha256("systemeng2025");
      if (!data.mp_team_hash)  updates.mp_team_hash  = await sha256("missionplanning2025");
      if (Object.keys(updates).length) await updateDoc(ref, updates);
    }
  } catch (_) {}
}
bootstrapAuth();

// ═══════════════════════════════════════════════════════════
//  ROLE-BASED ACCESS
// ═══════════════════════════════════════════════════════════
// Passwords now stored in Firestore /app_config/auth
// Fallback hardcoded hashes (for bootstrap only)
// Pages accessible per role
const ROLE_PAGES = {
  admin:    null,           // null = all pages
  manager:  ["propellant", "cycle", "overshoot", "deadmass", "attitude", "ionvsep", "energy"],
  employee: ["propellant", "cycle", "overshoot"],
  one_time: ["propellant", "cycle"],
  mentor:   ["emssvalidation", "pendulumswinging"],
  embedded: ["emmscoildriver"],   // embedded system engineer: EMMS Coil Driver PCB only
};

// ═══════════════════════════════════════════════════════════
//  ROUTER
// ═══════════════════════════════════════════════════════════
const PAGES = [
  // ── FEATURED (mentor/guest accessible) ─────────────────────────────────────
  { id:"emssvalidation", title:"EMMS Physics, CoM Validation & 300 J/cycle", subtitle:"Two-body GMAT proof: Hull +0.363mm/cycle, CoM objection refuted, live W=J×g×t calculator", section:"★", color:"#10B981", icon:"⚛", category:"physics", component:EMSSValidation },
  { id:"pendulumswinging", title:"Elliptical Orbit — Radial Apogee Manoeuvre", subtitle:"Dual-pulse EMMS: outward kick at apogee + inward damping → orbit raise", section:"★", color:"#00ff88", icon:"↔", category:"physics", component:PendulumSwinging },
  // ── FULL SUITE (admin/manager/employee) ─────────────────────────────────────
  { id:"propellant", title:"Propellant Savings Calculator",      subtitle:"Mission-specific savings across all 5 mechanisms",          section:"2",   color:"#22c55e", icon:"🛢", category:"simulations", component:PropellantPage },
  { id:"cycle",      title:"EP-ADCS Cycle Walkthrough",          subtitle:"Every step your thruster takes — step by step",            section:"2.2", color:"#a855f7", icon:"↺",  category:"simulations", component:CyclePage },
  { id:"ionvsep",    title:"EP-ADCS vs Ion Thruster",              subtitle:"Complete system mass: propellant + power + attitude hardware",  section:"0",   color:"#22c55e", icon:"⚡", category:"simulations", component:IonVsEPADCS },
  { id:"energy",     title:"Full System Energy Balance",         subtitle:"Every joule entering and leaving the satellite",             section:"1",   color:"#f59e0b", icon:"⚡", category:"simulations", component:EnergyPage },
  { id:"overshoot",  title:"Overshoot Factor — All Technologies",subtitle:"η comparison: EP-ADCS vs ion vs cold gas",                  section:"2.1", color:"#0e9cc0", icon:"η",  category:"simulations", component:OvershootPage },
  { id:"deadmass",   title:"Dead Mass Comparison",               subtitle:"Total system mass: EP-ADCS vs ion across satellite classes", section:"3",   color:"#ef4444", icon:"⚖",  category:"simulations", component:DeadMassPage },
  { id:"attitude",   title:"Attitude Control Analysis",          subtitle:"EM mass shifter vs reaction wheels",                        section:"4",   color:"#6366f1", icon:"🎯", category:"physics", component:AttitudePage },
  { id:"playbook",   title:"Satellite Design Playbook",          subtitle:"Complete mission engineering: BOM, sequence, power & mass budget", section:"6", color:"#00d4ff", icon:"🛰",  category:"dashboard", component:SatellitePlaybook },
  { id:"missionplanner", title:"Tech Demo — India Coverage Mission", subtitle:"EO-1: coverage analysis, EP-ADCS phasing & full mission propellant budget", section:"7", color:"#3fb950", icon:"🗺", category:"simulations", component:MissionPlanner },
  { id:"indiacoverage",  title:"India Coverage Animation",       subtitle:"Real-time satellite ground track and coverage swath over India",              section:"8",  color:"#f97316", icon:"🇮🇳", category:"physics", component:IndiaCoverageAnimation },
  { id:"emssphysics",    title:"EMSS Physics Simulator",         subtitle:"Side-by-side Newtonian: Conventional thruster vs EP-ADCS with EMMS",         section:"9",  color:"#00d4ff", icon:"⚛",  category:"physics", component:EMSSPhysicsSimulator },
  { id:"pendulumrise",   title:"Pendulum Resonant Orbit Rise",   subtitle:"EMMS resonant pendulum swing transfers energy to orbital altitude",           section:"10", color:"#a855f7", icon:"🌒", category:"physics", component:PendulumOrbitRise },
  { id:"pendulum",       title:"Pendulum → Orbit Raise (v1)",    subtitle:"Pendulum experiment: correct small-angle, fires at turning point only",       section:"11", color:"#ffd060", icon:"🔬", category:"physics", component:PendulumPage },
  { id:"orbitalpendulum",  title:"Orbital Pendulum Experiment",  subtitle:"Multi-phase mission: resonant impulse accumulation, Oberth proof, drag vs raise", section:"13", color:"#00ff9d", icon:"🧪", category:"physics", component:OrbitalPendulumExp },
  { id:"epadcssim",       title:"EP-ADCS Orbit Sim",             subtitle:"450→460km SSO Hohmann transfer via 215 EP-ADCS burns + drag + station keeping", section:"14", color:"#00d4ff", icon:"🛰",  category:"simulations", component:EPADCSOrbitSim },
  { id:"emmaorbitraise",  title:"Propellant-Free Orbit Raise",   subtitle:"EMMS gravity-assisted apogee raise — zero propellant, pendulum analogy, live patent claim", section:"15", color:"#00ff88", icon:"🔋", category:"physics", component:EMMAOrbitRaise },
  { id:"constants",  title:"Physics Constants",                  subtitle:"View, edit and validate all physics constants live",         section:"⚙",   color:"#94a3b8", icon:"⚙",  category:"dashboard", component:ConstantsPage },
  { id:"emmscoildriver", title:"EMMS Coil Driver PCB",           subtitle:"REV A1 · 24V/3A · RP2040 · BOM, schematic notes, pin map, firmware, PCB layout & documentation log", section:"HW", color:"#4a8fe0", icon:"⚡", category:"dashboard", component:EMSCoilDriverPage },
];

// ═══════════════════════════════════════════════════════════
//  SEARCH INDEX — add entries here when adding new pages
// ═══════════════════════════════════════════════════════════
const SEARCH_INDEX = [
  {
    pageId: "emssvalidation",
    tags: ["emss", "gmat", "two-body", "com", "centre of mass", "sma", "orbit raise", "validation", "300j", "impulse", "apogee", "radial velocity", "keplerian", "newton", "work", "coast time", "physics"],
    snippets: [
      "Two-body GMAT proof: Hull SMA +0.363mm/cycle, Baseline unchanged, R2=1.000000 over 39 cycles",
      "CoM objection refuted: temporal asymmetry in gravitational field allows net positive work",
      "Live W=J×g×t calculator: reach 300J target with t=17.5s, zero hardware change",
      "S1 outward at v_r=0 (min work), S3 inward at v_r=8.5m/s (max work) — asymmetry drives orbit raise",
    ],
  },
  {
    pageId: "ionvsep",
    tags: ["ion", "ep-adcs", "system mass", "xenon", "ppu", "reaction wheel", "solar panel", "crossover", "plume", "jitter", "cubesat", "smallsat", "isp", "hall thruster", "propellant", "comparison"],
    snippets: [
      "Complete system mass: propellant + PPU + solar panels + reaction wheels vs EP-ADCS",
      "Ion disqualified below 30kg: 150W solar panels outweigh total satellite mass budget",
      "Plume contamination: 2–15% mirror reflectivity loss per year on optical payloads",
      "Crossover at ~350kg — below that EP-ADCS wins total system mass every time",
    ],
  },
  {
    pageId: "energy",
    tags: ["energy", "drag", "oberth", "solar", "emms", "joule", "watt", "power", "leverage", "52x", "balance"],
    snippets: [
      "Drag force at 425km: 22μN — the orbital energy sink",
      "Oberth effect: 95.5% of drag compensation comes from orbital KE",
      "EMMS electrical: 4mW average, 346 J/day, zero orbital effect",
      "EP-ADCS energy leverage: 52× electrical to orbital",
      "Solar panels: 244W input vs 168mW drag loss",
    ],
  },
  {
    pageId: "propellant",
    tags: ["propellant", "savings", "calculator", "isp", "cold gas", "warm gas", "cubesat", "3u", "6u", "tsiolkovsky", "mission", "altitude", "budget"],
    snippets: [
      "W1: in-track retrograde overshoot eliminated — saves 39%",
      "W3: oscillatory perturbation absorption — saves 10%",
      "W4: desaturation burns eliminated (SKM-4 tier)",
      "W6: day-night differential drag sync — saves 8%",
      "5-year mission budget, 400–500 km altitude, 4–100 kg satellite",
    ],
  },
  {
    pageId: "overshoot",
    tags: ["eta", "η", "overshoot", "factor", "efficiency", "hall", "ion", "cold gas", "warm gas", "mono", "thruster", "comparison", "bang-bang"],
    snippets: [
      "η = total propellant consumed / theoretical Tsiolkovsky minimum",
      "Conventional cold gas: η=1.921 — wastes 92% more than needed",
      "EP-ADCS cold gas: η=1.020 — only 2% above theoretical perfect",
      "Hall ion thruster: η=1.058, needs 150W, xenon plume contaminates optics",
      "η_intrack × η_oscillatory × η_response × η_pointing breakdown",
    ],
  },
  {
    pageId: "cycle",
    tags: ["cycle", "steps", "emms", "coil", "charging", "thruster", "fire", "absorb", "reset", "timing", "133s", "20ms", "walkthrough"],
    snippets: [
      "133s cycle: drag accumulates → sensor detects → EMMS charges → thruster fires",
      "EMMS coil charging lag 20ms — the only source of residual η=1.020",
      "Overshoot absorbed by EMMS: no retrograde burn needed",
      "649 cycles per day, 1.18 million cycles over 5 years",
      "EMMS duty cycle: 0.96%, average power drawn: 4mW",
    ],
  },
  {
    pageId: "deadmass",
    tags: ["dead mass", "system mass", "ion", "solar panels", "reaction wheels", "crossover", "payload", "tank", "hardware", "350kg"],
    snippets: [
      "Total propulsion system mass: EP-ADCS vs ion thruster",
      "Ion requires 150W solar panels — outweighs propellant saving below ~350kg",
      "EP-ADCS eliminates reaction wheels entirely",
      "Crossover point: ~350kg satellite — below that EP-ADCS wins on total mass",
      "8 kg to 500 kg satellite class comparison",
    ],
  },
  {
    pageId: "attitude",
    tags: ["attitude", "jitter", "reaction wheel", "pointing", "stability", "image smear", "optical", "desaturation", "bearing", "lubrication", "kepler"],
    snippets: [
      "Reaction wheel jitter: 700–1200 μrad vs EMMS: <5 μrad",
      "Image smear at 450km: 360m per pixel (RW) vs 2.25m (EMMS)",
      "No bearings → no lubrication failure → no tumble risk",
      "Desaturation propellant: 0.08 kg/year (RW) vs zero (EMMS)",
      "160× better pointing stability for optical payloads",
    ],
  },
  {
    pageId: "playbook",
    tags: ["playbook", "satellite", "bom", "bill of materials", "mission", "sequence", "mass budget", "power budget", "cubesat", "12u", "ep-adcs", "emms", "structural", "harness", "thermal", "comms", "obc", "eps", "adcs", "launch", "deorbit", "aot", "timeline"],
    snippets: [
      "Complete mission engineering tool: BOM, subsystems, mass & power budget, mission sequence",
      "Full bill of materials with vendor part numbers for all subsystems",
      "Interactive physics engine: drag, J2, SRP, propellant savings live",
      "Mission timeline from AIT through deorbit with expandable steps",
    ],
  },
  {
    pageId: "constants",
    tags: ["constants", "physics", "edit", "validate", "rho", "eta", "isp", "CD", "scale height", "density", "constants page"],
    snippets: [
      "View and edit all physics constants live — changes validated instantly",
      "RHO_A, RHO_SCALE, ETA_CONV, ETA_EP, TIER_MULT — all editable",
      "Validation checks: density, drag, saving fraction, leverage",
    ],
  },
  {
    pageId: "missionplanner",
    tags: ["india", "coverage", "ground track", "passes", "access time", "imaging", "ep-adcs", "phasing",
           "differential drag", "propellant budget", "delta-v", "insertion", "station keeping", "deorbit",
           "momentum dumping", "desaturation", "eo-1", "tech demo", "sso", "500km", "rocket equation", "isp"],
    snippets: [
      "Full mission ΔV budget: insertion + station keeping + attitude desats + deorbit EOL",
      "EP-ADCS attitude control uses differential drag → zero propellant for momentum dumping",
      "India coverage: passes/day, access time, imaging windows for 8–37°N, 68–97°E",
      "EP-ADCS differential drag phasing — 180° phase in days, no propellant consumed",
      "Per-segment propellant savings: each mission operation compared EP-ADCS vs conventional",
    ],
  },
];

// ═══════════════════════════════════════════════════════════
//  STAR FIELD
// ═══════════════════════════════════════════════════════════
function Stars({ visible }) {
  const stars = useRef(
    Array.from({ length: 180 }, () => ({
      x: Math.random()*100, y: Math.random()*100,
      r: Math.random()*1.6+0.3, dur: Math.random()*4+3, delay: Math.random()*5,
    }))
  ).current;

  if (!visible) return null;
  return (
    <div style={{ position:"fixed", inset:0, overflow:"hidden", pointerEvents:"none", zIndex:0 }}>
      <svg style={{ width:"100%", height:"100%" }}>
        {stars.map((s,i) => (
          <circle key={i} cx={`${s.x}%`} cy={`${s.y}%`} r={s.r} fill="white" opacity={0.6}>
            <animate attributeName="opacity" values="0.2;0.9;0.2" dur={`${s.dur}s`} repeatCount="indefinite" begin={`${s.delay}s`} />
          </circle>
        ))}
      </svg>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  THEME TOGGLE BUTTON
// ═══════════════════════════════════════════════════════════
function SyncDot({ status }) {
  const cfg = {
    synced:     { color:"#22c55e", label:"synced",     pulse:false },
    connecting: { color:"#f59e0b", label:"connecting", pulse:true  },
    offline:    { color:"#3a5068", label:"local only",  pulse:false },
    error:      { color:"#ef4444", label:"sync error", pulse:false },
  }[status] || { color:"#3a5068", label:"local only", pulse:false };

  return (
    <div
      className="smc-sync-dot"
      title={cfg.label}
      aria-label={`Sync status: ${cfg.label}`}
      style={{
        display:"flex", alignItems:"center", gap:5,
        padding:"3px 8px", borderRadius:20, flexShrink:0,
        background:"var(--smc-el, rgba(128,128,128,0.08))",
        border:"0.5px solid var(--smc-bd, rgba(128,128,128,0.2))",
      }}
    >
      <span style={{
        width:7, height:7, borderRadius:"50%", background:cfg.color, display:"inline-block", flexShrink:0,
        boxShadow: cfg.pulse ? `0 0 0 0 ${cfg.color}88` : "none",
        animation: cfg.pulse ? "syncPulse 1.2s infinite" : "none",
      }} />
      <style>{`@keyframes syncPulse{0%{box-shadow:0 0 0 0 #f59e0b88}70%{box-shadow:0 0 0 6px transparent}100%{box-shadow:0 0 0 0 transparent}}`}</style>
      <span className="smc-sync-dot-label" style={{ ...mono, fontSize:9, color:cfg.color, letterSpacing:1, whiteSpace:'nowrap' }}>
        {cfg.label}
      </span>
    </div>
  );
}

// ─── SMC Theme Dropdown — nav bar quick-switch (Dark / Light / Space) ────────
const SMC_THEME_LIST = ['dark', 'light', 'space'];
function ThemeDropdown({ themeId, onChange }) {
  const [open, setOpen] = useState(false);
  const activeSmc = SMC_THEMES[themeId];
  const icon = THEME_ICONS[themeId] || '◐';
  const label = activeSmc?.name || (ALL_THEMES.find(t => t.id === themeId)?.name ?? 'Theme');
  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '4px 9px', borderRadius: 6,
          border: '0.5px solid var(--smc-bd, rgba(255,255,255,0.07))',
          cursor: 'pointer', background: 'transparent',
          color: 'var(--smc-tx3, #4d5a6e)', fontSize: 11,
          fontFamily: "'Share Tech Mono', monospace",
          transition: 'all 0.15s',
          userSelect: 'none',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--smc-ac-bg, rgba(14,156,192,0.10))';
          e.currentTarget.style.color = 'var(--smc-ac, #0e9cc0)';
          e.currentTarget.style.borderColor = 'var(--smc-ac-border, rgba(14,156,192,0.28))';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--smc-tx3, #4d5a6e)';
          e.currentTarget.style.borderColor = 'var(--smc-bd, rgba(255,255,255,0.07))';
        }}
      >
        <span style={{ fontSize: 12 }}>{icon}</span>
        <span>{label}</span>
        <span style={{ fontSize: 8 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <>
          <div onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 100,
            minWidth: 210, background: 'var(--smc-panel, #0d1526)',
            border: '0.5px solid var(--smc-bd, rgba(255,255,255,0.07))',
            borderRadius: 10, overflow: 'hidden',
            boxShadow: 'var(--smc-shadow, 0 4px 20px rgba(0,0,0,0.35))',
          }}>
            <div style={{
              padding: '8px 12px 6px', fontSize: 9, letterSpacing: '0.1em',
              fontFamily: "'Share Tech Mono', monospace",
              color: 'var(--smc-tx3, #4d5a6e)',
              borderBottom: '0.5px solid var(--smc-bd, rgba(255,255,255,0.07))',
            }}>APPEARANCE</div>

            {/* SMC themes with swatches */}
            {SMC_THEME_LIST.map(tid => {
              const t = SMC_THEMES[tid];
              const isActive = tid === themeId;
              const sw = THEME_SWATCHES[tid];
              return (
                <div key={tid}
                  onClick={() => { onChange(tid); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', cursor: 'pointer',
                    background: isActive ? 'var(--smc-ac-bg, rgba(14,156,192,0.10))' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--smc-hov, rgba(255,255,255,0.04))'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{
                    width: 36, height: 24, borderRadius: 5, overflow: 'hidden',
                    border: '0.5px solid var(--smc-bd, rgba(255,255,255,0.07))',
                    display: 'flex', flexShrink: 0,
                  }}>
                    {sw.map((c, i) => <div key={i} style={{ flex: 1, background: c }} />)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: isActive ? 'var(--smc-ac, #0e9cc0)' : 'var(--smc-tx, #e8edf5)' }}>
                      {t.name}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--smc-tx3, #4d5a6e)', fontFamily: "'Share Tech Mono', monospace" }}>
                      {t.label}
                    </div>
                  </div>
                  {isActive && (
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'var(--smc-ac, #0e9cc0)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, color: 'var(--smc-bg, #0a0f1e)', flexShrink: 0,
                    }}>✓</div>
                  )}
                </div>
              );
            })}

            {/* Divider + other legacy themes */}
            <div style={{
              padding: '6px 12px', fontSize: 10,
              color: 'var(--smc-tx3, #4d5a6e)',
              borderTop: '0.5px solid var(--smc-bd, rgba(255,255,255,0.07))',
              fontFamily: "'Share Tech Mono', monospace",
            }}>Theme syncs to your account</div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Theme picker — fixed top-right by default, inline when prop set ─────────
function ThemePicker({ themeId, onChange, inline = false }) {
  const [open, setOpen] = useState(false);
  const active = ALL_THEMES.find(t => t.id === themeId) || ALL_THEMES[0];
  return (
    <div style={inline
      ? { position: "relative", zIndex: 100 }
      : { position: "fixed", top: 14, right: 14, zIndex: 200 }}>
      {/* Collapsed: show active theme swatch + label */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Change theme"
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "6px 12px 6px 8px",
          background: `${active.panel}ee`, backdropFilter: "blur(12px)",
          border: `1px solid ${active.border}`,
          borderRadius: 20, cursor: "pointer",
          boxShadow: `0 2px 12px ${active.accent}22`,
        }}>
        {/* Active swatch */}
        <span style={{
          width: 12, height: 12, borderRadius: "50%",
          background: `linear-gradient(135deg, ${active.accent}, ${active.panel})`,
          border: `1px solid ${active.accent}66`, flexShrink: 0,
        }} />
        <span style={{ ...mono, fontSize: 9, color: active.muted, letterSpacing: 1.5 }}>
          {active.name.toUpperCase()}
        </span>
        <span style={{ ...mono, fontSize: 8, color: active.muted }}>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {/* Expanded: theme options */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          background: `${active.panel}f4`, backdropFilter: "blur(16px)",
          border: `1px solid ${active.border}`,
          borderRadius: 12, padding: 8, minWidth: 180,
          boxShadow: `0 8px 32px #00000044`,
        }}>
          <div style={{ ...mono, fontSize: 7, color: active.muted,
            letterSpacing: 2, padding: "4px 8px 8px", borderBottom: `1px solid ${active.border}` }}>
            SELECT THEME
          </div>
          {ALL_THEMES.map(t => (
            <button
              key={t.id}
              onClick={() => { onChange(t.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%", padding: "8px 10px", borderRadius: 8,
                background: themeId === t.id ? `${t.accent}18` : "transparent",
                border: `1px solid ${themeId === t.id ? t.accent + "44" : "transparent"}`,
                cursor: "pointer", marginTop: 2,
              }}>
              {/* Swatch preview */}
              <span style={{
                width: 28, height: 18, borderRadius: 4, flexShrink: 0,
                background: t.bg,
                border: `1px solid ${t.border}`,
                position: "relative", overflow: "hidden",
              }}>
                <span style={{
                  position: "absolute", top: 2, left: 2, right: 2, bottom: 2,
                  borderRadius: 2, background: t.panel,
                  borderTop: `2px solid ${t.accent}`,
                }} />
              </span>
              <div style={{ textAlign: "left" }}>
                <div style={{ ...mono, fontSize: 9, color: themeId === t.id ? t.accent : active.text,
                  fontWeight: themeId === t.id ? 700 : 400 }}>{t.name}</div>
                <div style={{ ...mono, fontSize: 7, color: active.muted }}>
                  {t.dark ? "Dark" : "Light"}
                </div>
              </div>
              {themeId === t.id && (
                <span style={{ ...mono, fontSize: 8, color: t.accent, marginLeft: "auto" }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PDF inline viewer — renders all pages via PDF.js ────────────────────────
function PdfInlineViewer({ url, name }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [status,   setStatus]   = useState("loading");
  const [errMsg,   setErrMsg]   = useState("");

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setStatus("loading"); setNumPages(0); setErrMsg("");
    if (containerRef.current) containerRef.current.innerHTML = "";

    async function render() {
      try {
        const pdfjs = await loadPDFJS();
        const pdf = await pdfjs.getDocument({ url }).promise;
        if (cancelled) return;
        setNumPages(pdf.numPages);
        for (let p = 1; p <= pdf.numPages; p++) {
          if (cancelled) break;
          const page     = await pdf.getPage(p);
          const vp       = page.getViewport({ scale: 1.6 });
          const canvas   = document.createElement("canvas");
          canvas.width   = vp.width;
          canvas.height  = vp.height;
          canvas.style.cssText = "width:100%;display:block;margin-bottom:6px;border-radius:4px;box-shadow:0 2px 8px #00000033";
          if (containerRef.current && !cancelled) containerRef.current.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        }
        if (!cancelled) setStatus("done");
      } catch(e) {
        if (!cancelled) { setErrMsg(e.message || "Failed to load PDF"); setStatus("error"); }
      }
    }
    render();
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div style={{ margin:"16px 0", background:"#e8eaf0", borderRadius:8, padding:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <span style={{ fontFamily:"monospace", fontSize:11, color:"#334" }}>📄 {name}</span>
        {numPages > 0 && <span style={{ fontFamily:"monospace", fontSize:9, color:"#777" }}>{numPages} page{numPages>1?"s":""}</span>}
        {status === "loading" && <span style={{ fontFamily:"monospace", fontSize:9, color:"#999" }}>Rendering…</span>}
        <a href={url} target="_blank" rel="noreferrer"
          style={{ fontFamily:"monospace", fontSize:9, color:"#0e9cc0", marginLeft:"auto", textDecoration:"none" }}>
          Open ↗
        </a>
      </div>
      {status === "error" && (
        <div style={{ fontFamily:"monospace", fontSize:11, color:"#f43f5e", marginBottom:8 }}>
          {errMsg} — <a href={url} target="_blank" rel="noreferrer" style={{ color:"#0e9cc0" }}>open PDF directly</a>
        </div>
      )}
      <div ref={containerRef} />
    </div>
  );
}

// ─── Word / document inline viewer (Google Docs Viewer iframe) ───────────────
function DocInlineViewer({ url, name, accentColor }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const viewUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
  const link = accentColor || "#0e9cc0";
  return (
    <div style={{ margin:"16px 0", background:"#e8eaf0", borderRadius:8, padding:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
        <span style={{ fontFamily:"monospace", fontSize:11, color:"#334" }}>📝 {name}</span>
        {!loaded && !failed && <span style={{ fontFamily:"monospace", fontSize:9, color:"#999" }}>Loading…</span>}
        <a href={url} target="_blank" rel="noreferrer"
          style={{ fontFamily:"monospace", fontSize:9, color:link, marginLeft:"auto", textDecoration:"none" }}>
          Download ↓
        </a>
      </div>
      {failed ? (
        <div style={{ fontFamily:"monospace", fontSize:11, color:"#555", padding:"20px 0" }}>
          Could not preview — <a href={url} target="_blank" rel="noreferrer" style={{ color:link }}>download the file</a>
        </div>
      ) : (
        <iframe src={viewUrl} title={name}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          style={{ width:"100%", height:700, border:"none", borderRadius:4, display:"block" }} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  SEARCH BAR
// ═══════════════════════════════════════════════════════════
function SearchBar({ onNavigate }) {
  const C = useTheme();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const q = query.trim().toLowerCase();
  const results = q.length < 2 ? [] : SEARCH_INDEX.flatMap(entry => {
    const page = PAGES.find(p => p.id === entry.pageId);
    if (!page) return [];
    const tagHit   = entry.tags.some(t => t.includes(q));
    const titleHit = page.title.toLowerCase().includes(q);
    const snippets = entry.snippets.filter(s => s.toLowerCase().includes(q));
    if (!tagHit && !titleHit && snippets.length === 0) return [];
    return [{ page, snippets: snippets.length > 0 ? snippets : [page.subtitle] }];
  });

  const highlight = (text) => {
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: "#0e9cc044", color: "#0e9cc0",
          borderRadius: 2, padding: "0 2px" }}>
          {text.slice(idx, idx + q.length)}
        </mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const showResults = focused && q.length >= 2;

  return (
    <div style={{ position: "relative", maxWidth: 560, margin: "0 auto 32px" }}>
      {/* Input */}
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 14, top: "50%",
          transform: "translateY(-50%)", color: C.muted, fontSize: 14 }}>⌕</span>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={e => {
            if (e.key === "Escape") { setQuery(""); inputRef.current.blur(); }
            if (e.key === "Enter" && results.length > 0) onNavigate(results[0].page.id);
          }}
          placeholder="Search pages, topics, equations…"
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "11px 40px 11px 38px", ...mono, fontSize: 12,
            background: C.panel, border: `1px solid ${focused ? "#0e9cc066" : C.border}`,
            borderRadius: showResults && results.length > 0 ? "6px 6px 0 0" : 6,
            color: C.text, outline: "none", transition: "border 0.15s",
            boxShadow: focused ? "0 0 0 3px #0e9cc015" : "none",
          }}
        />
        {query && (
          <button onClick={() => setQuery("")} style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", color: C.muted,
            cursor: "pointer", fontSize: 16, lineHeight: 1,
          }}>×</button>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: C.panel, border: `1px solid #0e9cc044`,
          borderTop: "none", borderRadius: "0 0 6px 6px",
          boxShadow: "0 8px 24px #00000044", maxHeight: 400, overflowY: "auto",
        }}>
          {results.length === 0 ? (
            <div style={{ ...mono, fontSize: 11, color: C.muted, padding: "14px 16px" }}>
              No results for "{query}"
            </div>
          ) : results.map(({ page, snippets }) => (
            <div key={page.id}
              onClick={() => { onNavigate(page.id); setQuery(""); }}
              style={{
                padding: "12px 16px", cursor: "pointer",
                borderBottom: `1px solid ${C.border}`,
                borderLeft: `3px solid ${page.color}`,
                transition: "background 0.1s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${page.color}10`}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 14, opacity: 0.7 }}>{page.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: page.color }}>
                  {highlight(page.title)}
                </span>
                <span style={{ ...mono, fontSize: 9, color: C.muted, marginLeft: "auto" }}>
                  § {page.section}
                </span>
              </div>
              {snippets.slice(0, 2).map((s, i) => (
                <div key={i} style={{ ...mono, fontSize: 10, color: C.muted,
                  lineHeight: 1.6, paddingLeft: 22 }}>
                  · {highlight(s)}
                </div>
              ))}
            </div>
          ))}
          <div style={{ ...mono, fontSize: 9, color: C.muted,
            padding: "8px 16px", textAlign: "right" }}>
            {results.length} result{results.length !== 1 ? "s" : ""} — press Enter to open first
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TEAM VALIDATIONS PAGE  (admin news feed + portal cards)
// ═══════════════════════════════════════════════════════════
// AdminPanel, AdminBell, AddUserDirectly → src/components/AdminPanel/AdminPanel.jsx
// PORTAL_CARDS → src/constants/portalCards.js

// placeholder to keep section header; real definitions imported above
// ── shared C2 palette used by both feed and portal cards ──────────────────────
const C2 = {
  bg:"#0b0e12", panel:"#111620", border:"#1e2636", text:"#b8ccd8",
  bright:"#dde9f0", dim:"#445566", green:"#10b981", red:"#f43f5e", yellow:"#f59e0b",
  mono:"'Share Tech Mono',monospace", sans:"'Inter',system-ui,sans-serif",
};

// Approve a report in any portal collection and award credits to the author
async function approvePost(post) {
  const adminUser    = auth.currentUser;
  const approverName = adminUser?.displayName || adminUser?.email?.split('@')[0] || 'Admin';
  const approverUid  = adminUser?.uid || null;

  await updateDoc(doc(db, post._portal.collection, post.id), {
    adminApproved: true, adminApprovedAt: serverTimestamp(),
    adminApprovedBy: approverName, approvedByUid: approverUid,
  });

  const authorUid = post.uploadedByUid || post.uploadedById;
  if (authorUid) {
    const isFeatured  = post.isFeatured === true;
    const isCrossTeam = post._crossTeam  === true;
    const points      = isFeatured ? 10 : isCrossTeam ? 8 : 5;

    await addDoc(collection(db, 'credits_log'), {
      recipientUid:   authorUid,
      recipientName:  post.uploadedBy || 'Unknown',
      type:           'report_approved',
      sourceId:       post.id,
      sourceTitle:    post.title || '',
      sourcePortal:   post._portal?.id || 'unknown',
      points,
      awardedBy:      approverName,
      awardedByUid:   approverUid,
      awardedAt:      serverTimestamp(),
      note: `${isFeatured ? 'Featured report' : isCrossTeam ? 'Cross-team report' : 'Standard report'} approval (+${points} pts)`,
    });

    await updateDoc(doc(db, 'authorized_users', authorUid), {
      'credits.total':                     increment(points),
      'credits.thisMonth':                 increment(points),
      'credits.thisYear':                  increment(points),
      'credits.breakdown.reportsApproved': increment(1),
    });

    await addDoc(collection(db, 'notifications_queue'), {
      eventType:  'PORTAL_REPORT_APPROVED',
      sourceId:   post.id,
      sourceType: 'portal_post',
      payload: {
        authorUid, authorName: post.uploadedBy || '',
        reportTitle: post.title || '', reportId: post.id,
        portalId: post._portal?.id || 'unknown',
        points, approvedBy: approverName,
      },
      actorUid: approverUid, actorName: approverName,
      createdAt: serverTimestamp(),
    });
  }
}
// Revoke approval (credits are not deducted — one-way award)
async function revokeApproval(post) {
  await updateDoc(doc(db, post._portal.collection, post.id), {
    adminApproved: false, adminApprovedAt: null, adminApprovedBy: null,
  });
}
// Hard-delete a report from any portal collection
async function removePost(post) {
  await deleteDoc(doc(db, post._portal.collection, post.id));
}

function SocialFeed({ role }) {
  const [sfParams, setSfParams] = useSearchParams();
  const [allPosts, setAllPosts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [viewPost, setViewPost] = useState(null);
  const [cadModal, setCadModal] = useState(null);
  const [busy,     setBusy]     = useState({});
  const [filter,   setFilter]   = useState("all");
  const [deptFilter,   setDeptFilter]   = useState("all");
  const [modalTab,     setModalTab]     = useState("report");
  const [postComments, setPostComments] = useState([]);
  const [postThreads,  setPostThreads]  = useState([]);
  const [newComment,   setNewComment]   = useState("");

  useEffect(() => {
    const parts = {};
    const feedPortals = PORTAL_CARDS.filter(p => !p.noFeed && p.collection);
    let ready = 0;
    const unsubs = feedPortals.map(portal => {
      const q = query(collection(db, portal.collection), orderBy("uploadedAt", "desc"));
      return onSnapshot(q, snap => {
        parts[portal.id] = snap.docs.map(d => ({ id:d.id, _portal:portal, ...d.data() }));
        ready++;
        const merged = Object.values(parts).flat().sort((a,b) => {
          if (a.adminApproved && !b.adminApproved) return -1;
          if (!a.adminApproved && b.adminApproved) return 1;
          const ta = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt||0);
          const tb = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt||0);
          return tb - ta;
        });
        setAllPosts(merged);
        if (ready >= feedPortals.length) setLoading(false);
      }, () => { ready++; if (ready >= feedPortals.length) setLoading(false); });
    });
    return () => unsubs.forEach(u => u());
  }, []);

  // Browser back closes the modal
  useEffect(() => {
    const handle = (e) => {
      if (!e.state?.sfReport) setViewPost(null);
    };
    window.addEventListener('popstate', handle);
    return () => window.removeEventListener('popstate', handle);
  }, []);

  // Load comments for open post
  useEffect(() => {
    if (!viewPost) { setPostComments([]); setPostThreads([]); setNewComment(""); return; }
    setModalTab("report");
    const colName = viewPost._portal.collection;
    const unsub = onSnapshot(
      query(collection(db, colName, viewPost.id, "comments"), orderBy("createdAt", "asc")),
      snap => setPostComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    const q2 = query(collection(db, colName), where("parentId", "==", viewPost.id), orderBy("uploadedAt", "asc"));
    const unsub2 = onSnapshot(q2, snap => setPostThreads(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => {});
    updateDoc(doc(db, colName, viewPost.id), { viewCount: increment(1) }).catch(() => {});
    return () => { unsub(); unsub2(); };
  }, [viewPost?.id]); // eslint-disable-line

  // Auto-open report from URL params (e.g. /?r=ID&p=mp) after posts load
  useEffect(() => {
    if (loading || allPosts.length === 0) return;
    const rId = sfParams.get('r');
    const pId = sfParams.get('p');
    if (!rId || !pId) return;
    const post = allPosts.find(p => p.id === rId && p._portal.id === pId);
    if (post && !viewPost) setViewPost(post);
  }, [loading, allPosts]); // eslint-disable-line

  function openReport(post) {
    setSfParams({ r: post.id, p: post._portal.id }, { replace: false });
    setViewPost(post);
  }

  function relTime(ts) {
    if (!ts?.toDate) return "";
    const d = (Date.now() - ts.toDate().getTime()) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d/60)}m ago`;
    if (d < 86400) return `${Math.floor(d/3600)}h ago`;
    if (d < 604800) return `${Math.floor(d/86400)}d ago`;
    return ts.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
  }

  const fmtD = ts => ts?.toDate ? ts.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short" }) : "";

  async function postAdminComment() {
    const text = newComment.trim();
    if (!text || !viewPost) return;
    await addDoc(collection(db, viewPost._portal.collection, viewPost.id, "comments"), {
      text, by: "Admin", createdAt: serverTimestamp(),
    });
    setNewComment("");
  }

  async function handleApprove(e, post) {
    e.stopPropagation();
    const key = post.id + post._portal.id;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      if (post.adminApproved) await revokeApproval(post);
      else await approvePost(post);
    } finally { setBusy(b => ({ ...b, [key]: false })); }
  }

  async function handleRemove(e, post) {
    e.stopPropagation();
    if (!window.confirm(`Remove "${post.title}"? This cannot be undone.`)) return;
    const key = post.id + post._portal.id;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      await removePost(post);
      if (viewPost?.id === post.id) setViewPost(null);
    } finally { setBusy(b => ({ ...b, [key]: false })); }
  }

  const visible = allPosts.filter(p => {
    if (deptFilter !== "all" && p._portal.id !== deptFilter) return false;
    return filter === "approved" ? p.adminApproved :
           filter === "pending"  ? !p.adminApproved : true;
  });

  return (
    <div style={{ padding:"0 0 40px" }}>
      {/* Section header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:11, color:"#0e9cc0", letterSpacing:3 }}>
          TEAM UPDATES
        </div>
        <div style={{ flex:1, height:1, background:"linear-gradient(to right,#0e9cc033,transparent)" }} />
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:9, color:C2.dim }}>
          {allPosts.length} reports · {allPosts.filter(p=>p.adminApproved).length} validated
        </div>
      </div>

      {/* Status filter pills */}
      <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>
        {[
          ["all","All Updates","#0e9cc0"],
          ["approved","✓ Validated",C2.green],
          ...(role==="admin" ? [["pending","Pending Review",C2.yellow]] : []),
        ].map(([v,l,col]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding:"6px 16px", fontFamily:"'Share Tech Mono',monospace", fontSize:9,
              cursor:"pointer", borderRadius:20,
              background: filter===v ? `${col}20` : "transparent",
              border:`1px solid ${filter===v ? col : C2.border}`,
              color: filter===v ? col : C2.dim, transition:"all 0.15s" }}>
            {l}
          </button>
        ))}
      </div>

      {/* Department filter pills */}
      <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:8, color:C2.dim, letterSpacing:1 }}>DEPT:</span>
        {[["all","All","#0e9cc0"], ...PORTAL_CARDS.filter(p => !p.noFeed).map(p => [p.id, p.icon+" "+p.label, p.color])].map(([v,l,col]) => (
          <button key={v} onClick={() => setDeptFilter(v)}
            style={{ padding:"5px 14px", fontFamily:"'Share Tech Mono',monospace", fontSize:9,
              cursor:"pointer", borderRadius:20,
              background: deptFilter===v ? `${col}20` : "transparent",
              border:`1px solid ${deptFilter===v ? col : C2.border}`,
              color: deptFilter===v ? col : C2.dim, transition:"all 0.15s" }}>
            {l}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign:"center", padding:48, fontFamily:"monospace", fontSize:11, color:C2.dim }}>
          Loading team updates…
        </div>
      )}

      {/* Feed cards */}
      <div style={{ display:"flex", flexDirection:"column", gap:18 }}>
        {visible.map(post => {
          const pi    = post._portal;
          const key   = post.id + pi.id;
          const excerpt = (post.sections||[]).find(s => s.type==="paragraph")?.text || "";
          const isApproved = !!post.adminApproved;
          const isBusy = !!busy[key];
          const initials = (post.uploadedBy||"?").split(" ").map(w=>w[0]||"").join("").slice(0,2).toUpperCase();
          const links = post.linkedTo || [];

          return (
            <div key={key} style={{
              background:"#121927", borderRadius:12,
              border:`1px solid ${isApproved ? C2.green+"44" : "#1a2540"}`,
              boxShadow: isApproved ? `0 0 0 1px ${C2.green}18,0 4px 20px #00000040` : "0 2px 16px #00000038",
              overflow:"hidden",
            }}>
              {/* Approved ribbon */}
              {isApproved && (
                <div style={{ background:`linear-gradient(90deg,${C2.green}22,${C2.green}08)`,
                  borderBottom:`1px solid ${C2.green}33`, padding:"5px 18px",
                  display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontFamily:"monospace", fontSize:9, color:C2.green, letterSpacing:1.5 }}>
                    ✓ VALIDATED BY ADMIN
                  </span>
                  {post.adminApprovedAt?.toDate && (
                    <span style={{ fontFamily:"monospace", fontSize:8, color:`${C2.green}88` }}>
                      · {fmtD(post.adminApprovedAt)}
                    </span>
                  )}
                </div>
              )}

              <div style={{ padding:"18px 20px" }}>
                {/* Header: avatar + author + team + time */}
                <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:14 }}>
                  <div style={{ width:44, height:44, borderRadius:"50%", flexShrink:0,
                    background:`${pi.color}18`, border:`2px solid ${pi.color}55`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontFamily:"'Share Tech Mono',monospace", fontSize:14, fontWeight:700, color:pi.color }}>
                    {initials}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:3 }}>
                      <span style={{ fontFamily:C2.sans, fontSize:15, fontWeight:700, color:C2.bright }}>
                        {post.uploadedBy}
                      </span>
                      <span style={{ fontFamily:"monospace", fontSize:8, color:pi.color,
                        background:`${pi.color}15`, border:`1px solid ${pi.color}33`,
                        padding:"1px 8px", borderRadius:10 }}>
                        {pi.icon} {pi.label}
                      </span>
                    </div>
                    <div style={{ fontFamily:C2.sans, fontSize:12, color:C2.dim }}>
                      {relTime(post.uploadedAt)}
                      {post.category && (
                        <span style={{ marginLeft:8, color:`${pi.color}77` }}>
                          · {(post.category||"").replace(/_/g," ")}
                        </span>
                      )}
                    </div>
                  </div>
                  {post.pinned && <span style={{ fontSize:14 }}>📌</span>}
                </div>

                {/* Clickable body */}
                <div onClick={() => openReport(post)} style={{ cursor:"pointer" }}>
                  <div style={{ fontFamily:C2.sans, fontSize:19, fontWeight:700, color:C2.bright,
                    lineHeight:1.3, marginBottom:10 }}>
                    {post.title}
                  </div>
                  {excerpt && (
                    <div style={{ fontFamily:C2.sans, fontSize:14, color:C2.text, lineHeight:1.65,
                      marginBottom:12, display:"-webkit-box", WebkitLineClamp:3,
                      WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                      {excerpt}
                    </div>
                  )}
                  {post.coverUrl && (
                    <div style={{ borderRadius:8, overflow:"hidden", marginBottom:14, border:"1px solid #1a2540" }}>
                      <img src={post.coverUrl} alt={post.title}
                        style={{ width:"100%", maxHeight:260, objectFit:"cover", display:"block", opacity:0.88 }} />
                    </div>
                  )}
                </div>

                {/* Tags */}
                {(post.tags||[]).length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12 }}>
                    {(post.tags||[]).map(t => (
                      <span key={t} style={{ fontFamily:"monospace", fontSize:10, color:`${pi.color}99` }}>#{t}</span>
                    ))}
                  </div>
                )}

                {/* Cross-links */}
                {links.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
                    {links.slice(0,3).map((l,i) => (
                      <span key={i} style={{ fontFamily:"monospace", fontSize:9, color:"#a855f7",
                        background:"#a855f710", border:"1px solid #a855f733",
                        padding:"2px 8px", borderRadius:10 }}>
                        🔗 {l.memberName || l.portalLabel || String(l)}
                      </span>
                    ))}
                    {links.length > 3 && (
                      <span style={{ fontFamily:"monospace", fontSize:9, color:C2.dim }}>+{links.length-3} more</span>
                    )}
                  </div>
                )}

                {/* Footer: engagement + actions */}
                <div style={{ display:"flex", alignItems:"center", gap:14, paddingTop:12,
                  borderTop:"1px solid #1a2540", flexWrap:"wrap" }}>
                  <span style={{ fontFamily:"monospace", fontSize:10, color:C2.dim }}>👁 {post.viewCount||0}</span>
                  <span style={{ fontFamily:"monospace", fontSize:10, color:C2.dim }}>⭐ {post.likeCount||0}</span>
                  <button onClick={() => openReport(post)}
                    style={{ fontFamily:"monospace", fontSize:9, color:"#0e9cc0", background:"none",
                      border:"none", cursor:"pointer", letterSpacing:1, padding:0 }}>
                    READ REPORT →
                  </button>
                  {role === "admin" && (
                    <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                      <button onClick={e => handleApprove(e, post)} disabled={isBusy}
                        style={{ padding:"4px 12px", fontFamily:"monospace", fontSize:9,
                          cursor: isBusy?"wait":"pointer", borderRadius:6,
                          background: isApproved ? `${C2.green}20` : "transparent",
                          border:`1px solid ${isApproved ? C2.green : C2.border}`,
                          color: isApproved ? C2.green : C2.dim }}>
                        {isBusy ? "…" : isApproved ? "✓ VALIDATED" : "✓ VALIDATE"}
                      </button>
                      <button onClick={e => handleRemove(e, post)} disabled={isBusy}
                        style={{ padding:"4px 10px", fontFamily:"monospace", fontSize:9,
                          cursor: isBusy?"wait":"pointer", borderRadius:6,
                          background:`${C2.red}12`, border:`1px solid ${C2.red}`, color:C2.red }}>
                        🗑
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!loading && visible.length === 0 && (
          <div style={{ textAlign:"center", padding:60, fontFamily:"monospace", fontSize:11, color:C2.dim }}>
            {allPosts.length === 0
              ? "No team updates yet. Reports from all portals will appear here once submitted."
              : deptFilter !== "all"
                ? `No reports from ${PORTAL_CARDS.find(p=>p.id===deptFilter)?.label || deptFilter}.`
                : `No ${filter === "approved" ? "validated" : "pending"} reports.`}
          </div>
        )}
      </div>

      {/* Full-view modal with tabs */}
      {viewPost && (
        <div style={{ position:"fixed", inset:0, background:"#000000cc", zIndex:500,
          display:"flex", flexDirection:"column" }}>
          <div style={{ background:C2.panel, flex:1, display:"flex", flexDirection:"column",
            width:"100%", maxWidth:900, margin:"0 auto", alignSelf:"stretch" }}>

            {/* Top bar */}
            <div style={{ background:"#07090f", borderBottom:`1px solid ${C2.border}`, padding:"8px 16px",
              display:"flex", alignItems:"center", gap:8, flexShrink:0, flexWrap:"wrap" }}>
              <button onClick={() => window.history.back()} style={{ background:"none", border:"none",
                color:C2.bright, cursor:"pointer", fontFamily:"monospace", fontSize:11, letterSpacing:1, flexShrink:0 }}>← BACK</button>
              <span style={{ fontFamily:"monospace", fontSize:9, color:viewPost._portal.color,
                background:`${viewPost._portal.color}15`, border:`1px solid ${viewPost._portal.color}33`,
                padding:"2px 8px", flexShrink:0 }}>{viewPost._portal.icon} {viewPost._portal.label}</span>
              {viewPost.adminApproved && (
                <span style={{ fontFamily:"monospace", fontSize:9, color:C2.green,
                  background:`${C2.green}18`, border:`1px solid ${C2.green}55`, padding:"2px 8px", flexShrink:0 }}>✓ VALIDATED</span>
              )}
              {/* Tabs */}
              <div style={{ display:"flex", gap:4, overflowX:"auto", scrollbarWidth:"none" }}>
                {[
                  ["report", "REPORT"],
                  ["comments", `COMMENTS (${postComments.length})`],
                  ["thread", `THREAD (${postThreads.length})`],
                  ...(role === "admin" ? [["admin", "ADMIN"]] : []),
                ].map(([v,l]) => (
                  <button key={v} onClick={() => setModalTab(v)} style={{
                    padding:"4px 10px", fontFamily:"monospace", fontSize:9, cursor:"pointer", whiteSpace:"nowrap",
                    background: modalTab===v ? `${C2.border}40` : "transparent",
                    border:`1px solid ${modalTab===v ? "#0e9cc0" : C2.border}`,
                    color: modalTab===v ? "#0e9cc0" : C2.dim, letterSpacing:1 }}>{l}</button>
                ))}
              </div>
              <a href={viewPost._portal.path} target="_blank" rel="noreferrer"
                style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, textDecoration:"none",
                  border:`1px solid ${C2.border}`, padding:"4px 10px", marginLeft:"auto", flexShrink:0 }}>
                OPEN PORTAL ↗
              </a>
            </div>

            {/* REPORT tab */}
            {modalTab === "report" && (
              <div style={{ flex:1, overflowY:"auto", background:"#e8eaf0", padding:"20px 12px" }}>
                <div style={{ background:"#fff", color:"#111", padding:"36px 48px",
                  fontFamily:"Times New Roman,serif", fontSize:"11pt", lineHeight:1.65, maxWidth:820, margin:"0 auto" }}>
                  <div style={{ borderBottom:`2.5px solid ${viewPost._portal.color}`, paddingBottom:12, marginBottom:24 }}>
                    <div style={{ fontFamily:"Arial,sans-serif", fontSize:"8pt", color:"#999", letterSpacing:2, textTransform:"uppercase" }}>Spacemancraft Pvt Ltd</div>
                    <div style={{ fontFamily:"Arial,sans-serif", fontSize:"9pt", color:"#888" }}>{viewPost._portal.icon} {viewPost._portal.label} Report</div>
                    <div style={{ fontFamily:"Arial,sans-serif", fontSize:"19pt", fontWeight:700, color:viewPost._portal.color, lineHeight:1.25, margin:"6px 0" }}>{viewPost.title}</div>
                    <div style={{ fontFamily:"Arial,sans-serif", fontSize:"9pt", color:"#666" }}>
                      By {viewPost.uploadedBy} · {viewPost.uploadedAt?.toDate ? viewPost.uploadedAt.toDate().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : ""}
                    </div>
                    {viewPost.adminApproved && (
                      <div style={{ fontFamily:"Arial,sans-serif", fontSize:"9pt", color:"#10b981", marginTop:4,
                        background:"#f0fdf4", border:"1px solid #bbf7d0", padding:"4px 10px", display:"inline-block" }}>
                        ✓ Validated by Admin
                      </div>
                    )}
                  </div>
                  {(viewPost.sections||[]).map((sec, i) => {
                    if (sec.type==="heading") return <div key={i} style={{ fontFamily:"Arial,sans-serif", fontWeight:700, fontSize:"12pt", color:viewPost._portal.color, margin:"14px 0 5px" }}>{sec.text}</div>;
                    if (sec.type==="paragraph") return <p key={i} style={{ margin:"0 0 10px", textAlign:"justify", lineHeight:1.7 }}>{sec.text}</p>;
                    if (sec.type==="bullets") return <ul key={i} style={{ margin:"6px 0 12px", paddingLeft:26 }}>{(sec.items||[]).map((it,j) => <li key={j}>{it}</li>)}</ul>;
                    if (sec.type==="code") return <pre key={i} style={{ background:"#f5f5f5", border:"1px solid #ddd", padding:"10px", margin:"8px 0", fontFamily:"monospace", fontSize:"9pt", overflowX:"auto", whiteSpace:"pre-wrap" }}>{sec.text}</pre>;
                    if ((sec.type==="graph"||sec.type==="image") && sec.url) {
                      if (/\.(png|jpg|jpeg|gif|svg|webp)$/i.test(sec.name||"")) return <div key={i} style={{ margin:"16px 0", textAlign:"center" }}><img src={sec.url} alt={sec.caption||""} style={{ maxWidth:"100%", border:"1px solid #eee" }} />{sec.caption && <div style={{ fontFamily:"Arial,sans-serif", fontSize:"10pt", fontWeight:700, margin:"6px 0" }}>{sec.caption}</div>}</div>;
                      if (/\.pdf$/i.test(sec.name||"")) return <div key={i} style={{ margin:"16px 0" }}><PdfInlineViewer url={sec.url} name={sec.name||"Document"} /></div>;
                    }
                    if (sec.type==="cad" && sec.url) return (
                      <div key={i} style={{ margin:"16px 0" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                          <span style={{ fontFamily:"Arial,sans-serif", fontSize:"10pt", fontWeight:700, color:"#063b2b" }}>⬡ 3D Model: {sec.name}</span>
                          <button onClick={() => setCadModal({ url:sec.url, name:sec.name||"model" })}
                            style={{ padding:"4px 12px", background:"#10b98118", border:"1px solid #10b981",
                              color:"#10b981", fontFamily:"Arial,sans-serif", fontSize:"9pt", cursor:"pointer", borderRadius:3 }}>
                            🔍 Open 3D Viewer
                          </button>
                        </div>
                        <CADViewer url={sec.url} filename={sec.name||"model"} height={360} />
                      </div>
                    );
                    if (sec.type==="attachment" && sec.url) {
                      if (isCADFile(sec.name)) return (
                        <div key={i} style={{ margin:"16px 0" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                            <span style={{ fontFamily:"Arial,sans-serif", fontSize:"10pt", fontWeight:700, color:"#063b2b" }}>⬡ {sec.name}</span>
                            <button onClick={() => setCadModal({ url:sec.url, name:sec.name })}
                              style={{ padding:"4px 12px", background:"#10b98118", border:"1px solid #10b981",
                                color:"#10b981", fontFamily:"Arial,sans-serif", fontSize:"9pt", cursor:"pointer", borderRadius:3 }}>
                              🔍 Open 3D Viewer
                            </button>
                          </div>
                          <CADViewer url={sec.url} filename={sec.name} height={360} />
                        </div>
                      );
                      if (/\.pdf$/i.test(sec.name||"")) return <div key={i} style={{ margin:"16px 0" }}><PdfInlineViewer url={sec.url} name={sec.name} /></div>;
                      return <div key={i} style={{ margin:"4px 0" }}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:viewPost._portal.color }}>📎 {sec.name}</a></div>;
                    }
                    return null;
                  })}
                </div>
              </div>
            )}

            {/* COMMENTS tab */}
            {modalTab === "comments" && (
              <div style={{ flex:1, overflowY:"auto", padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
                {postComments.length === 0 && (
                  <div style={{ fontFamily:"monospace", fontSize:12, color:C2.dim, textAlign:"center", padding:"32px 0" }}>No comments yet. Be the first to comment.</div>
                )}
                {postComments.map(c => (
                  <div key={c.id} style={{ background:"#0c0f18", border:`1px solid ${C2.border}`, padding:"12px 14px", borderRadius:6 }}>
                    <div style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, marginBottom:6 }}>{c.by} · {c.createdAt?.toDate?.()?.toLocaleString?.("en-IN") ?? ""}</div>
                    <div style={{ fontFamily:"'Inter',sans-serif", fontSize:13, color:C2.text, lineHeight:1.55 }}>{c.text}</div>
                  </div>
                ))}
                <div style={{ display:"flex", gap:8, marginTop:"auto", paddingTop:16 }}>
                  <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment…" rows={3}
                    style={{ flex:1, background:"#0c0f18", border:`1px solid ${C2.border}`, color:C2.text,
                      padding:"10px 12px", fontFamily:"'Inter',sans-serif", fontSize:13, resize:"vertical", borderRadius:6, outline:"none" }} />
                  <button onClick={postAdminComment} style={{ padding:"0 18px", fontFamily:"monospace", fontSize:10,
                    background:"#0e9cc018", border:"1px solid #0e9cc0", color:"#0e9cc0", cursor:"pointer", letterSpacing:1, alignSelf:"stretch", borderRadius:6 }}>POST</button>
                </div>
              </div>
            )}

            {/* THREAD tab */}
            {modalTab === "thread" && (
              <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <div style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, letterSpacing:2 }}>
                    FOLLOW-UP THREAD · {postThreads.length} {postThreads.length === 1 ? "UPDATE" : "UPDATES"}
                  </div>
                  <Link to={`${viewPost._portal.path}?uploadThread=${viewPost.id}`}
                    style={{ padding:"6px 14px", background:"#0e9cc018", border:"1px solid #0e9cc0",
                      color:"#0e9cc0", fontFamily:"monospace", fontSize:9, letterSpacing:1,
                      cursor:"pointer", textDecoration:"none" }}>
                    + ADD THREAD
                  </Link>
                </div>
                {postThreads.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"48px 0", fontFamily:"monospace", fontSize:12, color:C2.dim }}>No follow-up reports yet.</div>
                ) : postThreads.map((child, i) => (
                  <div key={child.id} style={{ marginBottom:16, padding:"14px 16px",
                    background:"#0a0d16", border:`1px solid ${C2.border}`, borderLeft:"3px solid #0e9cc0", borderRadius:4 }}>
                    <div style={{ fontFamily:"monospace", fontSize:11, color:C2.text, fontWeight:700, marginBottom:4 }}>{child.title}</div>
                    <div style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, marginBottom:8 }}>
                      by {child.uploadedBy} · {child.uploadedAt?.toDate ? child.uploadedAt.toDate().toLocaleDateString() : ""}
                    </div>
                    {(child.sections||[]).filter(s=>s.type==="paragraph").slice(0,1).map((s,j) => (
                      <div key={j} style={{ fontFamily:"'Inter',sans-serif", fontSize:12, color:C2.dim, lineHeight:1.5,
                        display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                        {s.text}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* ADMIN tab */}
            {modalTab === "admin" && role === "admin" && (
              <div style={{ flex:1, overflowY:"auto", padding:"20px 24px", display:"flex", flexDirection:"column", gap:20 }}>
                <div>
                  <div style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, letterSpacing:2, marginBottom:12 }}>REPORT ACTIONS</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    <button onClick={e => handleApprove(e, viewPost)}
                      style={{ padding:"8px 16px", fontFamily:"monospace", fontSize:10, cursor:"pointer", letterSpacing:1,
                        background: viewPost.adminApproved ? `${C2.green}20` : "transparent",
                        border:`1px solid ${viewPost.adminApproved ? C2.green : C2.border}`,
                        color: viewPost.adminApproved ? C2.green : C2.dim, borderRadius:4 }}>
                      {viewPost.adminApproved ? "✓ REVOKE VALIDATION" : "✓ VALIDATE REPORT"}
                    </button>
                    {viewPost.uploadedByUid && (
                      <button onClick={async () => {
                        await updateDoc(doc(db, viewPost._portal.collection, viewPost.id), {
                          [`editCount.${viewPost.uploadedByUid}`]: 0,
                        });
                        alert("Edit access granted to the original uploader.");
                      }} style={{ padding:"8px 16px", fontFamily:"monospace", fontSize:10, cursor:"pointer", letterSpacing:1,
                        background:"#10b98118", border:"1px solid #10b981", color:"#10b981", borderRadius:4 }}>
                        👤 ALLOW EDIT
                      </button>
                    )}
                    <button onClick={e => { handleRemove(e, viewPost); }}
                      style={{ padding:"8px 16px", fontFamily:"monospace", fontSize:10, cursor:"pointer",
                        background:`${C2.red}12`, border:`1px solid ${C2.red}`, color:C2.red, borderRadius:4 }}>
                      🗑 REMOVE REPORT
                    </button>
                  </div>
                </div>
                <div style={{ borderTop:`1px solid ${C2.border}`, paddingTop:16 }}>
                  <div style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, letterSpacing:2, marginBottom:10 }}>POST INFO</div>
                  <div style={{ fontFamily:"monospace", fontSize:10, color:C2.text, lineHeight:2 }}>
                    <div>ID: {viewPost.id}</div>
                    <div>Author: {viewPost.uploadedBy}</div>
                    <div>Department: {viewPost._portal.label}</div>
                    <div>Category: {(viewPost.category||"").replace(/_/g," ") || "—"}</div>
                    <div>Views: {viewPost.viewCount || 0} · Stars: {viewPost.likeCount || 0}</div>
                    <div>Visibility: {viewPost.visibility || "public"}</div>
                    <div>Status: {viewPost.adminApproved ? "✓ Validated" : "Pending"}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {cadModal && (
        <div style={{ position:"fixed", inset:0, background:"#000000ee", zIndex:600,
          display:"flex", flexDirection:"column", paddingTop:NAV_H }}>
          <div style={{ background:"#0f1512", borderBottom:"1px solid #1c2e23", padding:"10px 18px",
            display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
            <span style={{ fontFamily:"monospace", fontSize:11, color:"#10b981" }}>⬡ 3D VIEWER — {cadModal.name}</span>
            <div style={{ flex:1 }} />
            <a href={cadModal.url} download target="_blank" rel="noreferrer"
              style={{ fontFamily:"monospace", fontSize:10, color:"#425c4e", textDecoration:"none",
                border:"1px solid #1c2e23", padding:"5px 10px" }}>↓ DOWNLOAD</a>
            <button onClick={() => setCadModal(null)} style={{ background:"none", border:"none",
              color:"#425c4e", cursor:"pointer", fontSize:22, lineHeight:1 }}>×</button>
          </div>
          <div style={{ flex:1 }}>
            <CADViewer url={cadModal.url} filename={cadModal.name} height={window.innerHeight - 60} />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  PORTAL DETAIL VIEW — admin drills into one department
//  Shows: Reports feed (approve/reject) + Tasks board (add/manage)
// ═══════════════════════════════════════════════════════════
function PortalDetailView({ portal, role, onBack }) {
  // Ops is the C-suite role — same management powers as admin across every portal.
  const canManage = role === 'admin' || role === 'ops' || role === 'manager';
  const [tab,        setTab]        = useState('reports');
  const [posts,      setPosts]      = useState([]);
  const [tasks,      setTasks]      = useState([]);
  const [members,    setMembers]    = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [viewPost,   setViewPost]   = useState(null);
  const C = C2;

  const fmtD = ts => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' });
  };

  useEffect(() => {
    if (!portal.collection) return;
    const q = query(collection(db, portal.collection), orderBy('uploadedAt','desc'));
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id:d.id, _portal:portal, ...d.data() }));
      all.sort((a,b) => {
        if (a.adminApproved && !b.adminApproved) return -1;
        if (!a.adminApproved && b.adminApproved) return 1;
        const ta = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt||0);
        const tb = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt||0);
        return tb - ta;
      });
      setPosts(all);
    }, ()=>{});
  }, [portal]);

  useEffect(() => {
    // Load all tasks, filter client-side to avoid composite index requirement
    const q = query(collection(db, 'tasks'), orderBy('createdAt','desc'));
    return onSnapshot(q,
      snap => setTasks(snap.docs.map(d => ({id:d.id,...d.data()})).filter(t => t.portal === portal.id)),
      ()=>{}
    );
  }, [portal.id]);

  useEffect(() => {
    return onSnapshot(collection(db, 'authorized_users'),
      snap => setMembers(snap.docs.map(d => ({uid:d.id,...d.data()})).filter(m => m.active !== false)),
      ()=>{}
    );
  }, []);

  async function createTask(task) {
    await addDoc(collection(db, 'tasks'), { ...actorStamp(), ...task, portal: portal.id, createdAt: serverTimestamp() });
  }
  // Closing goes through review (two-person rule) — see utils/taskClosure.js
  async function updateStatus(id, status) {
    await setTaskStatus(id, status);
  }
  async function deleteTask(id) {
    if (!window.confirm('Delete this task?')) return;
    await deleteDoc(doc(db, 'tasks', id));
  }

  const taskCols = [
    { key:'pending',     label:'PENDING',     color:'#f59e0b' },
    { key:'in_progress', label:'IN PROGRESS', color:'#0e9cc0' },
    { key:'completed',   label:'COMPLETED',   color:'#10b981' },
  ];

  return (
    <div>
      {/* Header row */}
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:28, flexWrap:'wrap' }}>
        <button onClick={onBack}
          style={{ fontFamily:C.mono, fontSize:10, letterSpacing:1, padding:'7px 16px',
            background:'transparent', border:`1px solid ${C.border}`, color:C.dim, cursor:'pointer', borderRadius:4 }}>
          ← BACK
        </button>
        <div style={{ fontSize:30 }}>{portal.icon}</div>
        <div>
          <div style={{ fontFamily:C.mono, fontSize:14, fontWeight:700, color:portal.color, letterSpacing:2 }}>
            {portal.label.toUpperCase()}
          </div>
          <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, marginTop:2 }}>{portal.desc}</div>
        </div>
        {canManage && tab === 'tasks' && (
          <button onClick={() => setShowCreate(true)}
            style={{ marginLeft:'auto', padding:'9px 20px', fontFamily:C.mono, fontSize:10, letterSpacing:1,
              background:`${portal.color}18`, border:`1px solid ${portal.color}`,
              color:portal.color, cursor:'pointer', borderRadius:4 }}>
            + ASSIGN TASK
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:24, background:'#070a0f', padding:4, width:'fit-content', borderRadius:4 }}>
        {[['reports',`📊  REPORTS (${posts.length})`],['tasks',`📋  TASKS (${tasks.length})`]].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ padding:'8px 20px', fontFamily:C.mono, fontSize:9, cursor:'pointer', letterSpacing:1,
              background: tab===v ? `${portal.color}20` : 'transparent',
              border:`1px solid ${tab===v ? portal.color+'55' : 'transparent'}`,
              color: tab===v ? portal.color : C.dim, borderRadius:3 }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── REPORTS TAB ─────────────────────────────────────── */}
      {tab === 'reports' && (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {posts.length === 0 && (
            <div style={{ textAlign:'center', padding:60, fontFamily:C.mono, fontSize:11, color:C.dim }}>
              No reports uploaded yet.
            </div>
          )}
          {posts.map(post => (
            <div key={post.id}
              style={{ background:C.panel, border:`1px solid ${post.adminApproved ? C.green+'44' : C.border}`,
                borderLeft:`4px solid ${post.adminApproved ? C.green : portal.color}`,
                borderRadius:8, padding:'14px 18px', cursor:'pointer' }}
              onClick={() => setViewPost(post)}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:C.bright, fontSize:14, marginBottom:4 }}>{post.title}</div>
                  <div style={{ fontFamily:C.mono, fontSize:10, color:C.dim }}>
                    By {post.uploadedBy||'Unknown'} · {fmtD(post.uploadedAt)}
                    {post.category && <span style={{ color:portal.color, marginLeft:8 }}>· {post.category}</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }} onClick={e=>e.stopPropagation()}>
                  {post.adminApproved && (
                    <span style={{ fontFamily:C.mono, fontSize:9, color:C.green, background:`${C.green}15`,
                      border:`1px solid ${C.green}44`, padding:'3px 8px', borderRadius:3 }}>✓ APPROVED</span>
                  )}
                  {canManage && (
                    <button onClick={async () => { if(post.adminApproved) await revokeApproval(post); else await approvePost(post); }}
                      style={{ fontFamily:C.mono, fontSize:9, padding:'4px 12px', cursor:'pointer', borderRadius:3,
                        background: post.adminApproved?`${C.green}10`:'transparent',
                        border:`1px solid ${post.adminApproved?C.green:C.border}`,
                        color: post.adminApproved?C.green:C.dim }}>
                      {post.adminApproved ? 'REVOKE' : '✓ APPROVE'}
                    </button>
                  )}
                  {canManage && (
                    <button onClick={async () => { if(!window.confirm('Remove this report?')) return; await removePost(post); }}
                      style={{ fontFamily:C.mono, fontSize:9, padding:'4px 10px', cursor:'pointer', borderRadius:3,
                        background:`${C.red}10`, border:`1px solid ${C.red}44`, color:C.red }}>
                      🗑
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── TASKS TAB ───────────────────────────────────────── */}
      {tab === 'tasks' && (
        <div>
          {/* Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:28 }}>
            {[
              ['TOTAL',       tasks.length,                                    portal.color],
              ['PENDING',     tasks.filter(t=>t.status==='pending').length,    '#f59e0b'  ],
              ['IN PROGRESS', tasks.filter(t=>boardStatus(t.status)==='in_progress').length,'#0e9cc0'  ],
              ['COMPLETED',   tasks.filter(t=>t.status==='completed').length,  '#10b981'  ],
            ].map(([l,v,col]) => (
              <div key={l} style={{ background:C.panel, border:`1px solid ${C.border}`,
                borderTop:`3px solid ${col}`, borderRadius:6, padding:'14px 16px', textAlign:'center' }}>
                <div style={{ fontSize:26, fontWeight:700, color:col, fontFamily:C.mono }}>{v}</div>
                <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginTop:4 }}>{l}</div>
              </div>
            ))}
          </div>

          {tasks.length === 0 ? (
            <div style={{ textAlign:'center', padding:60 }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
              <div style={{ fontFamily:C.mono, fontSize:11, color:C.dim, marginBottom:20 }}>
                No tasks assigned to {portal.label} yet.
              </div>
              {canManage && (
                <button onClick={() => setShowCreate(true)}
                  style={{ fontFamily:C.mono, fontSize:10, letterSpacing:1, padding:'9px 22px',
                    background:`${portal.color}18`, border:`1px solid ${portal.color}`,
                    color:portal.color, cursor:'pointer', borderRadius:4 }}>
                  + ASSIGN FIRST TASK
                </button>
              )}
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:18 }}>
              {taskCols.map(col => {
                const colTasks = tasks.filter(t => boardStatus(t.status) === col.key);
                return (
                  <div key={col.key}>
                    <div style={{ fontFamily:C.mono, fontSize:10, letterSpacing:2, color:col.color,
                      borderBottom:`2px solid ${col.color}44`, paddingBottom:8, marginBottom:14 }}>
                      {col.label} ({colTasks.length})
                    </div>
                    {colTasks.length === 0 && (
                      <div style={{ fontFamily:C.mono, fontSize:10, color:C.dim,
                        textAlign:'center', padding:'18px 0' }}>— none —</div>
                    )}
                    {colTasks.map(task => {
                      const assignee = members.find(m => m.uid === task.assigneeId);
                      const roleColor = {admin:'#ef4444',mp:'#6366f1',embedded:'#f59e0b',system:'#10b981',mentor:'#0ea5e9'}[assignee?.role]||'#6366f1';
                      const due = task.dueDate ? new Date(task.dueDate) : null;
                      const overdue = due && due < new Date() && task.status !== 'completed';
                      return (
                        <div key={task.id} style={{ background:C.panel,
                          border:`1px solid ${overdue?'#ef444455':C.border}`,
                          borderLeft:`3px solid ${col.color}`,
                          borderRadius:8, padding:'14px 16px', marginBottom:10 }}>
                          {/* Title */}
                          <div style={{ fontWeight:700, color:C.bright, fontSize:13, marginBottom:6, lineHeight:1.3 }}>
                            {task.title}
                          </div>
                          <TaskReviewBadge task={task} />
                          {task.description && (
                            <div style={{ fontSize:12, color:C.text, lineHeight:1.5, marginBottom:10 }}>
                              {task.description}
                            </div>
                          )}
                          {/* Assignee card */}
                          {assignee ? (
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10,
                              background:`${roleColor}10`, border:`1px solid ${roleColor}33`,
                              borderRadius:6, padding:'6px 10px' }}>
                              <div style={{ width:30, height:30, borderRadius:'50%', background:`${roleColor}25`,
                                border:`1px solid ${roleColor}55`, display:'flex', alignItems:'center',
                                justifyContent:'center', fontFamily:C.mono, fontSize:11,
                                color:roleColor, flexShrink:0, fontWeight:700 }}>
                                {(assignee.name||'?').slice(0,2).toUpperCase()}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12, fontWeight:600, color:C.bright }}>
                                  {assignee.name||assignee.email}
                                </div>
                                <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim,
                                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {assignee.email}
                                </div>
                              </div>
                              <span style={{ fontFamily:C.mono, fontSize:8, color:roleColor,
                                background:`${roleColor}18`, border:`1px solid ${roleColor}44`,
                                padding:'2px 6px', borderRadius:2, flexShrink:0 }}>
                                {(assignee.role||'').toUpperCase()}
                              </span>
                            </div>
                          ) : (
                            <div style={{ fontFamily:C.mono, fontSize:10, color:C.dim, marginBottom:10 }}>
                              👤 Unassigned
                            </div>
                          )}
                          {/* Meta */}
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                            {task.priority && (
                              <span style={{ fontFamily:C.mono, fontSize:9, padding:'2px 8px', borderRadius:3,
                                background: task.priority==='high'?'#ef444422':task.priority==='critical'?'#a855f722':task.priority==='medium'?'#f59e0b22':'#10b98122',
                                color:      task.priority==='high'?'#ef4444': task.priority==='critical'?'#a855f7': task.priority==='medium'?'#f59e0b':'#10b981' }}>
                                ▲ {task.priority.toUpperCase()}
                              </span>
                            )}
                            {task.dueDate && (
                              <span style={{ fontFamily:C.mono, fontSize:9, color:overdue?'#ef4444':C.dim }}>
                                📅 {fmtD(task.dueDate)}{overdue?' ⚠ OVERDUE':''}
                              </span>
                            )}
                            {task.createdAt?.toDate && (
                              <span style={{ fontFamily:C.mono, fontSize:9, color:C.dim }}>
                                Created {fmtD(task.createdAt)}
                              </span>
                            )}
                          </div>
                          {/* Attachments */}
                          {task.attachments?.length > 0 && (
                            <div style={{ marginBottom:10 }}>
                              {task.attachments.map((a,i) => (
                                <a key={i} href={a.url} target="_blank" rel="noreferrer"
                                  style={{ display:'inline-block', fontSize:10, color:'#0e9cc0',
                                    fontFamily:C.mono, marginRight:8 }}>
                                  📎 {a.name}
                                </a>
                              ))}
                            </div>
                          )}
                          {/* Status + delete actions */}
                          {canManage && (
                            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                              {['pending','in_progress','completed'].map(s => (
                                <button key={s} onClick={() => updateStatus(task.id, s)}
                                  style={{ padding:'3px 9px', fontFamily:C.mono, fontSize:8,
                                    cursor:'pointer', borderRadius:2,
                                    background: task.status===s?`${col.color}20`:'transparent',
                                    border:`1px solid ${task.status===s?col.color:C.border}`,
                                    color: task.status===s?col.color:C.dim }}>
                                  {s==='pending'?'PENDING':s==='in_progress'?'IN PROG':'DONE'}
                                </button>
                              ))}
                              <button onClick={() => deleteTask(task.id)}
                                style={{ marginLeft:'auto', padding:'3px 8px', fontFamily:C.mono, fontSize:8,
                                  background:`${C.red}10`, border:`1px solid ${C.red}44`,
                                  color:C.red, cursor:'pointer', borderRadius:2 }}>✕</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Report detail modal */}
      {viewPost && (
        <div style={{ position:'fixed', inset:0, background:'#000000cc', zIndex:600,
          display:'flex', justifyContent:'center', paddingTop:NAV_H }}>
          <div style={{ background:C.panel, width:'100%', maxWidth:860, display:'flex', flexDirection:'column' }}>
            <div style={{ background:'#07090f', borderBottom:`1px solid ${C.border}`, padding:'10px 20px',
              display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', flexShrink:0 }}>
              <button onClick={() => setViewPost(null)}
                style={{ background:'none', border:'none', color:C.bright, cursor:'pointer',
                  fontFamily:'monospace', fontSize:11, letterSpacing:1 }}>← BACK</button>
              <span style={{ fontFamily:'monospace', fontSize:9, color:portal.color,
                background:`${portal.color}15`, border:`1px solid ${portal.color}33`,
                padding:'2px 10px' }}>{portal.icon} {portal.label}</span>
              <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
                {canManage && (
                  <>
                    <button onClick={async () => { if(viewPost.adminApproved) await revokeApproval(viewPost); else await approvePost(viewPost); setViewPost(p=>({...p,adminApproved:!p.adminApproved})); }}
                      style={{ padding:'5px 12px', fontFamily:'monospace', fontSize:9, cursor:'pointer',
                        background: viewPost.adminApproved?`${C.green}20`:'transparent',
                        border:`1px solid ${viewPost.adminApproved?C.green:C.border}`,
                        color: viewPost.adminApproved?C.green:C.dim }}>
                      {viewPost.adminApproved?'✓ APPROVED':'✓ APPROVE'}
                    </button>
                    <button onClick={async () => { if(!window.confirm('Remove?')) return; await removePost(viewPost); setViewPost(null); }}
                      style={{ padding:'5px 10px', fontFamily:'monospace', fontSize:9, cursor:'pointer',
                        background:`${C.red}12`, border:`1px solid ${C.red}`, color:C.red }}>
                      🗑 REMOVE
                    </button>
                  </>
                )}
              </div>
            </div>
            <div style={{ flex:1, overflowY:'auto', background:'#e8eaf0', padding:'20px 12px' }}>
              <div style={{ background:'#fff', color:'#111', padding:'36px 48px',
                fontFamily:'Times New Roman,serif', fontSize:'11pt', lineHeight:1.65,
                maxWidth:820, margin:'0 auto' }}>
                <div style={{ borderBottom:`2.5px solid ${portal.color}`, paddingBottom:12, marginBottom:24 }}>
                  <div style={{ fontFamily:'Arial,sans-serif', fontSize:'8pt', color:'#999', letterSpacing:2 }}>Spacemancraft Pvt Ltd</div>
                  <div style={{ fontFamily:'Arial,sans-serif', fontSize:'19pt', fontWeight:700,
                    color:portal.color, lineHeight:1.25, margin:'6px 0' }}>{viewPost.title}</div>
                  <div style={{ fontFamily:'Arial,sans-serif', fontSize:'9pt', color:'#666' }}>
                    By {viewPost.uploadedBy} · {fmtD(viewPost.uploadedAt)}
                  </div>
                </div>
                {(viewPost.sections||[]).map((sec,i) => {
                  if (sec.type==='heading')   return <div key={i} style={{ fontFamily:'Arial,sans-serif', fontWeight:700, fontSize:'12pt', color:portal.color, margin:'14px 0 5px' }}>{sec.text}</div>;
                  if (sec.type==='paragraph') return <p key={i} style={{ margin:'0 0 10px', lineHeight:1.7 }}>{sec.text}</p>;
                  if (sec.type==='bullets')   return <ul key={i} style={{ margin:'6px 0 12px', paddingLeft:26 }}>{(sec.items||[]).map((it,j)=><li key={j}>{it}</li>)}</ul>;
                  if ((sec.type==='graph'||sec.type==='image') && sec.url && /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(sec.name||''))
                    return <div key={i} style={{ margin:'16px 0', textAlign:'center' }}><img src={sec.url} alt={sec.caption||''} style={{ maxWidth:'100%' }} /></div>;
                  if (sec.type==='attachment' && sec.url)
                    return <div key={i}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:portal.color }}>📎 {sec.name}</a></div>;
                  return null;
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create task modal — portal pre-filled */}
      {showCreate && (
        <CreateTaskModal
          members={members}
          defaultPortal={portal.id}
          onSave={createTask}
          onClose={() => setShowCreate(false)}
          isAdmin={role === 'admin' || role === 'ops'}
        />
      )}
    </div>
  );
}

// ── Portal card with live recent-report list ──────────────────────────────────
function PortalCard({ portal, role, onViewPost, onExpand }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (portal.noFeed || !portal.collection) return;
    const q = query(collection(db, portal.collection), orderBy("uploadedAt", "desc"));
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id:d.id, _portal:portal, ...d.data() }));
      // approved first, then newest
      all.sort((a,b) => {
        if (a.adminApproved && !b.adminApproved) return -1;
        if (!a.adminApproved && b.adminApproved) return 1;
        const ta = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt||0);
        const tb = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt||0);
        return tb - ta;
      });
      setPosts(all);
    }, () => {});
  }, [portal]);

  const fmtD = ts => {
    if (!ts?.toDate) return "";
    return ts.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short" });
  };

  const approvedCount = posts.filter(p => p.adminApproved).length;
  const preview = posts.slice(0, 5);

  // OBC is a dashboard portal — render a special open-dashboard card
  if (portal.noFeed) {
    return (
      <div style={{ background:C2.panel, border:`1px solid ${C2.border}`, overflow:"hidden", display:"flex", flexDirection:"column" }}>
        <div style={{ background:`linear-gradient(135deg, ${portal.color}22, ${portal.color}08)`,
          borderBottom:`1px solid ${portal.color}33`, padding:"20px 20px 16px", flex:1 }}>
          <div style={{ fontSize:30, marginBottom:6 }}>{portal.icon}</div>
          <div style={{ fontFamily:C2.mono, fontSize:13, fontWeight:700, color:portal.color, letterSpacing:2, marginBottom:4 }}>
            {portal.label.toUpperCase()}
          </div>
          <div style={{ fontFamily:C2.mono, fontSize:8, color:C2.dim, lineHeight:1.7 }}>{portal.desc}</div>
        </div>
        <div style={{ padding:"12px 16px", borderTop:`1px solid ${C2.border}` }}>
          <Link to={portal.path} style={{ display:"inline-block", padding:"8px 18px",
            fontFamily:C2.mono, fontSize:9, letterSpacing:1,
            background:`${portal.color}18`, border:`1px solid ${portal.color}`,
            color:portal.color, textDecoration:"none", borderRadius:6 }}>
            OPEN DASHBOARD →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background:C2.panel, border:`1px solid ${C2.border}`, overflow:"hidden", display:"flex", flexDirection:"column" }}>
      {/* Header */}
      <div style={{ background:`linear-gradient(135deg, ${portal.color}22, ${portal.color}08)`,
        borderBottom:`1px solid ${portal.color}33`, padding:"20px 20px 16px" }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
          <div>
            <div style={{ fontSize:30, marginBottom:6 }}>{portal.icon}</div>
            <div style={{ fontFamily:C2.mono, fontSize:13, fontWeight:700, color:portal.color, letterSpacing:2 }}>{portal.label.toUpperCase()}</div>
            <div style={{ fontFamily:C2.mono, fontSize:8, color:C2.dim, marginTop:4, lineHeight:1.6 }}>{portal.desc}</div>
          </div>
          <div style={{ textAlign:"right", flexShrink:0 }}>
            <div style={{ fontFamily:C2.mono, fontSize:18, fontWeight:700, color:portal.color }}>{posts.length}</div>
            <div style={{ fontFamily:C2.mono, fontSize:8, color:C2.dim }}>reports</div>
            {approvedCount > 0 && (
              <div style={{ fontFamily:C2.mono, fontSize:9, color:C2.green, marginTop:4 }}>✓ {approvedCount} approved</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent reports list */}
      <div style={{ flex:1 }}>
        {preview.length === 0 ? (
          <div style={{ padding:"16px 20px", fontFamily:C2.mono, fontSize:10, color:C2.dim }}>No reports yet.</div>
        ) : preview.map((post, i) => (
          <div key={post.id}
            onClick={() => onViewPost && onViewPost(post)}
            style={{ padding:"9px 16px", borderBottom:`1px solid ${C2.border}30`,
              cursor: onViewPost ? "pointer" : "default",
              background: post.adminApproved ? `${C2.green}05` : "transparent",
              display:"flex", alignItems:"center", gap:8 }}
            onMouseEnter={e => { if(onViewPost) e.currentTarget.style.background = `${portal.color}08`; }}
            onMouseLeave={e => { e.currentTarget.style.background = post.adminApproved ? `${C2.green}05` : "transparent"; }}>
            <div style={{ width:4, height:4, borderRadius:"50%", flexShrink:0,
              background: post.adminApproved ? C2.green : portal.color+"77" }} />
            <div style={{ flex:1, minWidth:0, fontFamily:C2.sans, fontSize:12, color:C2.text,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{post.title}</div>
            <div style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
              {post.adminApproved && <span style={{ fontFamily:C2.mono, fontSize:8, color:C2.green }}>✓</span>}
              <span style={{ fontFamily:C2.mono, fontSize:9, color:C2.dim }}>{fmtD(post.uploadedAt)}</span>
            </div>
          </div>
        ))}
        {posts.length > 5 && (
          <div style={{ padding:"8px 16px", fontFamily:C2.mono, fontSize:9, color:C2.dim }}>
            +{posts.length - 5} more
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding:"12px 16px", borderTop:`1px solid ${C2.border}`, display:"flex", justifyContent:"flex-end", gap:8 }}>
        {onExpand && (
          <button onClick={() => onExpand(portal)}
            style={{ padding:"7px 16px", fontFamily:C2.mono, fontSize:9, cursor:"pointer",
              background:"#a855f718", border:"1px solid #a855f755", color:"#a855f7",
              letterSpacing:1, whiteSpace:"nowrap" }}>
            📊 VIEW DETAILS
          </button>
        )}
        <Link to={portal.path} style={{ padding:"7px 16px", fontFamily:C2.mono, fontSize:9,
          background:`${portal.color}18`, border:`1px solid ${portal.color}`, color:portal.color,
          textDecoration:"none", letterSpacing:1, whiteSpace:"nowrap" }}>
          OPEN PORTAL →
        </Link>
      </div>
    </div>
  );
}

function AnalysisModulesGrid({ onNavigate, visits, role }) {
  const C = useTheme();
  const [hovered, setHovered] = useState(null);
  return (
    <div>
      <div style={{ ...mono, fontSize:9, color:C.muted, letterSpacing:3, marginBottom:16 }}>ANALYSIS MODULES — SELECT TO OPEN</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12 }}>
        {PAGES.map(p => {
          const count   = visits[p.id] || 0;
          const allowed = ROLE_PAGES[role];
          const locked  = allowed !== null && allowed !== undefined && !allowed.includes(p.id);
          return locked ? (
            <div key={p.id} style={{ background:C.panel, border:`1px solid ${C.border}`,
              borderLeft:`3px solid #3a506840`, borderRadius:8, padding:"18px 20px",
              cursor:"not-allowed", opacity:0.5, position:"relative", overflow:"hidden" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div style={{ ...mono, fontSize:9, color:C.muted }}>§ {p.section}</div>
                <div style={{ fontSize:20, opacity:0.3 }}>{p.icon}</div>
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:C.muted, marginBottom:6, lineHeight:1.3 }}>{p.title}</div>
              <div style={{ ...mono, fontSize:10, color:C.muted, lineHeight:1.5 }}>{p.subtitle}</div>
              <div style={{ ...mono, fontSize:9, color:"#ef444488", marginTop:12, letterSpacing:1 }}>🔒 REQUIRES ADMIN LOGIN</div>
            </div>
          ) : (
            <div key={p.id} onClick={() => onNavigate(p.id)}
              onMouseEnter={() => setHovered(p.id)} onMouseLeave={() => setHovered(null)}
              style={{ background: hovered===p.id ? `${p.color}10` : C.panel,
                border:`1px solid ${hovered===p.id ? p.color+"55" : C.border}`,
                borderLeft:`3px solid ${p.color}`, borderRadius:8, padding:"18px 20px",
                cursor:"pointer", transition:"all 0.15s",
                transform: hovered===p.id ? "translateY(-2px)" : "none",
                boxShadow: hovered===p.id ? `0 8px 24px ${p.color}15` : "none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div style={{ ...mono, fontSize:9, color:C.muted }}>§ {p.section}</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  {count > 0 && (
                    <span style={{ ...mono, fontSize:9, color:p.color, background:`${p.color}18`,
                      border:`1px solid ${p.color}44`, padding:"1px 7px", borderRadius:10 }}>
                      {count} {count === 1 ? "visit" : "visits"}
                    </span>
                  )}
                  <div style={{ fontSize:20, opacity:0.6 }}>{p.icon}</div>
                </div>
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:p.color, marginBottom:6, lineHeight:1.3 }}>{p.title}</div>
              <div style={{ ...mono, fontSize:10, color:C.muted, lineHeight:1.5 }}>{p.subtitle}</div>
              <div style={{ ...mono, fontSize:9, color:p.color+"66", marginTop:12 }}>OPEN →</div>
            </div>
          );
        })}
        {(!ROLE_PAGES[role] || ROLE_PAGES[role]?.length > 5) && (
          <div style={{ background:"transparent", border:`1px dashed ${C.border}`, borderRadius:8,
            padding:"18px 20px", display:"flex", flexDirection:"column",
            justifyContent:"center", alignItems:"center", opacity:0.4 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>+</div>
            <div style={{ ...mono, fontSize:10, color:C.muted, textAlign:"center" }}>Add new module</div>
          </div>
        )}
      </div>
    </div>
  );
}

function PhysicsPage({ onNavigate, visits, role }) {
  const C = useTheme();
  const [hovered, setHovered] = useState(null);
  const pages = PAGES.filter(p => p.category === "physics");
  return (
    <div className="smc-page-container" style={{ maxWidth:1100, margin:"0 auto", padding:"0 20px 60px" }}>
      <div style={{ padding:"48px 0 32px", textAlign:"center" }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:9, color:"#a855f755", letterSpacing:6, marginBottom:12 }}>SPACEMANCRAFT</div>
        <h2 style={{ fontSize:32, fontWeight:800, margin:0, background:"linear-gradient(135deg,#6366f1,#a855f7)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>PHYSICS EXPERIMENTS</h2>
        <p style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:10, color:C.muted, marginTop:12, letterSpacing:2 }}>INTERACTIVE SIMULATIONS · ORBITAL MECHANICS · ATTITUDE DYNAMICS</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:16 }}>
        {pages.map(p => {
          const count   = visits[p.id] || 0;
          const allowed = ROLE_PAGES[role];
          const locked  = allowed !== null && allowed !== undefined && !allowed.includes(p.id);
          return locked ? (
            <div key={p.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderLeft:`3px solid #3a506840`, borderRadius:8, padding:"18px 20px", cursor:"not-allowed", opacity:0.5 }}>
              <div style={{ fontSize:20, marginBottom:8 }}>{p.icon}</div>
              <div style={{ fontSize:14, fontWeight:700, color:C.muted, marginBottom:6 }}>{p.title}</div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted }}>{p.subtitle}</div>
              <div style={{ fontFamily:"monospace", fontSize:9, color:"#ef444488", marginTop:12 }}>🔒 REQUIRES ADMIN LOGIN</div>
            </div>
          ) : (
            <div key={p.id} onClick={() => onNavigate(p.id)}
              onMouseEnter={() => setHovered(p.id)} onMouseLeave={() => setHovered(null)}
              style={{ background: hovered===p.id ? `${p.color}12` : C.panel,
                border:`1px solid ${hovered===p.id ? p.color+"55" : C.border}`,
                borderLeft:`3px solid ${p.color}`, borderRadius:8, padding:"18px 20px",
                cursor:"pointer", transition:"all 0.15s",
                transform: hovered===p.id ? "translateY(-2px)" : "none",
                boxShadow: hovered===p.id ? `0 8px 24px ${p.color}15` : "none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ fontSize:28 }}>{p.icon}</div>
                {count > 0 && <span style={{ fontFamily:"monospace", fontSize:9, color:p.color, background:`${p.color}18`, border:`1px solid ${p.color}44`, padding:"1px 7px", borderRadius:10 }}>{count}×</span>}
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:p.color, marginBottom:8, lineHeight:1.3 }}>{p.title}</div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, lineHeight:1.5 }}>{p.subtitle}</div>
              <div style={{ fontFamily:"monospace", fontSize:9, color:p.color+"66", marginTop:14, letterSpacing:1 }}>LAUNCH →</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SimulationsPage({ onNavigate, visits, role }) {
  const C = useTheme();
  const [hovered, setHovered] = useState(null);
  const pages = PAGES.filter(p => p.category === "simulations");
  return (
    <div className="smc-page-container" style={{ maxWidth:1100, margin:"0 auto", padding:"0 20px 60px" }}>
      <div style={{ padding:"48px 0 32px", textAlign:"center" }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:9, color:"#0e9cc055", letterSpacing:6, marginBottom:12 }}>SPACEMANCRAFT</div>
        <h2 style={{ fontSize:32, fontWeight:800, margin:0, background:"linear-gradient(135deg,#0e9cc0,#22c55e)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>SIMULATIONS</h2>
        <p style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:10, color:C.muted, marginTop:12, letterSpacing:2 }}>ORBIT SIMULATORS · PROPULSION CALCULATORS · MISSION ANALYSIS</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:16 }}>
        {pages.map(p => {
          const count   = visits[p.id] || 0;
          const allowed = ROLE_PAGES[role];
          const locked  = allowed !== null && allowed !== undefined && !allowed.includes(p.id);
          return locked ? (
            <div key={p.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderLeft:`3px solid #3a506840`, borderRadius:8, padding:"18px 20px", cursor:"not-allowed", opacity:0.5 }}>
              <div style={{ fontSize:20, marginBottom:8 }}>{p.icon}</div>
              <div style={{ fontSize:14, fontWeight:700, color:C.muted, marginBottom:6 }}>{p.title}</div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted }}>{p.subtitle}</div>
              <div style={{ fontFamily:"monospace", fontSize:9, color:"#ef444488", marginTop:12 }}>🔒 REQUIRES ADMIN LOGIN</div>
            </div>
          ) : (
            <div key={p.id} onClick={() => onNavigate(p.id)}
              onMouseEnter={() => setHovered(p.id)} onMouseLeave={() => setHovered(null)}
              style={{ background: hovered===p.id ? `${p.color}12` : C.panel,
                border:`1px solid ${hovered===p.id ? p.color+"55" : C.border}`,
                borderLeft:`3px solid ${p.color}`, borderRadius:8, padding:"18px 20px",
                cursor:"pointer", transition:"all 0.15s",
                transform: hovered===p.id ? "translateY(-2px)" : "none",
                boxShadow: hovered===p.id ? `0 8px 24px ${p.color}15` : "none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ fontSize:28 }}>{p.icon}</div>
                {count > 0 && <span style={{ fontFamily:"monospace", fontSize:9, color:p.color, background:`${p.color}18`, border:`1px solid ${p.color}44`, padding:"1px 7px", borderRadius:10 }}>{count}×</span>}
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:p.color, marginBottom:8, lineHeight:1.3 }}>{p.title}</div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, lineHeight:1.5 }}>{p.subtitle}</div>
              <div style={{ fontFamily:"monospace", fontSize:9, color:p.color+"66", marginTop:14, letterSpacing:1 }}>OPEN →</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DashboardPage({ onNavigate, visits, role }) {
  const C = useTheme();
  const [hovered, setHovered] = useState(null);
  const pages = PAGES.filter(p => p.category === "dashboard");
  return (
    <div className="smc-page-container" style={{ maxWidth:1100, margin:"0 auto", padding:"0 20px 60px" }}>
      <div style={{ padding:"48px 0 32px", textAlign:"center" }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:9, color:"#f59e0b55", letterSpacing:6, marginBottom:12 }}>SPACEMANCRAFT</div>
        <h2 style={{ fontSize:32, fontWeight:800, margin:0, background:"linear-gradient(135deg,#f59e0b,#ef4444)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>DASHBOARD</h2>
        <p style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:10, color:C.muted, marginTop:12, letterSpacing:2 }}>SATELLITE PLAYBOOK · DESIGN TOOLS · HARDWARE REFERENCE</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:16 }}>
        {pages.map(p => {
          const count   = visits[p.id] || 0;
          const allowed = ROLE_PAGES[role];
          const locked  = allowed !== null && allowed !== undefined && !allowed.includes(p.id);
          return locked ? (
            <div key={p.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderLeft:`3px solid #3a506840`, borderRadius:8, padding:"18px 20px", cursor:"not-allowed", opacity:0.5 }}>
              <div style={{ fontSize:20, marginBottom:8 }}>{p.icon}</div>
              <div style={{ fontSize:14, fontWeight:700, color:C.muted, marginBottom:6 }}>{p.title}</div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted }}>{p.subtitle}</div>
              <div style={{ fontFamily:"monospace", fontSize:9, color:"#ef444488", marginTop:12 }}>🔒 REQUIRES ADMIN LOGIN</div>
            </div>
          ) : (
            <div key={p.id} onClick={() => onNavigate(p.id)}
              onMouseEnter={() => setHovered(p.id)} onMouseLeave={() => setHovered(null)}
              style={{ background: hovered===p.id ? `${p.color}12` : C.panel,
                border:`1px solid ${hovered===p.id ? p.color+"55" : C.border}`,
                borderLeft:`3px solid ${p.color}`, borderRadius:8, padding:"18px 20px",
                cursor:"pointer", transition:"all 0.15s",
                transform: hovered===p.id ? "translateY(-2px)" : "none",
                boxShadow: hovered===p.id ? `0 8px 24px ${p.color}15` : "none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ fontSize:28 }}>{p.icon}</div>
                {count > 0 && <span style={{ fontFamily:"monospace", fontSize:9, color:p.color, background:`${p.color}18`, border:`1px solid ${p.color}44`, padding:"1px 7px", borderRadius:10 }}>{count}×</span>}
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:p.color, marginBottom:8, lineHeight:1.3 }}>{p.title}</div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, lineHeight:1.5 }}>{p.subtitle}</div>
              <div style={{ fontFamily:"monospace", fontSize:9, color:p.color+"66", marginTop:14, letterSpacing:1 }}>OPEN →</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TaskCard({ task, members, canManage, onStatusChange, onDelete }) {
  const assignee = members.find(m => m.uid === task.assigneeId);
  const priorityColor = { low:'#10b981', medium:'#f59e0b', high:'#ef4444', critical:'#a855f7' }[task.priority] || '#6366f1';
  const statusColor   = { pending:'#6366f1', in_progress:'#f59e0b', completed:'#10b981', overdue:'#ef4444' }[task.status] || '#6366f1';
  const fmtD = ts => !ts?.toDate ? (ts ? new Date(ts).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : '—') : ts.toDate().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
  const portalColor = { mp:'#6366f1', embedded:'#f59e0b', system:'#10b981' }[task.portal] || '#0ea5e9';
  const now = new Date();
  const due = task.dueDate ? new Date(task.dueDate) : null;
  const overdue = due && due < now && task.status !== 'completed';
  const C = C2;
  return (
    <div style={{ background:C.panel, border:`1px solid ${overdue ? '#ef444455' : C.border}`,
      borderLeft:`3px solid ${statusColor}`, padding:'12px 14px', marginBottom:8, borderRadius:4 }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:6 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.bright, lineHeight:1.35, marginBottom:3 }}>{task.title}</div>
          <TaskReviewBadge task={task} />
          {task.description && <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, lineHeight:1.5 }}>{task.description}</div>}
        </div>
        <span style={{ fontFamily:C.mono, fontSize:8, color:priorityColor, background:`${priorityColor}18`,
          border:`1px solid ${priorityColor}44`, padding:'1px 6px', borderRadius:2, whiteSpace:'nowrap', flexShrink:0 }}>
          {task.priority||'medium'}
        </span>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center', marginBottom:8 }}>
        {assignee && (
          <span style={{ fontFamily:C.mono, fontSize:9, color:C.text, background:C.panel2, border:`1px solid ${C.border}`, padding:'2px 7px', borderRadius:2 }}>
            👤 {assignee.name||assignee.email}
          </span>
        )}
        {task.portal && (
          <span style={{ fontFamily:C.mono, fontSize:9, color:portalColor, background:`${portalColor}15`, border:`1px solid ${portalColor}33`, padding:'2px 7px', borderRadius:2 }}>
            {task.portal==='mp'?'🛰 MP':task.portal==='embedded'?'📟 EMB':'⚙ SE'}
          </span>
        )}
        {due && (
          <span style={{ fontFamily:C.mono, fontSize:9, color: overdue ? '#ef4444' : C.dim, padding:'2px 7px' }}>
            📅 {fmtD(task.dueDate)}
          </span>
        )}
      </div>
      {canManage && (
        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          {['pending','in_progress','completed'].map(s => (
            <button key={s} onClick={() => onStatusChange(task.id, s)}
              style={{ padding:'3px 9px', fontFamily:C.mono, fontSize:8, cursor:'pointer', borderRadius:2,
                background: task.status===s ? `${statusColor}20` : 'transparent',
                border:`1px solid ${task.status===s ? statusColor : C.border}`,
                color: task.status===s ? statusColor : C.dim }}>
              {s==='pending'?'PENDING':s==='in_progress'?'IN PROGRESS':'DONE'}
            </button>
          ))}
          <button onClick={() => onDelete(task.id)} style={{ marginLeft:'auto', padding:'3px 8px',
            background:`${C2.red}10`, border:`1px solid ${C2.red}44`, color:C2.red, fontFamily:C.mono, fontSize:8, cursor:'pointer', borderRadius:2 }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── Visibility helpers ────────────────────────────────────────────────────────
function canSeePost(post, userId, isAdmin) {
  if (isAdmin) return true;
  const v = post.visibility || 'org';
  if (v === 'private')  return (post.allowedUsers || []).includes(userId);
  if (v === 'unlisted') return false;
  return true; // 'org'
}

function VisibilityPicker({ value, onChange, allowedUsers, onAllowedUsersChange, members }) {
  const C = useTheme();
  const OPTS = [
    { v:'org',      label:'Organisation', icon:'🌐' },
    { v:'unlisted', label:'Unlisted',     icon:'🔗' },
    { v:'private',  label:'Private',      icon:'🔒' },
  ];
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontFamily:C.mono||'monospace', fontSize:9, color:'#a855f7', letterSpacing:2, marginBottom:8 }}>
        VISIBILITY
      </div>
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom: value === 'private' ? 10 : 0 }}>
        {OPTS.map(o => (
          <button key={o.v} type="button" onClick={() => onChange(o.v)}
            style={{ padding:'6px 12px', fontFamily:'monospace', fontSize:9, cursor:'pointer', borderRadius:4,
              background: value === o.v ? '#a855f720' : 'transparent',
              border:`1px solid ${value === o.v ? '#a855f7' : C.border}`,
              color: value === o.v ? '#a855f7' : C.muted }}>
            {o.icon} {o.label}
          </button>
        ))}
      </div>
      {value === 'private' && members && members.length > 0 && (
        <div>
          <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:1, marginBottom:6 }}>
            ALLOWED USERS (select who can see this)
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {members.map(m => {
              const sel = (allowedUsers || []).includes(m.uid);
              return (
                <button key={m.uid} type="button" onClick={() => {
                  const next = sel
                    ? (allowedUsers || []).filter(u => u !== m.uid)
                    : [...(allowedUsers || []), m.uid];
                  onAllowedUsersChange(next);
                }}
                  style={{ padding:'4px 10px', fontFamily:'monospace', fontSize:9, cursor:'pointer', borderRadius:10,
                    background: sel ? '#a855f720' : 'transparent',
                    border:`1px solid ${sel ? '#a855f7' : C.border}`,
                    color: sel ? '#a855f7' : C.muted }}>
                  {m.name || m.email}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTaskModal({ members, onSave, onClose, defaultPortal, isAdmin }) {
  const [title,        setTitle]        = useState('');
  const [desc,         setDesc]         = useState('');
  const [assigneeId,   setAssigneeId]   = useState('');
  const [priority,     setPriority]     = useState('medium');
  const [dueDate,      setDueDate]      = useState('');
  const [portal,       setPortal]       = useState(defaultPortal || '');
  const [saving,       setSaving]       = useState(false);
  const [pickerOpen,   setPickerOpen]   = useState(false);
  const [attachments,  setAttachments]  = useState([]); // [{name,url,size}]
  const [uploading,    setUploading]    = useState(false);
  const [visibility,   setVisibility]   = useState('org');
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [requiresProof, setRequiresProof] = useState(true);
  const C = C2;

  const assignee = members.find(m => m.uid === assigneeId);

  const roleColor = { admin:'#ef4444', mp:'#6366f1', embedded:'#f59e0b', system:'#10b981', mentor:'#a855f7', investor:'#f59e0b', guest:'#06b6d4' };
  const roleLabel = { admin:'ADMIN', mp:'MISSION PLAN', embedded:'EMBEDDED', system:'SYSTEMS ENG', mentor:'MENTOR', investor:'INVESTOR', guest:'GUEST' };

  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { getStorage, ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      const path = `tasks/attachments/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const task = uploadBytesResumable(ref(storage, path), file);
      await new Promise((res, rej) => task.on('state_changed', null, rej, res));
      const url = await getDownloadURL(task.snapshot.ref);
      setAttachments(prev => [...prev, { name: file.name, url, size: file.size }]);
    } catch (ex) { alert('Upload failed: ' + ex.message); }
    setUploading(false);
    e.target.value = '';
  }

  async function save() {
    if (!title.trim()) return;
    setSaving(true);
    const payload = { title: title.trim(), description: desc.trim(), assigneeId, assigneeName: assignee?.name || '',
      priority, dueDate, portal, status: 'pending', attachments, requiresProof, sourceType: 'manual' };
    if (isAdmin) { payload.visibility = visibility; payload.allowedUsers = allowedUsers; }
    await onSave(payload);
    setSaving(false);
    onClose();
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'#000000bb', zIndex:400, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:`${NAV_H + 20}px 20px 20px` }}>
      <div style={{ background:C.panel, border:`1px solid ${C.border}`, width:'100%', maxWidth:520, maxHeight:'92vh', display:'flex', flexDirection:'column', borderRadius:6 }}>

        {/* Header */}
        <div style={{ padding:'12px 18px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center' }}>
          <span style={{ fontFamily:C.mono, fontSize:11, color:'#a855f7', letterSpacing:2 }}>📋 ASSIGN NEW TASK</span>
          <div style={{ flex:1 }} />
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:20 }}>×</button>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>

          {/* Title */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:5 }}>TASK TITLE *</div>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done…"
              style={{ width:'100%', background:C.panel2||'#0c0f18', border:`1px solid ${C.border}`, color:C.bright, padding:'9px 12px', fontFamily:"'Inter',sans-serif", fontSize:14, borderRadius:3 }} />
          </div>

          {/* Description */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:5 }}>DESCRIPTION</div>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} placeholder="Additional details, links, context…"
              style={{ width:'100%', background:C.panel2||'#0c0f18', border:`1px solid ${C.border}`, color:C.text, padding:'9px 12px', fontFamily:"'Inter',sans-serif", fontSize:13, resize:'vertical', borderRadius:3 }} />
          </div>

          {/* Assignee — rich picker */}
          <div style={{ marginBottom:14, position:'relative' }}>
            <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:5 }}>ASSIGNEE</div>
            <div onClick={() => setPickerOpen(p => !p)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', cursor:'pointer',
                background:C.panel2||'#0c0f18', border:`1px solid ${pickerOpen ? '#a855f7' : C.border}`, borderRadius:3 }}>
              {assignee ? (
                <>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:`${roleColor[assignee.role]||'#6366f1'}25`,
                    border:`1px solid ${roleColor[assignee.role]||'#6366f1'}55`,
                    display:'flex', alignItems:'center', justifyContent:'center', fontFamily:C.mono, fontSize:10,
                    color:roleColor[assignee.role]||'#6366f1', flexShrink:0 }}>
                    {(assignee.name||'?').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, color:C.bright, fontWeight:600 }}>{assignee.name||assignee.email}</div>
                    <div style={{ fontFamily:C.mono, fontSize:9, color:roleColor[assignee.role]||'#6366f1' }}>{roleLabel[assignee.role]||assignee.role}</div>
                  </div>
                </>
              ) : (
                <div style={{ fontFamily:C.mono, fontSize:11, color:C.dim }}>— Select team member —</div>
              )}
              <div style={{ fontFamily:C.mono, fontSize:10, color:C.dim }}>{pickerOpen ? '▲' : '▼'}</div>
            </div>

            {pickerOpen && (
              <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:50,
                background:C.panel, border:`1px solid ${C.border}`, maxHeight:240, overflowY:'auto',
                boxShadow:'0 8px 24px #00000066', borderRadius:'0 0 4px 4px' }}>
                <div onClick={() => { setAssigneeId(''); setPickerOpen(false); }}
                  style={{ padding:'10px 14px', cursor:'pointer', fontFamily:C.mono, fontSize:11, color:C.dim,
                    borderBottom:`1px solid ${C.border}` }}>
                  — Unassigned —
                </div>
                {members.map(m => {
                  const rc = roleColor[m.role]||'#6366f1';
                  const rl = roleLabel[m.role]||m.role;
                  return (
                    <div key={m.uid} onClick={() => { setAssigneeId(m.uid); setPickerOpen(false); }}
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', cursor:'pointer',
                        background: assigneeId===m.uid ? `${rc}12` : 'transparent',
                        borderBottom:`1px solid ${C.border}22` }}>
                      <div style={{ width:36, height:36, borderRadius:'50%', background:`${rc}20`,
                        border:`1px solid ${rc}44`, display:'flex', alignItems:'center', justifyContent:'center',
                        fontFamily:C.mono, fontSize:12, color:rc, flexShrink:0, fontWeight:700 }}>
                        {(m.name||'?').slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:C.bright, marginBottom:2 }}>{m.name||'—'}</div>
                        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.email}</div>
                      </div>
                      <span style={{ fontFamily:C.mono, fontSize:8, color:rc, background:`${rc}18`,
                        border:`1px solid ${rc}44`, padding:'2px 7px', borderRadius:2, flexShrink:0 }}>{rl}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Priority + Portal */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:5 }}>PRIORITY</div>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                style={{ width:'100%', background:C.panel2||'#0c0f18', border:`1px solid ${C.border}`, color:C.text, padding:'8px 11px', fontFamily:"'Inter',sans-serif", fontSize:13, borderRadius:3 }}>
                {['low','medium','high','critical'].map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:5 }}>PORTAL</div>
              <select value={portal} onChange={e => setPortal(e.target.value)}
                style={{ width:'100%', background:C.panel2||'#0c0f18', border:`1px solid ${C.border}`, color:C.text, padding:'8px 11px', fontFamily:"'Inter',sans-serif", fontSize:13, borderRadius:3 }}>
                <option value="">— Any —</option>
                <option value="mp">🛰 Mission Planning</option>
                <option value="embedded">📟 Embedded Systems</option>
                <option value="system">⚙ Systems Engineering</option>
              </select>
            </div>
          </div>

          {/* Due date */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:5 }}>DUE DATE</div>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              style={{ width:'100%', background:C.panel2||'#0c0f18', border:`1px solid ${C.border}`, color:C.bright, padding:'8px 12px', fontFamily:"'Inter',sans-serif", fontSize:13, borderRadius:3 }} />
          </div>

          {/* Attachments */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2, marginBottom:8 }}>ATTACHMENTS</div>
            {attachments.map((a, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px',
                background:C.panel2||'#0c0f18', border:`1px solid ${C.border}`, marginBottom:5, borderRadius:3 }}>
                <span style={{ fontSize:14 }}>📎</span>
                <a href={a.url} target="_blank" rel="noreferrer"
                  style={{ flex:1, fontFamily:C.mono, fontSize:10, color:'#a855f7', textDecoration:'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {a.name}
                </a>
                <span style={{ fontFamily:C.mono, fontSize:9, color:C.dim }}>
                  {a.size > 1048576 ? (a.size/1048576).toFixed(1)+'MB' : Math.round(a.size/1024)+'KB'}
                </span>
                <button onClick={() => setAttachments(prev => prev.filter((_,j) => j!==i))}
                  style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:14, padding:'0 2px' }}>×</button>
              </div>
            ))}
            <label style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px', cursor: uploading ? 'wait' : 'pointer',
              background:'transparent', border:`1px dashed ${C.border}`, color:C.dim,
              fontFamily:C.mono, fontSize:10, borderRadius:3 }}>
              <input type="file" style={{ display:'none' }} onChange={handleFileUpload} disabled={uploading} />
              {uploading ? '⏳ Uploading…' : '+ Add attachment'}
            </label>
          </div>

          {/* Visibility — admin only */}
          {isAdmin && (
            <VisibilityPicker
              value={visibility}
              onChange={setVisibility}
              allowedUsers={allowedUsers}
              onAllowedUsersChange={setAllowedUsers}
              members={members}
            />
          )}

          {/* Requires proof — defaults to on */}
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginTop:12 }}>
            <input type="checkbox" checked={requiresProof} onChange={e => setRequiresProof(e.target.checked)}
              style={{ width:14, height:14, cursor:'pointer', accentColor:'#a855f7' }} />
            <span style={{ fontFamily:C.mono, fontSize:9, color:C.dim }}>
              Require proof (text or attachment) before this can be marked complete
            </span>
          </label>
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 18px', borderTop:`1px solid ${C.border}` }}>
          <button onClick={save} disabled={saving || !title.trim()}
            style={{ width:'100%', padding:'11px', fontFamily:C.mono, fontSize:11, letterSpacing:2, borderRadius:3,
              background: (saving || !title.trim()) ? 'transparent' : '#a855f720',
              border:`1px solid ${(saving || !title.trim()) ? C.border : '#a855f7'}`,
              color: (saving || !title.trim()) ? C.dim : '#a855f7',
              cursor: (saving || !title.trim()) ? 'not-allowed' : 'pointer' }}>
            {saving ? 'SAVING…' : 'ASSIGN TASK →'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TasksPage({ role }) {
  // Ops (C-suite) gets admin-equivalent task management across every portal.
  const canManage = role === 'admin' || role === 'ops' || role === 'manager';
  const [tasks,      setTasks]      = useState([]);
  const [members,    setMembers]    = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterPortal,   setFilterPortal]   = useState('all');
  const C = C2;

  useEffect(() => {
    const q = query(collection(db, 'tasks'), orderBy('createdAt','desc'));
    return onSnapshot(q, snap => setTasks(snap.docs.map(d => ({id:d.id,...d.data()}))), ()=>{});
  }, []);

  useEffect(() => {
    return onSnapshot(collection(db, 'authorized_users'),
      snap => setMembers(snap.docs.map(d => ({uid:d.id,...d.data()})).filter(m => m.active !== false)),
      ()=>{}
    );
  }, []);

  async function createTask(task) {
    await addDoc(collection(db, 'tasks'), { ...actorStamp(), ...task, createdAt: serverTimestamp() });
  }
  // Closing goes through review (two-person rule) — see utils/taskClosure.js
  async function updateStatus(taskId, status) {
    await setTaskStatus(taskId, status);
  }
  async function deleteTask(taskId) {
    if (!window.confirm('Delete this task?')) return;
    await deleteDoc(doc(db, 'tasks', taskId));
  }

  const filtered = tasks
    .filter(t => filterStatus==='all' || boardStatus(t.status)===filterStatus)
    .filter(t => filterAssignee==='all' || t.assigneeId===filterAssignee)
    .filter(t => filterPortal==='all' || t.portal===filterPortal);

  const counts = { all:tasks.length, pending:0, in_progress:0, completed:0 };
  tasks.forEach(t => { const st = boardStatus(t.status); if (counts[st]!==undefined) counts[st]++; });

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20, alignItems:'center' }}>
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.dim, letterSpacing:2 }}>FILTERS:</div>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{ background:C.panel, border:`1px solid ${C.border}`, color:C.text, padding:'5px 10px', fontFamily:C.mono, fontSize:10 }}>
          <option value="all">ALL STATUS ({counts.all})</option>
          <option value="pending">PENDING ({counts.pending})</option>
          <option value="in_progress">IN PROGRESS ({counts.in_progress})</option>
          <option value="completed">COMPLETED ({counts.completed})</option>
        </select>
        <select value={filterAssignee} onChange={e=>setFilterAssignee(e.target.value)}
          style={{ background:C.panel, border:`1px solid ${C.border}`, color:C.text, padding:'5px 10px', fontFamily:C.mono, fontSize:10 }}>
          <option value="all">ALL MEMBERS</option>
          {members.map(m => <option key={m.uid} value={m.uid}>{m.name||m.email}</option>)}
        </select>
        <select value={filterPortal} onChange={e=>setFilterPortal(e.target.value)}
          style={{ background:C.panel, border:`1px solid ${C.border}`, color:C.text, padding:'5px 10px', fontFamily:C.mono, fontSize:10 }}>
          <option value="all">ALL PORTALS</option>
          <option value="mp">Mission Planning</option>
          <option value="embedded">Embedded Systems</option>
          <option value="system">Systems Engineering</option>
        </select>
        <div style={{ flex:1 }} />
        {canManage && (
          <button onClick={() => setShowCreate(true)}
            style={{ padding:'8px 18px', fontFamily:C.mono, fontSize:10, letterSpacing:1, cursor:'pointer',
              background:'#a855f720', border:'1px solid #a855f755', color:'#a855f7' }}>
            + ASSIGN TASK
          </button>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
        {[
          { label:'TOTAL TASKS',   val:counts.all,         color:'#6366f1' },
          { label:'PENDING',       val:counts.pending,     color:'#6366f1' },
          { label:'IN PROGRESS',   val:counts.in_progress, color:'#f59e0b' },
          { label:'COMPLETED',     val:counts.completed,   color:'#10b981' },
        ].map(s => (
          <div key={s.label} style={{ background:C.panel, border:`1px solid ${C.border}`, padding:'14px 16px', borderLeft:`3px solid ${s.color}` }}>
            <div style={{ fontSize:22, fontWeight:800, color:s.color }}>{s.val}</div>
            <div style={{ fontFamily:C.mono, fontSize:8, color:C.dim, letterSpacing:2, marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Task board — 3 columns */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', fontFamily:C.mono, fontSize:13, color:C.dim }}>
          {tasks.length===0 ? 'No tasks assigned yet.' : 'No tasks match filters.'}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px,1fr))', gap:16 }}>
          {['pending','in_progress','completed'].map(status => {
            const col = filtered.filter(t => boardStatus(t.status||'pending')===status);
            if (!col.length) return null;
            const sc = { pending:'#6366f1', in_progress:'#f59e0b', completed:'#10b981' }[status];
            return (
              <div key={status}>
                <div style={{ fontFamily:C.mono, fontSize:9, color:sc, letterSpacing:2, marginBottom:10,
                  borderBottom:`1px solid ${sc}44`, paddingBottom:6 }}>
                  {status==='pending'?'⏳ PENDING':status==='in_progress'?'⚡ IN PROGRESS':'✓ COMPLETED'} · {col.length}
                </div>
                {col.map(t => (
                  <TaskCard key={t.id} task={t} members={members} canManage={canManage}
                    onStatusChange={updateStatus} onDelete={deleteTask} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateTaskModal members={members} onSave={createTask} onClose={() => setShowCreate(false)} isAdmin={canManage} />
      )}
    </div>
  );
}

function TeamValidationsPage({ role, onNavigate, visits, userId, userName }) {
  const [feedTab,       setFeedTab]       = useState("portals");
  const [viewPost,      setViewPost]      = useState(null);
  const [expandedPortal, setExpandedPortal] = useState(null);

  if (expandedPortal) {
    return <PortalDetailView portal={expandedPortal} role={role} onBack={() => setExpandedPortal(null)} />;
  }

  const fmtD = ts => {
    if (!ts?.toDate) return "";
    return ts.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short" });
  };

  return (
    <div className="smc-page-container" style={{ maxWidth:1200, margin:"0 auto", padding:"0 20px 60px" }}>
      {/* Header */}
      <div style={{ padding:"48px 0 32px", textAlign:"center" }}>
        <div style={{ fontFamily:C2.mono, fontSize:9, color:"#a855f755", letterSpacing:6, marginBottom:12 }}>SPACEMANCRAFT</div>
        <h2 style={{ fontSize:32, fontWeight:800, margin:0, lineHeight:1.15,
          background:"linear-gradient(135deg, #6366f1 0%, #10b981 50%, #f59e0b 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>TEAM VALIDATIONS</h2>
        <p style={{ fontFamily:C2.mono, fontSize:10, color:C2.dim, marginTop:12, letterSpacing:2 }}>
          MISSION PLANNING · EMBEDDED SYSTEMS · SYSTEMS ENGINEERING
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:24, background:"#070a0f", padding:4, width:"fit-content" }}>
        {[["portals","⚙ TEAM PORTALS"],["tasks","📋 TASKS STATUS"]].map(([v,l]) => (
          <button key={v} onClick={() => setFeedTab(v)}
            style={{ padding:"8px 18px", fontFamily:C2.mono, fontSize:9, cursor:"pointer", letterSpacing:1,
              background: feedTab===v ? "#a855f720" : "transparent",
              border:`1px solid ${feedTab===v ? "#a855f755" : "transparent"}`,
              color: feedTab===v ? "#a855f7" : C2.dim }}>{l}</button>
        ))}
      </div>

      {feedTab === "portals" && (
        <DepartmentsDashboard role={role} onNavigate={onNavigate}
          userId={userId} userName={userName} />
      )}

      {feedTab === "tasks" && <TasksPage role={role} />}

      {/* Cross-portal quick viewer from portal cards */}
      {viewPost && (
        <div style={{ position:"fixed", inset:0, background:"#000000cc", zIndex:500,
          display:"flex", justifyContent:"center", paddingTop:NAV_H }}>
          <div style={{ background:C2.panel, width:"100%", maxWidth:860, display:"flex", flexDirection:"column" }}>
            <div style={{ background:"#07090f", borderBottom:`1px solid ${C2.border}`, padding:"10px 20px",
              display:"flex", alignItems:"center", gap:10, flexShrink:0, flexWrap:"wrap" }}>
              <button onClick={() => setViewPost(null)} style={{ background:"none", border:"none",
                color:C2.bright, cursor:"pointer", fontFamily:"monospace", fontSize:11, letterSpacing:1 }}>← BACK</button>
              <span style={{ fontFamily:"monospace", fontSize:9, color:viewPost._portal.color,
                background:`${viewPost._portal.color}15`, border:`1px solid ${viewPost._portal.color}33`,
                padding:"2px 10px" }}>{viewPost._portal.icon} {viewPost._portal.label}</span>
              {viewPost.adminApproved && (
                <span style={{ fontFamily:"monospace", fontSize:9, color:C2.green,
                  background:`${C2.green}18`, border:`1px solid ${C2.green}55`, padding:"2px 10px" }}>✓ APPROVED</span>
              )}
              <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                <button onClick={async e => {
                    e.stopPropagation();
                    if (viewPost.adminApproved) await revokeApproval(viewPost);
                    else await approvePost(viewPost);
                    setViewPost(p => ({ ...p, adminApproved: !p.adminApproved }));
                  }}
                  style={{ padding:"5px 12px", fontFamily:"monospace", fontSize:9, cursor:"pointer",
                    background: viewPost.adminApproved ? `${C2.green}20` : "transparent",
                    border:`1px solid ${viewPost.adminApproved ? C2.green : C2.border}`,
                    color: viewPost.adminApproved ? C2.green : C2.dim }}>
                  {viewPost.adminApproved ? "✓ APPROVED" : "✓ APPROVE"}
                </button>
                <button onClick={async e => {
                    e.stopPropagation();
                    if (!window.confirm(`Remove "${viewPost.title}"?`)) return;
                    await removePost(viewPost);
                    setViewPost(null);
                  }}
                  style={{ padding:"5px 12px", fontFamily:"monospace", fontSize:9, cursor:"pointer",
                    background:`${C2.red}12`, border:`1px solid ${C2.red}`, color:C2.red }}>
                  🗑 REMOVE
                </button>
                <a href={viewPost._portal.path} target="_blank" rel="noreferrer"
                  style={{ fontFamily:"monospace", fontSize:9, color:C2.dim, textDecoration:"none",
                    border:`1px solid ${C2.border}`, padding:"5px 12px" }}>
                  OPEN PORTAL ↗
                </a>
              </div>
            </div>
            <div style={{ flex:1, overflowY:"auto", background:"#e8eaf0", padding:"20px 12px" }}>
              <div style={{ background:"#fff", color:"#111", padding:"36px 48px",
                fontFamily:"Times New Roman,serif", fontSize:"11pt", lineHeight:1.65, maxWidth:820, margin:"0 auto" }}>
                <div style={{ borderBottom:`2.5px solid ${viewPost._portal.color}`, paddingBottom:12, marginBottom:24 }}>
                  <div style={{ fontFamily:"Arial,sans-serif", fontSize:"8pt", color:"#999", letterSpacing:2, textTransform:"uppercase" }}>Spacemancraft Pvt Ltd</div>
                  <div style={{ fontFamily:"Arial,sans-serif", fontSize:"9pt", color:"#888" }}>{viewPost._portal.icon} {viewPost._portal.label} Report</div>
                  <div style={{ fontFamily:"Arial,sans-serif", fontSize:"19pt", fontWeight:700, color:viewPost._portal.color, lineHeight:1.25, margin:"6px 0" }}>{viewPost.title}</div>
                  <div style={{ fontFamily:"Arial,sans-serif", fontSize:"9pt", color:"#666" }}>By {viewPost.uploadedBy} · {fmtD(viewPost.uploadedAt)}</div>
                  {viewPost.adminApproved && (
                    <div style={{ fontFamily:"Arial,sans-serif", fontSize:"9pt", color:"#10b981", marginTop:4,
                      background:"#f0fdf4", border:"1px solid #bbf7d0", padding:"4px 10px", display:"inline-block" }}>
                      ✓ Validated by Admin
                    </div>
                  )}
                </div>
                {(viewPost.sections||[]).map((sec, i) => {
                  if (sec.type==="heading") return <div key={i} style={{ fontFamily:"Arial,sans-serif", fontWeight:700, fontSize:"12pt", color:viewPost._portal.color, margin:"14px 0 5px" }}>{sec.text}</div>;
                  if (sec.type==="paragraph") return <p key={i} style={{ margin:"0 0 10px", textAlign:"justify", lineHeight:1.7 }}>{sec.text}</p>;
                  if (sec.type==="bullets") return <ul key={i} style={{ margin:"6px 0 12px", paddingLeft:26 }}>{(sec.items||[]).map((it,j) => <li key={j}>{it}</li>)}</ul>;
                  if (sec.type==="code") return <pre key={i} style={{ background:"#f5f5f5", border:"1px solid #ddd", padding:"10px", margin:"8px 0", fontFamily:"monospace", fontSize:"9pt", overflowX:"auto", whiteSpace:"pre-wrap" }}>{sec.text}</pre>;
                  if ((sec.type==="graph"||sec.type==="image") && sec.url) {
                    if (/\.(png|jpg|jpeg|gif|svg|webp)$/i.test(sec.name||"")) return <div key={i} style={{ margin:"16px 0", textAlign:"center" }}><img src={sec.url} alt={sec.caption||""} style={{ maxWidth:"100%", border:"1px solid #eee" }} />{sec.caption && <div style={{ fontFamily:"Arial,sans-serif", fontSize:"10pt", fontWeight:700, margin:"6px 0" }}>{sec.caption}</div>}</div>;
                    if (/\.pdf$/i.test(sec.name||"")) return <div key={i} style={{ margin:"16px 0", textAlign:"center" }}>{sec.thumbnailUrl ? <img src={sec.thumbnailUrl} alt={sec.caption||"PDF"} style={{ maxWidth:"100%", border:"1px solid #eee" }} /> : null}<div style={{ marginTop:6 }}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:viewPost._portal.color }}>📄 Open PDF →</a></div>{sec.caption && <div style={{ fontFamily:"Arial,sans-serif", fontSize:"10pt", fontWeight:700, margin:"6px 0" }}>{sec.caption}</div>}</div>;
                  }
                  if (sec.type==="attachment" && sec.url) return <div key={i} style={{ margin:"4px 0" }}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:viewPost._portal.color }}>📎 {sec.name}</a></div>;
                  return null;
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Live mini-card for one team portal on the home page ──────────────────────
// ── Celestial Ledger palette (shared with DepartmentsDashboard) ──────────────
const CL = {
  bg:"#0c112e", surfLow:"#141936", surf:"#191d3b",
  surfLowest:"#070b28", surfHigh:"#232846", surfHighest:"#2e3351",
  primary:"#89ceff", onPriCont:"#0085be",
  tertiary:"#4cd6ff", tertiCont:"#00141b", onTertiCont:"#0088a7",
  secondary:"#c0c4ea", secCont:"#424767", onSecCont:"#b2b6db",
  error:"#ffb4ab", errCont:"#93000a",
  onSurf:"#dee0ff", onSurfVar:"#c7c5ce", outlineVar:"#46464d",
  H:"'Space Grotesk', system-ui, sans-serif",
  B:"'Inter', system-ui, sans-serif",
};
const CL_GRAD = `linear-gradient(135deg, ${CL.primary} 0%, ${CL.onPriCont} 100%)`;

// Map portal id → Material Symbol icon name
const PORTAL_ICON_MAP = {
  obc:      "developer_board",
  mp:       "rocket_launch",
  embedded: "electrical_services",
  system:   "engineering",
  ops:      "corporate_fare",
  pe:       "psychology",
};

function TeamPortalMiniCard({ portal }) {
  const [count,  setCount]  = useState(null);
  const [latest, setLatest] = useState(null);
  const [hov,    setHov]    = useState(false);

  useEffect(() => {
    if (portal.noFeed || !portal.collection) return;
    const q = query(collection(db, portal.collection), orderBy("uploadedAt", "desc"));
    return onSnapshot(q, snap => {
      setCount(snap.size);
      if (snap.size > 0) setLatest(snap.docs[0].data().title || "Untitled");
    }, () => {});
  }, [portal.collection, portal.noFeed]);

  // Status derived from report count
  const hasReports = count !== null && count > 0;
  const status = portal.noFeed ? "DASHBOARD"
    : count === null ? "LOADING"
    : count === 0 ? "INACTIVE"
    : count < 5 ? "BUSY"
    : "ACTIVE";

  const STATUS_CFG = {
    ACTIVE:    { bg:CL.tertiCont,  text:CL.onTertiCont },
    BUSY:      { bg:CL.secCont,    text:CL.onSecCont },
    INACTIVE:  { bg:CL.surfHighest,text:CL.onSurfVar },
    DASHBOARD: { bg:CL.surfHighest,text:CL.primary },
    LOADING:   { bg:CL.surfHighest,text:CL.onSurfVar },
  };
  const sc = STATUS_CFG[status] || STATUS_CFG.INACTIVE;
  const msIcon = PORTAL_ICON_MAP[portal.id] || "hub";
  const pct = count ? Math.min(100, Math.round((count / 20) * 100)) : 0;

  return (
    <Link to={portal.path}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ textDecoration:"none", display:"block",
        background: hov ? CL.surf : CL.surfLow,
        borderRadius:12, padding:"24px",
        borderBottom:`1px solid ${CL.outlineVar}0d`,
        borderRight:`1px solid ${CL.outlineVar}0d`,
        boxShadow: hov ? `0 8px 32px rgba(0,180,255,0.10)` : `0 2px 8px rgba(0,0,0,0.20)`,
        transition:"all 0.2s", transform: hov ? "translateY(-2px)" : "none",
        cursor:"pointer" }}>

      {/* Header row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ padding:"10px", borderRadius:10,
            background:`${portal.color}18`, color:portal.color,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span className="material-symbols-outlined" style={{ fontSize:28, fontVariationSettings:"'FILL' 0,'wght' 400" }}>
              {msIcon}
            </span>
          </div>
          <div>
            <div style={{ fontFamily:CL.H, fontWeight:700, fontSize:17, color:CL.onSurf, lineHeight:1.2 }}>
              {portal.label}
            </div>
            <div style={{ fontFamily:CL.B, fontSize:10, color:CL.onSurfVar, letterSpacing:"0.08em",
              textTransform:"uppercase", marginTop:3 }}>
              {portal.id.toUpperCase()} · Portal
            </div>
          </div>
        </div>
        <span style={{ fontFamily:CL.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
          textTransform:"uppercase", padding:"4px 10px", borderRadius:20,
          background:sc.bg, color:sc.text }}>
          {status}
        </span>
      </div>

      {/* Stats row */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        <div style={{ background:CL.surfLowest, borderRadius:8, padding:"10px 12px" }}>
          <div style={{ fontFamily:CL.B, fontSize:9, fontWeight:700, color:"#64748b",
            letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
            Reports
          </div>
          <div style={{ fontFamily:CL.H, fontSize:22, fontWeight:700, color:CL.primary, lineHeight:1 }}>
            {count ?? "—"}
          </div>
        </div>
        <div style={{ background:CL.surfLowest, borderRadius:8, padding:"10px 12px" }}>
          <div style={{ fontFamily:CL.B, fontSize:9, fontWeight:700, color:"#64748b",
            letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
            Status
          </div>
          <div style={{ fontFamily:CL.H, fontSize:22, fontWeight:700, color:CL.tertiary, lineHeight:1 }}>
            {hasReports ? "Live" : "—"}
          </div>
        </div>
      </div>

      {/* Latest report */}
      {latest && (
        <div style={{ fontFamily:CL.B, fontSize:11, color:CL.onSurfVar, marginBottom:12,
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          Latest: {latest}
        </div>
      )}
      {!latest && portal.desc && (
        <div style={{ fontFamily:CL.B, fontSize:11, color:CL.onSurfVar, marginBottom:12,
          lineHeight:1.5, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical",
          overflow:"hidden" }}>
          {portal.desc}
        </div>
      )}

      {/* Progress bar */}
      <div style={{ marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
          <span style={{ fontFamily:CL.B, fontSize:9, fontWeight:700, color:"#64748b",
            letterSpacing:"0.1em", textTransform:"uppercase" }}>ACTIVITY</span>
          <span style={{ fontFamily:CL.H, fontSize:12, fontWeight:700, color:CL.primary }}>{pct}%</span>
        </div>
        <div style={{ height:5, borderRadius:3, background:CL.surfHighest, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${pct}%`, background:CL_GRAD,
            borderRadius:3, transition:"width 0.6s ease" }} />
        </div>
      </div>

      {/* CTA */}
      <div style={{ fontFamily:CL.B, fontSize:10, fontWeight:700, letterSpacing:"0.08em",
        color:`${portal.color}99`, textTransform:"uppercase" }}>
        {portal.noFeed ? 'OPEN DASHBOARD →' : 'OPEN PORTAL →'}
      </div>
    </Link>
  );
}

function TeamPortalsStrip() {
  const cfg = useAppConfig();
  const visiblePortals = PORTAL_CARDS.filter(p =>
    p.id !== 'finance' || cfg.features.financePortalEnabled !== false
  );
  return (
    <div style={{ marginBottom:48 }}>
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:24 }}>
        <div>
          <div style={{ fontFamily:CL.B, fontSize:10, fontWeight:700, letterSpacing:"0.15em",
            textTransform:"uppercase", color:CL.tertiary, marginBottom:6 }}>
            Fleet Management
          </div>
          <h2 style={{ fontFamily:CL.H, fontWeight:700, fontSize:32, color:CL.onSurf,
            margin:0, letterSpacing:"-0.02em" }}>
            Team Portals
          </h2>
        </div>
        <div style={{ fontFamily:CL.B, fontSize:9, fontWeight:700, color:CL.onSurfVar,
          letterSpacing:"0.08em", textTransform:"uppercase", opacity:0.6 }}>
          DOCUMENTATION & REPORTS
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
        {visiblePortals.map(p => <TeamPortalMiniCard key={p.id} portal={p} />)}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  HOME PAGE
// ═══════════════════════════════════════════════════════════
function HomePage({ onNavigate, visits, role }) {
  return (
    <div id="home-page" className="smc-page-container" style={{ maxWidth:1100, margin:"0 auto", padding:"0 24px 80px",
      fontFamily:CL.B }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300..700&family=Inter:wght@100..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap');
        .material-symbols-outlined { font-family:'Material Symbols Outlined'; font-style:normal;
          font-weight:normal; font-size:24px; display:inline-block; line-height:1;
          text-transform:none; letter-spacing:normal; word-wrap:normal; white-space:nowrap;
          direction:ltr; font-variation-settings:'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 24; }
        @keyframes hp-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes hp-glow { 0%,100%{opacity:0.15} 50%{opacity:0.25} }
      `}</style>

      {/* Ambient glow blobs */}
      <div style={{ position:"fixed", top:"10%", left:"5%", width:500, height:500,
        borderRadius:"50%", background:"radial-gradient(circle,rgba(137,206,255,0.06) 0%,transparent 70%)",
        pointerEvents:"none", zIndex:0, animation:"hp-glow 8s ease-in-out infinite" }} />
      <div style={{ position:"fixed", bottom:"15%", right:"5%", width:400, height:400,
        borderRadius:"50%", background:"radial-gradient(circle,rgba(76,214,255,0.05) 0%,transparent 70%)",
        pointerEvents:"none", zIndex:0, animation:"hp-glow 10s ease-in-out infinite reverse" }} />

      {/* ── Hero ───────────────────────────────────────────── */}
      <div style={{ position:"relative", textAlign:"center", padding:"72px 0 56px", zIndex:1 }}>
        <div style={{ fontFamily:CL.B, fontSize:10, fontWeight:700, letterSpacing:"0.2em",
          textTransform:"uppercase", color:CL.tertiary, marginBottom:16, opacity:0.8 }}>
          EP-ADCS · Technical Analysis Suite
        </div>
        <h1 style={{ fontFamily:CL.H, fontWeight:800, fontSize:"clamp(40px,8vw,80px)",
          margin:"0 0 8px", lineHeight:1, letterSpacing:"-0.03em",
          background:CL_GRAD, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
          SPACEMANCRAFT
        </h1>
        <div style={{ height:2, background:`linear-gradient(90deg,transparent,${CL.primary},transparent)`,
          maxWidth:500, margin:"0 auto 24px" }} />
        <p style={{ fontFamily:CL.B, fontSize:13, color:CL.onSurfVar, letterSpacing:"0.05em",
          margin:"0 0 28px" }}>
          Orbital Command Platform · Mission Control Dashboard
        </p>

        {/* KPI strip */}
        <div style={{ display:"flex", justifyContent:"center", gap:0, flexWrap:"wrap",
          background:CL.surfLow, borderRadius:12, maxWidth:640, margin:"0 auto 28px",
          boxShadow:"0 2px 16px rgba(0,0,0,0.25)", overflow:"hidden" }}>
          {[
            ["η = 1.020","Overshoot Factor",CL.primary],
            ["48–58%","Propellant Saving",CL.tertiary],
            ["< 5 μrad","Attitude Jitter",CL.secondary],
            ["4 mW","Avg Power",CL.primary],
          ].map(([v,l,col],i) => (
            <div key={l} style={{ flex:"1 1 120px", padding:"16px 20px", textAlign:"center",
              borderRight: i < 3 ? `1px solid ${CL.outlineVar}33` : "none" }}>
              <div style={{ fontFamily:CL.H, fontSize:20, fontWeight:700, color:col, lineHeight:1.1 }}>{v}</div>
              <div style={{ fontFamily:CL.B, fontSize:9, fontWeight:700, color:"#64748b",
                letterSpacing:"0.1em", textTransform:"uppercase", marginTop:4 }}>{l}</div>
            </div>
          ))}
        </div>

        {role === "mentor" && (
          <div style={{ fontFamily:CL.B, fontSize:10, fontWeight:700, color:"#f59e0b",
            letterSpacing:"0.1em", textTransform:"uppercase",
            background:"#f59e0b12", border:`1px solid #f59e0b33`, borderRadius:8,
            display:"inline-block", padding:"6px 20px", marginBottom:8 }}>
            Mentor Access — Radial Apogee Manoeuvre demo unlocked
          </div>
        )}
        {(role === "admin" || role === "ops") && <AdminBell />}
      </div>

      {/* ── Search ─────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:1 }}>
        <SearchBar onNavigate={onNavigate} />
      </div>

      {/* ── Team portals ───────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:1 }}>
        <TeamPortalsStrip />
      </div>

      {/* ── Mission feed ───────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:1 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <div style={{ fontFamily:CL.B, fontSize:10, fontWeight:700, letterSpacing:"0.15em",
            textTransform:"uppercase", color:CL.tertiary }}>Mission Feed</div>
          <div style={{ flex:1, height:1,
            background:`linear-gradient(to right,${CL.primary}44,transparent)` }} />
        </div>
        <SocialFeed role={role} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  PAGE SHELL
// ═══════════════════════════════════════════════════════════
function PageShell({ page, onBack, visits, userName, role }) {
  const C = useTheme();
  const Component = page.component;
  // Embedded engineer: render full-screen (page manages its own layout)
  if (role === "embedded") {
    return <Component userName={userName} />;
  }
  return (
    <div className="smc-page-container-sm" style={{ maxWidth:860, margin:"0 auto", padding:"0 20px 60px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:16,
        padding:"24px 0 28px", borderBottom:`1px solid ${C.border}`, marginBottom:24 }}>
        <button onClick={onBack} style={{ padding:"6px 14px", ...mono, fontSize:10,
          cursor:"pointer", background:"transparent", border:`1px solid ${C.border}`,
          color:C.muted, borderRadius:4, letterSpacing:1 }}>← HOME</button>
        <div style={{ width:1, height:28, background:C.border }} />
        <div style={{ fontSize:9, color:page.color, ...mono, letterSpacing:3 }}>§ {page.section}</div>
        <div style={{ fontSize:16, fontWeight:700, color:C.bright, flex:1 }}>{page.title}</div>
        <span style={{ ...mono, fontSize:9, color:page.color,
          background:`${page.color}18`, border:`1px solid ${page.color}44`,
          padding:"3px 10px", borderRadius:10 }}>
          {visits} {visits === 1 ? "visit" : "visits"}
        </span>
      </div>
      <Component userName={userName} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  DEPARTMENT VIEW — shown to portal team members (mp, embedded, system, …)
//  Auto-scales: any role matching a PORTAL_CARD entry gets this view.
//  New departments only need a PORTAL_CARDS entry — no other code change.
// ═══════════════════════════════════════════════════════════

// ── Post feed for the member's own department ─────────────────────────────────
function DeptFeed({ portal, userId, userName, readOnly = false }) {
  const C = useTheme(); // ← theme-aware: responds to theme changes
  const [allPosts, setAllPosts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [viewPost, setViewPost] = useState(null);
  const [filter,   setFilter]   = useState(readOnly ? "all" : "mine");
  const [modalTab, setModalTab] = useState("report");
  const [threads,  setThreads]  = useState([]);
  const [comments, setComments] = useState([]);
  const [comment,  setComment]  = useState('');
  const [view,     setView]     = useReportView();
  const [leadOf,   setLeadOf]   = useState([]);
  const [reviewing, setReviewing] = useState(false);

  // Team lead of this department? (authorized_users.leadOf)
  const leadKey = portal.deptId || portal.id;
  useEffect(() => {
    if (!userId || readOnly) return undefined;
    return onSnapshot(doc(db, 'authorized_users', userId), snap => setLeadOf(snap.data()?.leadOf || []), () => {});
  }, [userId, readOnly]);
  const isLead = !readOnly && leadOf.includes(leadKey);

  async function leadUpdate(post, patch) {
    setReviewing(true);
    try {
      await updateDoc(doc(db, portal.collection, post.id), {
        ...patch, reviewedBy: userName || 'Team lead', reviewedByUid: userId, reviewedAt: serverTimestamp(),
      });
      setViewPost(p => (p && p.id === post.id ? { ...p, ...patch } : p));
    } catch (e) { alert(`Could not update report: ${e.message}`); }
    setReviewing(false);
  }
  const toggleApprove = post => leadUpdate(post, post.adminLiked
    ? { adminLiked: false, adminLikedAt: null, likeCount: Math.max(0, (post.likeCount || 1) - 1) }
    : { adminLiked: true, adminLikedAt: serverTimestamp(), likeCount: (post.likeCount || 0) + 1, reportStatus: post.reportStatus || 'completed' });
  const togglePin = post => leadUpdate(post, (post.pinned || post.isPinned)
    ? { pinned: false, isPinned: false, pinnedAt: null, pinnedBy: null, pinnedByUid: null }
    : { pinned: true, isPinned: true, pinnedAt: serverTimestamp(), pinnedBy: userName || 'Team lead', pinnedByUid: userId });

  const MONO = "'Share Tech Mono',monospace";
  const SANS = "'Inter',system-ui,sans-serif";

  useEffect(() => {
    if (!portal.collection) { setLoading(false); return; }
    // dept_posts is shared by every admin-created department — scope by deptId
    const q = portal.deptId
      ? query(collection(db, portal.collection), where('deptId', '==', portal.deptId), orderBy('uploadedAt', 'desc'))
      : query(collection(db, portal.collection), orderBy('uploadedAt', 'desc'));
    return onSnapshot(q,
      snap => { setAllPosts(snap.docs.map(d => ({ id: d.id, _portal: portal, ...d.data() }))); setLoading(false); },
      ()   => setLoading(false)
    );
  }, [portal.collection, portal.deptId]); // eslint-disable-line

  // Load threads whenever a post is opened
  useEffect(() => {
    if (!viewPost || !portal.collection) { setThreads([]); return; }
    const q = portal.deptId
      ? query(collection(db, portal.collection), where('deptId', '==', portal.deptId),
          where('parentId', '==', viewPost.id), orderBy('uploadedAt', 'asc'))
      : query(collection(db, portal.collection), where('parentId', '==', viewPost.id), orderBy('uploadedAt', 'asc'));
    return onSnapshot(q, snap => setThreads(snap.docs.map(d => ({ id:d.id, ...d.data() }))), () => {});
  }, [viewPost?.id, portal.collection]); // eslint-disable-line

  // Comments on the open report (same <collection>/<id>/comments the portals use)
  useEffect(() => {
    if (!viewPost || !portal.collection) { setComments([]); return; }
    return onSnapshot(query(collection(db, portal.collection, viewPost.id, 'comments'), orderBy('createdAt', 'asc')),
      snap => setComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => {});
  }, [viewPost?.id, portal.collection]); // eslint-disable-line

  async function postComment() {
    const text = comment.trim();
    if (!text || !viewPost) return;
    await addDoc(collection(db, portal.collection, viewPost.id, 'comments'), {
      text, by: userName || 'Team', byUid: userId || null, createdAt: serverTimestamp(),
    });
    setComment('');
  }

  function openPost(post) {
    setViewPost(post);
    setModalTab("report");
  }

  function relTime(ts) {
    if (!ts?.toDate) return "";
    const d = (Date.now() - ts.toDate().getTime()) / 1000;
    if (d < 60)     return "just now";
    if (d < 3600)   return `${Math.floor(d/60)}m ago`;
    if (d < 86400)  return `${Math.floor(d/3600)}h ago`;
    if (d < 604800) return `${Math.floor(d/86400)}d ago`;
    return ts.toDate().toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
  }

  const visible = allPosts.filter(post => {
    if (post.parentId) return false; // thread posts belong inside their parent
    if (!canSeePost(post, userId, false)) return false;
    if (filter === "all") return true;
    if (filter === "review") return !post.adminLiked;
    const isOwn = post.uploadedBy === userName || post.uploadedById === userId;
    const isCollab = (post.sharedWith || []).includes(userId) ||
      (post.linkedTo || []).some(l => l.memberKey === userId || l.uid === userId);
    return isOwn || isCollab;
  });

  // Workbench: reports pinned as the team's current work
  const workbench = allPosts.filter(p => !p.parentId && (p.pinned || p.isPinned) && canSeePost(p, userId, false));

  const renderCard = post => {
    const catLabel = (post.category||'report').replace(/_/g,' ');
    const cat = { label: catLabel.toUpperCase(), color: portal.color, icon: portal.icon };
    return (
      <ReportCard
        key={post.id}
        post={post}
        cat={cat}
        accentColor={portal.color}
        isAdmin={false}
        user={{ uid: userId, name: userName, role: portal.id }}
        onOpen={openPost}
        threads={[]}
        C={C}
        postCol={portal.collection}
        portalId={portal.id}
      />
    );
  };

  // Render a single report section — handles all section types the portals produce
  function renderSection(sec, i) {
    const isPdf = /\.pdf$/i.test(sec.name||sec.url||"");
    if (sec.type === "heading") return (
      <div key={i} style={{ fontWeight:700, fontSize:17, color:portal.color, margin:"20px 0 6px", fontFamily:SANS }}>{sec.text||sec.heading}</div>
    );
    if (sec.type === "paragraph" || sec.type === "text") return (
      <p key={i} style={{ color:C.text, lineHeight:1.8, margin:"0 0 12px", fontFamily:SANS }}>{sec.content||sec.text}</p>
    );
    if (sec.type === "bullets") return (
      <ul key={i} style={{ color:C.text, lineHeight:1.8, margin:"0 0 12px", paddingLeft:24, fontFamily:SANS }}>
        {(sec.items||[]).map((it,j) => <li key={j}>{it}</li>)}
      </ul>
    );
    if (sec.type === "code") return (
      <pre key={i} style={{ background:C.panel, border:`1px solid ${C.border}`, padding:14, borderRadius:6,
        fontFamily:"monospace", fontSize:12, overflowX:"auto", margin:"8px 0", color:C.text }}>{sec.text||sec.content}</pre>
    );
    if ((sec.type === "image" || sec.type === "graph") && sec.url) {
      if (isPdf) return <div key={i} style={{ margin:"16px 0" }}><PdfInlineViewer url={sec.url} name={sec.name||"Document"} /></div>;
      return (
        <div key={i} style={{ margin:"16px 0" }}>
          <img src={sec.url} alt={sec.caption||sec.name||""} style={{ maxWidth:"100%", borderRadius:8, display:"block" }} />
          {sec.caption && <div style={{ fontFamily:SANS, fontSize:12, color:C.muted, marginTop:6 }}>{sec.caption}</div>}
        </div>
      );
    }
    if (sec.type === "cad" && sec.url) return (
      <div key={i} style={{ margin:"8px 0" }}>
        <a href={sec.url} target="_blank" rel="noreferrer" style={{ color:portal.color, fontFamily:MONO, fontSize:12 }}>⬡ CAD: {sec.name||"Download"}</a>
      </div>
    );
    if ((sec.type === "attachment" || sec.type === "pdf" || sec.type === "file") && sec.url) {
      if (isPdf) return <div key={i} style={{ margin:"16px 0" }}><PdfInlineViewer url={sec.url} name={sec.name||"Document"} /></div>;
      const isDocFile = /\.(doc|docx|odt|rtf)$/i.test(sec.name||sec.url||"");
      if (isDocFile) return <div key={i} style={{ margin:"16px 0" }}><DocInlineViewer url={sec.url} name={sec.name||"Document"} accentColor={portal.color} /></div>;
      return <div key={i} style={{ margin:"8px 0" }}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:portal.color, fontFamily:MONO, fontSize:12 }}>📎 {sec.name||"Open file"}</a></div>;
    }
    return null;
  }

  return (
    <>
      {/* Header row */}
      <div className="smc-dept-feed-header" style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ fontFamily:MONO, fontSize:11, color:portal.color, letterSpacing:3 }}>
          {portal.icon} MY REPORTS
        </div>
        <div style={{ flex:1, height:1, background:`linear-gradient(to right,${portal.color}33,transparent)` }} />
        {!readOnly && (
          <Link to={portal.path}
            style={{ fontFamily:MONO, fontSize:9, letterSpacing:2,
              padding:"7px 16px", borderRadius:6,
              background:`${portal.color}18`, border:`1px solid ${portal.color}`,
              color:portal.color, textDecoration:"none" }}>
            + NEW REPORT
          </Link>
        )}
      </div>

      {/* Filter pills */}
      <div className="smc-dept-feed-filters" style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap", alignItems:"center" }}>
        {(readOnly
          ? [["all","All Dept Reports",C.muted]]
          : [["mine","My Reports & Collabs",portal.color],["all","All Dept Reports",C.muted],
             ...(isLead ? [["review","Review Queue (Team Lead)","#f59e0b"]] : [])]
        ).map(([v,l,col]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ padding:"6px 16px", fontFamily:MONO, fontSize:9,
              cursor:"pointer", borderRadius:20,
              background: filter===v ? `${col}20` : "transparent",
              border:`1px solid ${filter===v ? col : C.border}`,
              color: filter===v ? col : C.muted, transition:"all 0.15s" }}>
            {l}
          </button>
        ))}
        <span style={{ fontFamily:MONO, fontSize:9, color:C.muted, alignSelf:"center", marginLeft:"auto" }}>
          {visible.length} report{visible.length !== 1 ? "s" : ""}
        </span>
        <ReportViewToggle view={view} onChange={setView} color={portal.color} />
      </div>

      {/* Workbench — pinned reports = what the team is working on now */}
      {workbench.length > 0 && (
        <section style={{ marginBottom:24, padding:16, borderRadius:12,
          border:`1px solid ${portal.color}44`, background:`${portal.color}0a` }} aria-label="Workbench">
          <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap", marginBottom:12 }}>
            <span style={{ fontFamily:MONO, fontSize:10, color:portal.color, letterSpacing:2 }}>WORKBENCH · {workbench.length}</span>
            <span style={{ fontFamily:SANS, fontSize:12, color:C.muted }}>Current work the team is focused on</span>
          </div>
          <div data-view={view} className="smc-card-grid" style={{ display:"grid", gridTemplateColumns:reportGridColumns(view), gap:14 }}>
            {workbench.map(renderCard)}
          </div>
        </section>
      )}

      {loading && (
        <div style={{ textAlign:"center", padding:60, fontFamily:MONO, fontSize:11, color:C.muted }}>
          LOADING REPORTS…
        </div>
      )}

      {/* Feed cards */}
      <div data-view={view} className="smc-card-grid smc-dept-feed-grid" style={{ display:"grid", gridTemplateColumns:reportGridColumns(view), gap:18 }}>
        {visible.map(renderCard)}

        {!loading && visible.length === 0 && (
          <div style={{ textAlign:"center", padding:64 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>📭</div>
            <div style={{ fontFamily:MONO, fontSize:11, color:C.muted, marginBottom:20 }}>
              {filter === "mine" ? "You haven't uploaded any reports yet." : "No reports in this department yet."}
            </div>
            <Link to={portal.path}
              style={{ fontFamily:MONO, fontSize:10, color:portal.color,
                background:`${portal.color}15`, border:`1px solid ${portal.color}`,
                padding:"10px 24px", borderRadius:6, textDecoration:"none" }}>
              + UPLOAD YOUR FIRST REPORT
            </Link>
          </div>
        )}
      </div>

      {/* Full-report modal */}
      {viewPost && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:2000,
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          overflowY:"auto", padding:`${NAV_H + 12}px 16px 32px` }}
          onClick={() => setViewPost(null)}>
          <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:16,
            width:"100%", maxWidth:860, position:"relative", overflow:"hidden" }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`,
              display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <span style={{ fontFamily:MONO, fontSize:9, color:portal.color, letterSpacing:2, background:`${portal.color}15`,
                border:`1px solid ${portal.color}33`, padding:"2px 8px", borderRadius:10 }}>
                {portal.icon} {portal.label.toUpperCase()}
              </span>
              <h2 style={{ color:C.bright, margin:0, fontSize:18, flex:1, minWidth:0,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{viewPost.title}</h2>
              <button onClick={() => setViewPost(null)}
                style={{ background:"transparent", border:"none", color:C.muted,
                  fontSize:22, cursor:"pointer", lineHeight:1, flexShrink:0 }}>✕</button>
            </div>

            {/* Team lead review bar */}
            {isLead && (
              <div style={{ padding:"10px 20px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:8,
                alignItems:"center", flexWrap:"wrap", background:"#f59e0b0d" }}>
                <span style={{ fontFamily:MONO, fontSize:9, color:"#f59e0b", letterSpacing:2 }}>TEAM LEAD REVIEW</span>
                <button type="button" disabled={reviewing} onClick={() => toggleApprove(viewPost)}
                  style={{ padding:"5px 12px", fontFamily:MONO, fontSize:9, borderRadius:6, cursor:"pointer",
                    background: viewPost.adminLiked ? "transparent" : "#10b98118", border:"1px solid #10b981", color:"#10b981" }}>
                  {viewPost.adminLiked ? "★ REMOVE APPROVAL" : "★ APPROVE"}
                </button>
                {[["processing","PROCESSING"],["completed","COMPLETED"]].map(([v,l]) => (
                  <button key={v} type="button" disabled={reviewing} onClick={() => leadUpdate(viewPost, { reportStatus: v })}
                    style={{ padding:"5px 12px", fontFamily:MONO, fontSize:9, borderRadius:6, cursor:"pointer",
                      background: viewPost.reportStatus === v ? `${portal.color}22` : "transparent",
                      border:`1px solid ${viewPost.reportStatus === v ? portal.color : C.border}`,
                      color: viewPost.reportStatus === v ? portal.color : C.muted }}>{l}</button>
                ))}
                <button type="button" disabled={reviewing} onClick={() => togglePin(viewPost)}
                  style={{ padding:"5px 12px", fontFamily:MONO, fontSize:9, borderRadius:6, cursor:"pointer",
                    background:"transparent", border:`1px solid ${C.border}`, color:C.muted, marginLeft:"auto" }}>
                  {(viewPost.pinned || viewPost.isPinned) ? "REMOVE FROM WORKBENCH" : "📌 PIN TO WORKBENCH"}
                </button>
              </div>
            )}

            {/* Tabs */}
            <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, background:`${portal.color}06` }}>
              {[
                ["report","REPORT"],
                ["thread", `THREAD (${threads.length})`],
                ["comments", `COMMENTS (${comments.length})`],
              ].map(([v,l]) => (
                <button key={v} onClick={() => setModalTab(v)}
                  style={{ padding:"10px 20px", fontFamily:MONO, fontSize:9, letterSpacing:1,
                    cursor:"pointer", background:"transparent", border:"none",
                    borderBottom:`2px solid ${modalTab===v ? portal.color : "transparent"}`,
                    color: modalTab===v ? portal.color : C.muted }}>
                  {l}
                </button>
              ))}
            </div>

            {/* REPORT tab */}
            {modalTab === "report" && (
              <div style={{ padding:32, overflowY:"auto", maxHeight:"70vh" }}>
                <div style={{ fontFamily:MONO, fontSize:11, color:C.muted, marginBottom:24 }}>
                  {viewPost.uploadedBy} · {(viewPost.category||"").replace(/_/g," ")} ·{" "}
                  {viewPost.uploadedAt?.toDate?.()?.toLocaleDateString?.("en-IN",{day:"numeric",month:"short",year:"numeric"}) ?? ""}
                </div>
                {viewPost.coverUrl && (
                  <img src={viewPost.coverUrl} alt={viewPost.title}
                    style={{ width:"100%", maxHeight:300, objectFit:"cover", borderRadius:8, marginBottom:24 }} />
                )}
                {(viewPost.sections||[]).map((sec, i) => renderSection(sec, i))}
              </div>
            )}

            {/* COMMENTS tab */}
            {modalTab === "comments" && (
              <div style={{ padding:"20px 24px", overflowY:"auto", maxHeight:"70vh", display:"flex", flexDirection:"column", gap:10 }}>
                {comments.length === 0 && (
                  <div style={{ textAlign:"center", padding:"32px 0", fontFamily:MONO, fontSize:12, color:C.muted }}>No comments yet</div>
                )}
                {comments.map(c => (
                  <div key={c.id} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px" }}>
                    <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginBottom:6 }}>
                      {c.by} · {c.createdAt?.toDate ? c.createdAt.toDate().toLocaleString("en-GB",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : ""}
                    </div>
                    <div style={{ fontFamily:SANS, fontSize:13, color:C.text, lineHeight:1.55, whiteSpace:"pre-wrap" }}>{c.text}</div>
                  </div>
                ))}
                {!readOnly && (
                  <div style={{ display:"flex", gap:8, paddingTop:8 }}>
                    <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                      onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) postComment(); }}
                      placeholder="Add a comment… (Ctrl+Enter to post)" aria-label="Comment"
                      style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, color:C.text, borderRadius:8,
                        padding:"10px 12px", fontFamily:SANS, fontSize:13, resize:"vertical" }} />
                    <button type="button" onClick={postComment} disabled={!comment.trim()}
                      style={{ padding:"0 18px", fontFamily:MONO, fontSize:10, letterSpacing:1, borderRadius:8,
                        background:`${portal.color}18`, border:`1px solid ${portal.color}`, color:portal.color,
                        cursor: comment.trim() ? "pointer" : "default", opacity: comment.trim() ? 1 : 0.5 }}>POST</button>
                  </div>
                )}
              </div>
            )}

            {/* THREAD tab */}
            {modalTab === "thread" && (
              <div style={{ padding:"20px 24px", overflowY:"auto", maxHeight:"70vh" }}>
                {/* Header row */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                  <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, letterSpacing:2 }}>
                    THREAD · {threads.length} {threads.length===1?"UPDATE":"UPDATES"}
                  </div>
                  {portal.path && !readOnly && (
                    <Link to={`${portal.path}?uploadThread=${viewPost.id}`}
                      style={{ padding:"6px 14px", background:`${portal.color}18`,
                        border:`1px solid ${portal.color}`, color:portal.color,
                        fontFamily:MONO, fontSize:9, letterSpacing:1, cursor:"pointer",
                        borderRadius:4, textDecoration:"none" }}>
                      + ADD THREAD
                    </Link>
                  )}
                </div>

                {/* Thread list */}
                {threads.length === 0 ? (
                  <div style={{ textAlign:"center", padding:"48px 0",
                    fontFamily:MONO, fontSize:12, color:C.muted }}>
                    No threads yet.<br/>
                    <span style={{ fontSize:10 }}>Use "+ ADD THREAD" to upload a follow-up report, file, or update.</span>
                  </div>
                ) : threads.map((th, i) => (
                  <div key={th.id} style={{ marginBottom:14, padding:"14px 16px",
                    background:C.bg, border:`1px solid ${C.border}`,
                    borderLeft:`3px solid ${portal.color}`, borderRadius:"0 8px 8px 0" }}>
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:6 }}>
                      <span style={{ fontFamily:MONO, fontSize:9, color:C.muted, minWidth:20 }}>{i+1}.</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontFamily:MONO, fontSize:11, color:C.bright, fontWeight:700, marginBottom:2 }}>
                          {th.title}
                        </div>
                        <div style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>
                          by {th.uploadedBy} · {th.uploadedAt?.toDate ? th.uploadedAt.toDate().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : ""}
                        </div>
                      </div>
                    </div>
                    {(th.sections||[]).filter(s=>s.type==="paragraph").slice(0,1).map((s,j) => (
                      <div key={j} style={{ fontFamily:SANS, fontSize:13, color:C.text, lineHeight:1.5,
                        display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical",
                        overflow:"hidden", marginLeft:28, marginBottom:6 }}>
                        {s.text}
                      </div>
                    ))}
                    {(th.files||[]).length > 0 && (
                      <div style={{ marginLeft:28, display:"flex", gap:10, flexWrap:"wrap", marginTop:4 }}>
                        {th.files.map((f,fi) => (
                          <a key={fi} href={f.url} target="_blank" rel="noreferrer"
                            style={{ color:portal.color, fontFamily:MONO, fontSize:9,
                              textDecoration:"underline" }}>
                            📎 {f.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Tasks assigned to this member ─────────────────────────────────────────────
function MyTasks({ userId }) {
  const C = useTheme();
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'));
    return onSnapshot(q,
      snap => setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.assigneeId === userId)),
      () => {}
    );
  }, [userId]);

  // Closing goes through review (two-person rule) — see utils/taskClosure.js
  async function updateStatus(id, status) {
    await setTaskStatus(id, status);
  }

  const cols = [
    { key:'pending',     label:'PENDING',     color:'#f59e0b' },
    { key:'in_progress', label:'IN PROGRESS',  color:'#0e9cc0' },
    { key:'completed',   label:'COMPLETED',    color:'#10b981' },
  ];

  if (!tasks.length) return (
    <div style={{ textAlign:'center', padding:80 }}>
      <div style={{ fontSize:48, marginBottom:16 }}>📋</div>
      <div style={{ color:C.muted, fontFamily:'monospace', letterSpacing:2 }}>NO TASKS ASSIGNED TO YOU YET</div>
    </div>
  );

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:20 }}>
      {cols.map(col => {
        const colTasks = tasks.filter(t => boardStatus(t.status) === col.key);
        return (
          <div key={col.key}>
            <div style={{ fontFamily:'monospace', fontSize:10, letterSpacing:2, color:col.color,
              borderBottom:`2px solid ${col.color}44`, paddingBottom:8, marginBottom:14 }}>
              {col.label} ({colTasks.length})
            </div>
            {colTasks.length === 0 && (
              <div style={{ color:C.dim, fontSize:12, textAlign:'center', padding:'20px 0',
                fontFamily:'monospace' }}>— none —</div>
            )}
            {colTasks.map(task => (
              <div key={task.id} style={{ background:C.panel, border:`1px solid ${C.border}`,
                borderRadius:10, padding:16, marginBottom:10,
                borderLeft:`3px solid ${col.color}` }}>
                <div style={{ fontWeight:700, color:C.bright, fontSize:14, marginBottom:6 }}>{task.title}</div>
                <TaskReviewBadge task={task} />
                {task.description && (
                  <div style={{ fontSize:12, color:C.text, lineHeight:1.5, marginBottom:10 }}>{task.description}</div>
                )}
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                  {task.priority && (
                    <span style={{ fontSize:10, padding:'2px 8px', borderRadius:4, fontFamily:'monospace',
                      background: task.priority==='high'?'#f43f5e22': task.priority==='medium'?'#f59e0b22':'#10b98122',
                      color:      task.priority==='high'?'#f43f5e':   task.priority==='medium'?'#f59e0b':  '#10b981' }}>
                      {task.priority.toUpperCase()}
                    </span>
                  )}
                  {task.dueDate && (
                    <span style={{ fontSize:10, color:C.muted, fontFamily:'monospace' }}>📅 {task.dueDate}</span>
                  )}
                </div>
                {task.attachments?.length > 0 && (
                  <div style={{ marginBottom:8 }}>
                    {task.attachments.map((a,i) => (
                      <a key={i} href={a.url} target="_blank" rel="noreferrer"
                        style={{ display:'block', fontSize:10, color:'#0e9cc0', fontFamily:'monospace', marginBottom:2 }}>
                        📎 {a.name}
                      </a>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:6 }}>
                  {task.status !== 'in_progress' && task.status !== 'completed' && (
                    <button onClick={() => updateStatus(task.id,'in_progress')}
                      style={{ flex:1, padding:'6px 0', fontSize:9, fontFamily:'monospace', letterSpacing:1,
                        background:'#0e9cc015', border:'1px solid #0e9cc0', color:'#0e9cc0',
                        cursor:'pointer', borderRadius:4 }}>
                      START →
                    </button>
                  )}
                  {task.status !== 'completed' && (
                    <button onClick={() => updateStatus(task.id,'completed')}
                      style={{ flex:1, padding:'6px 0', fontSize:9, fontFamily:'monospace', letterSpacing:1,
                        background:'#10b98115', border:'1px solid #10b981', color:'#10b981',
                        cursor:'pointer', borderRadius:4 }}>
                      ✔ DONE
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── Sign-out menu (current device + all devices) ──────────────────────────────
function SignOutMenu({ onSignOut, onSignOutAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block', marginLeft:8 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ fontFamily:'monospace', fontSize:8, letterSpacing:1, padding:'5px 12px',
          background:'transparent', border:'1px solid #2d3748', color:'#445566',
          cursor:'pointer', borderRadius:4, display:'flex', alignItems:'center', gap:4 }}>
        SIGN OUT ▾
      </button>
      {open && (
        <div style={{ position:'absolute', right:0, top:'110%', zIndex:900, minWidth:200,
          background:'#111827', border:'1px solid #1e2d45', borderRadius:8,
          boxShadow:'0 4px 24px #00000080', overflow:'hidden' }}>
          <button type="button" onClick={() => { setOpen(false); onSignOut?.(); }}
            style={{ width:'100%', padding:'10px 16px', background:'transparent', border:'none',
              borderBottom:'1px solid #1e2d4530', color:'#c8d4e8', fontFamily:'monospace',
              fontSize:9, letterSpacing:1, cursor:'pointer', textAlign:'left' }}
            onMouseEnter={e => e.currentTarget.style.background='#1e2d4530'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            SIGN OUT THIS DEVICE
          </button>
          <button type="button" onClick={() => { setOpen(false); onSignOutAll?.(); }}
            style={{ width:'100%', padding:'10px 16px', background:'transparent', border:'none',
              color:'#f43f5e', fontFamily:'monospace', fontSize:9, letterSpacing:1,
              cursor:'pointer', textAlign:'left' }}
            onMouseEnter={e => e.currentTarget.style.background='#f43f5e18'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            🚫 SIGN OUT ALL DEVICES
          </button>
        </div>
      )}
    </div>
  );
}

// ── Department shell — wraps DeptFeed + MyTasks for a portal role ─────────────
function hasDataRoomNavAccess(role, userId, allowedUids = []) {
  return role === 'admin' || (!!userId && allowedUids.includes(userId));
}

// Full-screen "which portal?" picker for members of 2+ portals (shown on entry
// and whenever they hit SWITCH PORTAL → ALL PORTALS).
function PortalChooser({ portals, onChoose, userName, themeId, onThemeChange, onSignOut, onSignOutAll }) {
  const C = useTheme();
    return (
      <div className="smc-dept-portal" style={{ minHeight:'100vh', background:C.bg, color:C.text, fontFamily:C.font }}>
        <Stars visible={C.dark} />
        <div className="smc-portal-header" style={{ position:'sticky', top:0, zIndex:90,
          background:`${C.bg}f2`, backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)',
          borderBottom:`1px solid ${C.border}`, padding:'0 20px',
          display:'flex', alignItems:'center', gap:10, minHeight:52 }}>
          <img src={C.dark ? '/logo-dark.png' : '/logo-horizontal.jpg'} alt="Spacemancraft"
            style={{ height:C.dark?36:28, width:'auto', objectFit:'contain', flexShrink:0,
              borderRadius:C.dark?6:0 }} />
          <div style={{ flex:1 }} />
          <ThemePicker themeId={themeId} onChange={onThemeChange} inline />
          <span className="smc-user-name" style={{ fontFamily:'monospace', fontSize:10, color:C.muted, marginLeft:4 }}>
            👤 {userName}
          </span>
          <SignOutMenu onSignOut={onSignOut} onSignOutAll={onSignOutAll} />
        </div>

        <div style={{ maxWidth:820, margin:'0 auto', padding:'56px 16px 80px', position:'relative', zIndex:1 }}>
          <div style={{ fontFamily:'monospace', fontSize:10, color:C.muted, letterSpacing:3, textAlign:'center', marginBottom:10 }}>
            SELECT PORTAL
          </div>
          <h1 style={{ fontSize:24, fontWeight:700, textAlign:'center', margin:'0 0 8px', color:C.text }}>
            Which department do you want to enter?
          </h1>
          <p style={{ fontSize:13, color:C.muted, textAlign:'center', margin:'0 0 36px' }}>
            You can switch at any time from the portal header.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14 }}>
            {portals.map(pt => (
              <button key={pt.id} type="button" onClick={() => onChoose(pt.id)}
                style={{ textAlign:'left', cursor:'pointer', padding:'20px 18px', borderRadius:10,
                  background:`${pt.color}10`, border:`1px solid ${pt.color}55`, color:C.text,
                  display:'flex', flexDirection:'column', gap:8, fontFamily:C.font,
                  transition:'background 0.15s, border-color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = `${pt.color}22`; e.currentTarget.style.borderColor = pt.color; }}
                onMouseLeave={e => { e.currentTarget.style.background = `${pt.color}10`; e.currentTarget.style.borderColor = `${pt.color}55`; }}>
                <span style={{ fontSize:26, lineHeight:1 }}>{pt.icon}</span>
                <span style={{ fontFamily:'monospace', fontSize:12, fontWeight:700, letterSpacing:1.5, color:pt.color }}>
                  {pt.label.toUpperCase()}
                </span>
                {pt.desc && (
                  <span style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>{pt.desc}</span>
                )}
                <span style={{ fontFamily:'monospace', fontSize:9, color:pt.color, letterSpacing:1, marginTop:'auto' }}>
                  ENTER →
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
}

// Header dropdown for switching portals from inside any portal view.
function PortalSwitcher({ portals, activeId, onChoose }) {
  const C = useTheme();
  const [open, setOpen] = useState(false);
  const active = portals.find(p => p.id === activeId) || portals[0];
  if (!portals || portals.length < 2) return null;
  return (
    <div style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} title="Switch portal" aria-expanded={open}
        style={{ fontFamily:'monospace', fontSize:9, letterSpacing:1, cursor:'pointer', whiteSpace:'nowrap',
          padding:'5px 10px', borderRadius:4, background:`${active.color}12`,
          border:`1px solid ${active.color}66`, color:active.color }}>
        ⇄ SWITCH PORTAL ▾
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:199 }} />
          <div style={{ position:'absolute', right:0, top:'calc(100% + 6px)', zIndex:200, minWidth:220,
            background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:6,
            boxShadow:'0 12px 32px rgba(0,0,0,0.35)' }}>
            {portals.map(pt => {
              const isActive = pt.id === active.id;
              return (
                <button key={pt.id} type="button" onClick={() => { setOpen(false); if (!isActive) onChoose(pt.id); }}
                  style={{ display:'flex', alignItems:'center', gap:10, width:'100%', textAlign:'left',
                    padding:'9px 10px', borderRadius:6, cursor:'pointer', border:'none',
                    background: isActive ? `${pt.color}18` : 'transparent',
                    color: isActive ? pt.color : C.text, fontFamily:'monospace', fontSize:10, letterSpacing:1 }}>
                  <span style={{ fontSize:14 }}>{pt.icon}</span>
                  <span style={{ flex:1 }}>{pt.label.toUpperCase()}</span>
                  {isActive && <span style={{ fontSize:9 }}>●</span>}
                </button>
              );
            })}
            <div style={{ borderTop:`1px solid ${C.border}`, margin:'6px 0 4px' }} />
            <button type="button" onClick={() => { setOpen(false); onChoose(null); }}
              style={{ width:'100%', textAlign:'left', padding:'8px 10px', borderRadius:6, cursor:'pointer',
                border:'none', background:'transparent', color:C.muted,
                fontFamily:'monospace', fontSize:9, letterSpacing:1 }}>
              ▦ ALL PORTALS…
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const DEPT_TAB_IDS = ['daily-log','tasks','feed','mr1','data-room','dataroom','mail','chat','attendance','meetings','my-day','requests','interns'];

function DepartmentView({ portal, portals, activePortalId: controlledPortalId, onChoosePortal, userName, userId, role, themeId, onThemeChange, onSignOut, onSignOutAll, appVersion, appVersionDate, readOnly = false, allowProfileEdit = true, profileUserId, notifications = [], onMarkRead, onMarkOneRead }) {
  const C    = useTheme();
  const designV2 = isV2('shell');
  // Tab lives in the URL (/app/<tab>) for real sessions; read-only previews
  // inside another view keep it local so they don't move that view's URL.
  const [tab,          setTab]          = useRoutedTab('daily-log', DEPT_TAB_IDS, useState('daily-log'), !readOnly);
  const [deptMenuOpen, setDeptMenuOpen] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showQuickPost, setShowQuickPost] = useState(false);
  // Active portal is owned by MainApp (onChoosePortal) for real sessions;
  // local state covers admin previews that render DepartmentView directly.
  const [localPortalId, setLocalPortalId] = useState(portal.id);
  const [dataRoomAllowedUids, setDataRoomAllowedUids] = useState([]);

  useEffect(() => {
    if (!userId) { setDataRoomAllowedUids([]); return; }
    const unsub = onSnapshot(doc(db, 'data_room_access', 'config'), snap => {
      setDataRoomAllowedUids(snap.exists() ? (snap.data().allowedUids || []) : []);
    }, () => {});
    return unsub;
  }, [userId]);

  // Members assigned to more than one portal can switch which one is "active" —
  // everything below (feed, chat/log/attendance department tags, hero) follows
  // activePortal instead of the fixed `portal` prop.
  const allPortals   = portals && portals.length ? portals : [portal];
  const activePortal = allPortals.find(p => p.id === (controlledPortalId || localPortalId)) || portal;
  const sectionBadges = useSectionBadges({ userId, role: role || activePortal.id, activeTab: readOnly ? null : tab,
    scope: 'dept', deptId: activePortal.id, alias: { reports: 'feed' } });

  function choosePortal(pid) {
    if (onChoosePortal) onChoosePortal(pid);
    else setLocalPortalId(pid || portal.id);
    setTab(readOnly ? 'feed' : 'daily-log');
    setDeptMenuOpen(false);
  }

  const userRole = role || activePortal.id;
  const canAccessDataRoom = !readOnly && hasDataRoomNavAccess(userRole, userId, dataRoomAllowedUids);

  const deptTabs = [
    ['daily-log', '📝  MISSION LOG'],
    ['tasks', '📋  MY TASKS'],
    ['feed', '📡  REPORTS'],
    ['mr1', '🚀  MR-1'],
    ...(canAccessDataRoom ? [['data-room', '🗄  DATA ROOM']] : []),
    ...(activePortal.id === 'c_suite' ? [['dataroom', '📁  DATA ROOM']] : []),
    ['mail', '✉  MAIL'],
    ['chat', '💬  CHAT'],
    ['attendance', '📅  ATTENDANCE'],
    ['meetings', '📹  MEETINGS'],
    ['my-day', '🗓  MY DAY'],
    ...(!readOnly ? [['requests', '🙋  REQUESTS'], ['interns', '🎓  INTERNS']] : []),
  ];

  const currentTabLabel = deptTabs.find(([v]) => v === tab)?.[1]?.replace(/\s+/g, ' ').trim() || 'MENU';

  useEffect(() => {
    if (deptMenuOpen) document.body.classList.add('smc-dept-menu-open');
    else document.body.classList.remove('smc-dept-menu-open');
    return () => document.body.classList.remove('smc-dept-menu-open');
  }, [deptMenuOpen]);

  useEffect(() => {
    if (tab === 'data-room' && !canAccessDataRoom) setTab('daily-log');
  }, [tab, canAccessDataRoom]);

  const handleIncomingCall = useCallback((call) => setIncomingCall(call), []);
  useCallListener(userId, handleIncomingCall);

  function navigateTab(nextTab) {
    setTab(nextTab);
    setDeptMenuOpen(false);
  }

  function selectDeptTab(nextTab) {
    setTab(nextTab);
    setDeptMenuOpen(false);
  }

  function renderDeptTabButton([v, l], { drawer = false } = {}) {
    const active = tab === v;
    return (
      <button
        key={v}
        type="button"
        className={drawer ? 'smc-dept-nav-drawer-item' : undefined}
        onClick={() => selectDeptTab(v)}
        style={{
          padding: drawer ? '12px 14px' : '13px 28px',
          fontFamily: 'monospace',
          fontSize: drawer ? 10 : 10,
          letterSpacing: 1,
          cursor: 'pointer',
          background: drawer ? (active ? `${activePortal.color}18` : 'transparent') : 'transparent',
          border: drawer ? `0.5px solid ${active ? activePortal.color : C.border}` : 'none',
          borderRadius: drawer ? 8 : 0,
          borderBottom: drawer ? undefined : `2px solid ${active ? activePortal.color : 'transparent'}`,
          color: active ? activePortal.color : C.muted,
          transition: 'color 0.15s, background 0.15s, border-color 0.15s',
          textAlign: drawer ? 'left' : 'center',
          width: drawer ? '100%' : undefined,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {l}
      </button>
    );
  }

  return (
    <div className="smc-dept-portal" style={{ minHeight:'100vh', background:C.bg, color:C.text, fontFamily:C.font }}>
      <Stars visible={C.dark} />

      {/* Incoming call overlay */}
      {incomingCall && (
        <IncomingCallOverlay
          call={incomingCall}
          onDecline={async () => {
            try { await updateDoc(doc(db,'calls',incomingCall.id), { status:'declined' }); } catch(_) {}
            setIncomingCall(null);
          }}
          onAccept={() => { setTab('meetings'); setIncomingCall(null); }}
        />
      )}
      <TaskReminderService userId={userId} role={activePortal.id} />

      {/* Sticky header */}
      {designV2 ? (
        <ShellHeaderV2
          label={`${activePortal.icon || ''} ${activePortal.label.toUpperCase()}`}
          accent={activePortal.color}
          tabs={activePortal.id !== 'mentor' ? deptTabs : []}
          dots={readOnly ? {} : sectionBadges}
          activeTab={tab}
          onTab={selectDeptTab}
          onLogo={() => selectDeptTab(readOnly ? 'feed' : 'daily-log')}
          chip={readOnly ? <span className="smc2-chip" style={{ color:activePortal.color }}>👁 READ-ONLY</span> : null}
          actions={<>
            {!readOnly && <PortalSwitcher portals={allPortals} activeId={activePortal.id} onChoose={choosePortal} />}
            <span className="smc2-chip smc-hide-tablet" title={`Build v${APP_VERSION}`}>v{APP_VERSION}</span>
            <NotificationBell notifications={notifications} onMarkRead={onMarkRead} onMarkOneRead={onMarkOneRead} role={activePortal.id} userId={userId} />
            <ThemeDropdown themeId={themeId} onChange={onThemeChange} />
            <span className="smc2-hud smc2-hide-sm" style={{ color:'var(--smc-tx2)', letterSpacing:'0.1em' }}>{userName}</span>
            <SignOutMenu onSignOut={onSignOut} onSignOutAll={onSignOutAll} />
          </>}
        />
      ) : (
      <div className="smc-portal-header" style={{ position:'sticky', top:0, zIndex:90,
        background:`${C.bg}f2`, backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)',
        borderBottom:`1px solid ${C.border}`, padding:'0 20px',
        display:'flex', alignItems:'center', gap:10 }}>
        <img src={C.dark ? '/logo-dark.png' : '/logo-horizontal.jpg'} alt="Spacemancraft"
          style={{ height:C.dark?36:28, width:'auto', objectFit:'contain', flexShrink:0,
            borderRadius:C.dark?6:0 }} />
        <span className="smc-portal-label" style={{ fontFamily:'monospace', fontSize:10, color:activePortal.color,
          letterSpacing:2, fontWeight:700 }}>
          {activePortal.icon} {activePortal.label.toUpperCase()}
        </span>
        <div style={{ flex:1 }} />
        <div className="smc-portal-header-end" style={{ display:'flex', alignItems:'center', gap:10 }}>
        {!readOnly && (
          <PortalSwitcher portals={allPortals} activeId={activePortal.id} onChoose={choosePortal} />
        )}
        <ThemePicker themeId={themeId} onChange={onThemeChange} inline />
        <span className="smc-hide-tablet" style={{ fontFamily:'monospace', fontSize:9, color:C.muted,
          border:`1px solid ${C.border}`, padding:'2px 7px', borderRadius:4,
          userSelect:'none' }} title={`Build v${APP_VERSION}`}>
          v{APP_VERSION}
        </span>
        <span className="smc-user-name" style={{ fontFamily:'monospace', fontSize:10, color:C.muted, marginLeft:4 }}>
          👤 {userName}
        </span>
        <NotificationBell notifications={notifications} onMarkRead={onMarkRead} onMarkOneRead={onMarkOneRead} role={activePortal.id} userId={userId} />
        <SignOutMenu onSignOut={onSignOut} onSignOutAll={onSignOutAll} />
        </div>
      </div>
      )}

      {/* Portal switcher — only shown to members assigned to more than one portal */}
      {allPortals.length > 1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, flexWrap:'wrap',
          padding:'10px 20px', borderBottom:`1px solid ${C.border}` }}>
          {allPortals.map(pt => (
            <button key={pt.id} type="button" onClick={() => choosePortal(pt.id)}
              style={{ padding:'6px 16px', borderRadius:20, cursor:'pointer',
                fontFamily:'monospace', fontSize:10, letterSpacing:1,
                background: activePortal.id === pt.id ? `${pt.color}20` : 'transparent',
                border:`1px solid ${activePortal.id === pt.id ? pt.color : C.border}`,
                color: activePortal.id === pt.id ? pt.color : C.muted }}>
              {pt.icon} {pt.label}
            </button>
          ))}
        </div>
      )}

      <AnnouncementBanner userId={userId} userRole={activePortal.id} />

      {/* Check-in strip — hidden in read-only preview */}
      {!readOnly && (
        <div className="smc-dept-checkin-wrap smc-page-container-sm" style={{ maxWidth:900, margin:'12px auto 0', padding:'0 24px' }}>
          <CheckInStrip userId={userId} userName={userName} role={activePortal.id}
            onNavigateToAttendance={() => setTab('attendance')} />
        </div>
      )}

      {/* Hero — cover image + department branding (home view only) */}
      {tab === (readOnly ? 'feed' : 'daily-log') && <DepartmentHeroCover
        db={db}
        portal={activePortal}
        theme={C}
        userId={userId}
        profileUserId={profileUserId}
        userName={userName}
        role={role}
        readOnly={readOnly}
        allowProfileEdit={allowProfileEdit}
      />}

      {/* Mentor role: show investor dashboard link as primary action */}
      {activePortal.id === 'mentor' && (
        <div style={{ maxWidth:700, margin:'0 auto 40px', padding:'0 24px', textAlign:'center' }}>
          <a href="/mentor_dashboard"
            style={{ display:'inline-flex', alignItems:'center', gap:10, textDecoration:'none',
              background:`${activePortal.color}18`, border:`2px solid ${activePortal.color}`,
              borderRadius:10, padding:'18px 40px', marginBottom:24,
              fontFamily:'monospace', fontSize:13, color:activePortal.color, letterSpacing:2 }}>
            📊 &nbsp; OPEN INVESTOR DASHBOARD
          </a>
          <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:1, marginBottom:20 }}>
            PHYSICS CALCULATORS
          </div>
          <div style={{ display:'flex', justifyContent:'center', gap:12, flexWrap:'wrap' }}>
            {[
              { id:'emssvalidation',   label:'EMMS Physics Validation',              icon:'⚛', color:'#10B981' },
              { id:'pendulumswinging', label:'Elliptical Orbit — Radial Apogee Manoeuvre', icon:'↔', color:'#00ff88' },
            ].map(p => (
              <a key={p.id} href={`/?page=${p.id}`}
                style={{ display:'inline-flex', alignItems:'center', gap:8, textDecoration:'none',
                  background:`${p.color}12`, border:`1px solid ${p.color}44`,
                  borderRadius:8, padding:'12px 20px',
                  fontFamily:'monospace', fontSize:10, color:p.color, letterSpacing:1 }}>
                {p.icon} {p.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Tabs — scrollable on desktop; mobile menu drawer (classic only — v2 tabs live in the header) */}
      {activePortal.id !== 'mentor' && !designV2 && (
        <>
          <div className="smc-dept-nav-mobile-wrap">
            <button
              type="button"
              className="smc-dept-nav-menu-btn"
              aria-expanded={deptMenuOpen}
              aria-label={deptMenuOpen ? 'Close section menu' : 'Open section menu'}
              onClick={() => setDeptMenuOpen(o => !o)}
              style={{ borderBottom: `1px solid ${C.border}`, color: C.text }}
            >
              <span style={{ fontSize: 16, lineHeight: 1, color: activePortal.color }}>{deptMenuOpen ? '✕' : '☰'}</span>
              <span className="smc-dept-nav-current-label" style={{ color: activePortal.color }}>{currentTabLabel}</span>
            </button>
            {deptMenuOpen && (
              <>
                <button
                  type="button"
                  className="smc-dept-nav-drawer-backdrop"
                  aria-label="Close section menu"
                  onClick={() => setDeptMenuOpen(false)}
                />
                <div
                  className="smc-dept-nav-drawer"
                  style={{
                    background: `${C.panel}fa`,
                    borderBottom: `1px solid ${C.border}`,
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                  }}
                >
                  <div className="smc-dept-nav-drawer-grid">
                    {deptTabs.map(item => renderDeptTabButton(item, { drawer: true }))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div
            className="smc-tab-bar smc-dept-tab-bar smc-dept-tab-bar--desktop"
            style={{ borderBottom: `1px solid ${C.border}`, marginBottom: 32 }}
          >
            {deptTabs.map(item => renderDeptTabButton(item))}
            {readOnly && (
              <span style={{ alignSelf: 'center', marginLeft: 'auto', marginRight: 24,
                fontFamily: 'monospace', fontSize: 8, color: C.muted, letterSpacing: 1,
                background: `${activePortal.color}12`, border: `1px solid ${activePortal.color}30`,
                padding: '3px 10px', borderRadius: 4, flexShrink: 0 }}>
                👁 READ-ONLY
              </span>
            )}
          </div>
        </>
      )}

      {/* Content (hidden for mentor — they use the dashboard link above) */}
      {activePortal.id !== 'mentor' && (
        <div className="smc-page-container-sm smc-dept-portal-content" style={{ maxWidth: designV2 ? 1180 : 900, margin:'0 auto', padding:'0 24px 100px', position:'relative', zIndex:1 }}>
          {tab === 'feed'       && <DeptFeed portal={activePortal} userId={userId} userName={userName} readOnly={readOnly} />}
          {tab === 'data-room'  && canAccessDataRoom && (
            <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
              <DataRoomPortal userId={userId} userName={userName} role={userRole} />
            </Suspense>
          )}
          {tab === 'dataroom'   && activePortal.id === 'c_suite' && <OpsCompanyDataRoom userName={userName} userId={userId} canUpload={false} />}
          {tab === 'tasks'      && <DailyTasksPage user={{ uid: userId, name: userName }} />}
          {tab === 'mr1'        && (
            <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
              <MR1TrackerPage userId={userId} userName={userName} role={activePortal.id} />
            </Suspense>
          )}
          {tab === 'my-day'     && (
            <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
              <MyDayPage userId={userId} userName={userName} role={activePortal.id} onNavigate={navigateTab} />
            </Suspense>
          )}
          {tab === 'daily-log'  && (
            <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
              <DailyLogPage userId={userId} userName={userName} userRole={activePortal.id} />
            </Suspense>
          )}
          {tab === 'meetings'   && <MeetingsPage user={{ uid: userId, name: userName }} />}
          {tab === 'interns'    && !readOnly && (
            <Suspense fallback={null}><InternsHub userId={userId} userName={userName} role={userRole} /></Suspense>
          )}
          {tab === 'requests'   && !readOnly && (
            <Suspense fallback={null}><MemberRequestsPage userId={userId} userName={userName} /></Suspense>
          )}
          {tab === 'mail'       && <MailboxPage  user={{ uid: userId, name: userName }} />}
          {tab === 'chat'       && <ChatPage     user={{ uid: userId, name: userName, role: activePortal.id }} />}
          {tab === 'attendance' && (
            <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
              <AttendancePage role={activePortal.id} userId={userId} userName={userName} />
            </Suspense>
          )}
        </div>
      )}

      {!readOnly && userId && (
        <DailyLogFAB userId={userId} onOpenComposer={() => setShowQuickPost(true)} />
      )}

      {showQuickPost && (
        <Suspense fallback={null}>
          <QuickPostDrawer
            userId={userId} userName={userName} userRole={activePortal.id}
            onClose={() => setShowQuickPost(false)}
            onPost={() => { setShowQuickPost(false); setTab('daily-log'); }}
          />
        </Suspense>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  OPERATIONS OFFICER VIEW (C-Suite)
// ══════════════════════════════════════════════════════════════════════════════
const OPS_COLOR = "#e11d48";
const OPS_TAB_IDS = ['overview','daily-log','reports','tasks','mytasks','mr1','my-day','calendar','meetings','company','attendance','interns','mail','chat'];
// Height (px) of the sticky top nav bar — used to offset fixed modals so they don't sit behind it
const NAV_H = 52;
const OPS_PORTALS = [
  { id:"mp",       label:"Mission Planning",    icon:"🛰", color:"#6366f1", col:"mission_planning_posts" },
  { id:"embedded", label:"Embedded Systems",    icon:"🔌", color:"#f59e0b", col:"embedded_posts"        },
  { id:"system",   label:"Systems Engineering", icon:"⚙",  color:"#10b981", col:"system_posts"          },
  { id:"ops",      label:"Operations",          icon:"🏛", color:"#e11d48", col:"operations_posts"      },
  { id:"c_suite",  label:"C-Suite",             icon:"👔", color:"#dc2626", col:"c_suite_posts"         },
  { id:"payload",  label:"Payloads",            icon:"📦", color:"#0891b2", col:"payload_posts"         },
  { id:"pe",       label:"Prompt Engineering",  icon:"🧠", color:"#8b5cf6", col:"prompt_engineering_posts" },
];

function OpsCalendar({ userName }) {
  const C = useTheme();
  const [events,   setEvents]   = useState([]);
  const [today]                 = useState(new Date());
  const [yr,       setYr]       = useState(new Date().getFullYear());
  const [mo,       setMo]       = useState(new Date().getMonth());
  const [selDay,   setSelDay]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [evTitle,  setEvTitle]  = useState("");
  const [evDesc,   setEvDesc]   = useState("");
  const [evColor,  setEvColor]  = useState(OPS_COLOR);
  const [saving,   setSaving]   = useState(false);
  const [viewEv,   setViewEv]   = useState(null);

  useEffect(() => {
    const q = query(collection(db, "calendars"), orderBy("createdAt", "desc"));
    return onSnapshot(q, snap => setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => {});
  }, []);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const firstDow  = new Date(yr, mo, 1).getDay();
  const daysInMo  = new Date(yr, mo + 1, 0).getDate();
  const todayStr  = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;

  function ds(day) { return `${yr}-${String(mo+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`; }
  function evDay(day) { return events.filter(e => e.date === ds(day)); }
  function prevMo() { if (mo === 0) { setMo(11); setYr(y => y-1); } else setMo(m => m-1); }
  function nextMo() { if (mo === 11) { setMo(0); setYr(y => y+1); } else setMo(m => m+1); }

  async function saveEvent() {
    if (!evTitle.trim() || !selDay) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "calendars"), {
        title: evTitle.trim(), description: evDesc.trim(),
        date: ds(selDay), color: evColor, createdBy: userName,
        createdAt: serverTimestamp(),
      });
      setEvTitle(""); setEvDesc(""); setEvColor(OPS_COLOR); setShowForm(false); setSelDay(null);
    } catch(e) {}
    setSaving(false);
  }

  async function deleteEvent(id) {
    if (!window.confirm("Delete this event?")) return;
    await deleteDoc(doc(db, "calendars", id));
    setViewEv(null);
  }

  const upcomingEvents = [...events]
    .filter(e => e.date >= todayStr)
    .sort((a,b) => a.date.localeCompare(b.date))
    .slice(0, 8);

  return (
    <div>
      {viewEv && (
        <div style={{ position:"fixed", inset:0, background:"#00000088", zIndex:200,
          display:"flex", alignItems:"flex-start", justifyContent:"center", padding:`${NAV_H + 24}px 24px 24px` }}
          onClick={e => e.target===e.currentTarget && setViewEv(null)}>
          <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:28, width:"100%", maxWidth:420 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ width:12, height:12, borderRadius:"50%", background:viewEv.color, flexShrink:0 }} />
              <div style={{ fontWeight:700, color:C.bright, fontSize:16, flex:1 }}>{viewEv.title}</div>
              <button onClick={() => setViewEv(null)} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:20 }}>×</button>
            </div>
            <div style={{ fontFamily:"monospace", fontSize:11, color:C.muted, marginBottom:10 }}>📅 {viewEv.date} · by {viewEv.createdBy}</div>
            {viewEv.description && <p style={{ color:C.text, fontSize:13, lineHeight:1.6, marginBottom:16 }}>{viewEv.description}</p>}
            <button onClick={() => deleteEvent(viewEv.id)}
              style={{ padding:"8px 16px", background:"#f43f5e15", border:"1px solid #f43f5e44", color:"#f43f5e",
                fontFamily:"monospace", fontSize:10, cursor:"pointer", borderRadius:6 }}>
              🗑 Delete Event
            </button>
          </div>
        </div>
      )}

      <div className="smc-ops-calendar-panel" style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button onClick={prevMo} style={{ background:"none", border:`1px solid ${C.border}`, color:C.muted,
            cursor:"pointer", borderRadius:6, padding:"4px 14px", fontFamily:"monospace", fontSize:13, minWidth:44, minHeight:44 }}>‹</button>
          <div style={{ flex:1, textAlign:"center", fontFamily:"monospace", fontSize:14, fontWeight:700, color:C.bright, letterSpacing:2 }}>
            {MONTHS[mo]} {yr}
          </div>
          <button onClick={nextMo} style={{ background:"none", border:`1px solid ${C.border}`, color:C.muted,
            cursor:"pointer", borderRadius:6, padding:"4px 14px", fontFamily:"monospace", fontSize:13, minWidth:44, minHeight:44 }}>›</button>
        </div>
        <div className="smc-ops-calendar-grid">
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
          {DAYS.map(d => <div key={d} style={{ textAlign:"center", fontFamily:"monospace", fontSize:9, color:C.muted, letterSpacing:1, padding:"4px 0" }}>{d}</div>)}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
          {Array.from({ length: firstDow }, (_,i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMo }, (_,i) => {
            const day = i + 1;
            const dayEvs = evDay(day);
            const isToday = ds(day) === todayStr;
            return (
              <div key={day} onClick={() => { setSelDay(day); setShowForm(true); }}
                className="smc-ops-cal-day"
                style={{ padding:"4px 3px", borderRadius:6, cursor:"pointer",
                  background: isToday ? `${OPS_COLOR}18` : "transparent",
                  border: isToday ? `1px solid ${OPS_COLOR}66` : `1px solid ${C.border}44` }}>
                <div style={{ fontFamily:"monospace", fontSize:10, fontWeight:isToday?700:400,
                  color:isToday?OPS_COLOR:C.text, marginBottom:2 }}>{day}</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:2 }}>
                  {dayEvs.slice(0,3).map(ev => (
                    <div key={ev.id} onClick={e => { e.stopPropagation(); setViewEv(ev); }}
                      style={{ width:6, height:6, borderRadius:"50%", background:ev.color, cursor:"pointer" }} />
                  ))}
                  {dayEvs.length > 3 && <span style={{ fontFamily:"monospace", fontSize:7, color:C.muted }}>+{dayEvs.length-3}</span>}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {showForm && selDay && (
        <div style={{ background:C.panel, border:`1px solid ${OPS_COLOR}44`, borderRadius:12, padding:20, marginBottom:24 }}>
          <div style={{ fontFamily:"monospace", fontSize:11, fontWeight:700, color:OPS_COLOR, marginBottom:14, letterSpacing:2 }}>
            + NEW EVENT · {MONTHS[mo]} {selDay}, {yr}
          </div>
          <input value={evTitle} onChange={e => setEvTitle(e.target.value)} placeholder="Event title *"
            style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, color:C.text,
              padding:"10px 14px", fontFamily:"monospace", fontSize:12, borderRadius:6, boxSizing:"border-box", marginBottom:10 }} />
          <textarea value={evDesc} onChange={e => setEvDesc(e.target.value)} placeholder="Description (optional)" rows={2}
            style={{ width:"100%", background:C.bg, border:`1px solid ${C.border}`, color:C.text,
              padding:"10px 14px", fontFamily:"monospace", fontSize:12, borderRadius:6, boxSizing:"border-box", marginBottom:10, resize:"vertical" }} />
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            <span style={{ fontFamily:"monospace", fontSize:10, color:C.muted }}>Color:</span>
            {[OPS_COLOR,"#6366f1","#f59e0b","#10b981","#0ea5e9","#a855f7"].map(col => (
              <div key={col} onClick={() => setEvColor(col)}
                style={{ width:18, height:18, borderRadius:"50%", background:col, cursor:"pointer",
                  border: evColor===col ? `3px solid ${C.bright}` : "3px solid transparent" }} />
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={saveEvent} disabled={saving || !evTitle.trim()}
              style={{ padding:"9px 20px", background:`${OPS_COLOR}20`, border:`1px solid ${OPS_COLOR}`,
                color:OPS_COLOR, fontFamily:"monospace", fontSize:10, cursor:saving?"wait":"pointer", borderRadius:6, letterSpacing:1 }}>
              {saving ? "SAVING…" : "SAVE EVENT"}
            </button>
            <button onClick={() => { setShowForm(false); setSelDay(null); }}
              style={{ padding:"9px 20px", background:"transparent", border:`1px solid ${C.border}`,
                color:C.muted, fontFamily:"monospace", fontSize:10, cursor:"pointer", borderRadius:6 }}>
              CANCEL
            </button>
          </div>
        </div>
      )}

      <div style={{ fontFamily:"monospace", fontSize:10, letterSpacing:2, color:OPS_COLOR,
        borderBottom:`2px solid ${OPS_COLOR}44`, paddingBottom:8, marginBottom:16 }}>
        UPCOMING EVENTS ({upcomingEvents.length})
      </div>
      {upcomingEvents.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:C.muted, fontFamily:"monospace", fontSize:11 }}>
          No upcoming events. Click a day on the calendar to add one.
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {upcomingEvents.map(ev => (
            <div key={ev.id} onClick={() => setViewEv(ev)}
              style={{ background:C.panel, border:`1px solid ${C.border}`, borderLeft:`4px solid ${ev.color}`,
                borderRadius:"0 8px 8px 0", padding:"12px 14px", cursor:"pointer", display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, color:C.bright, fontSize:13, marginBottom:2 }}>{ev.title}</div>
                {ev.description && <div style={{ fontSize:12, color:C.muted, lineHeight:1.4 }}>{ev.description}</div>}
              </div>
              <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, flexShrink:0 }}>
                {ev.date === todayStr ? "TODAY" : ev.date}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OpsFeed({ userName, userId }) {
  const C = useTheme();
  const [allPosts,      setAllPosts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [deptTab,       setDeptTab]       = useState("all");
  const [viewPost,      setViewPost]      = useState(null);
  const [search,        setSearch]        = useState("");
  const [showOpsUpload, setShowOpsUpload] = useState(false);

  useEffect(() => {
    let loaded = 0;
    const bucket = {};
    const unsubs = OPS_PORTALS.map(p => {
      const q = query(collection(db, p.col), orderBy("uploadedAt","desc"));
      return onSnapshot(q, snap => {
        bucket[p.id] = snap.docs.map(d => ({ id:d.id, ...d.data(), _portal:p }));
        loaded++;
        if (loaded >= OPS_PORTALS.length) {
          const merged = Object.values(bucket).flat().sort((a,b) => {
            const ta = a.uploadedAt?.toDate?.() || new Date(a.uploadedAt||0);
            const tb = b.uploadedAt?.toDate?.() || new Date(b.uploadedAt||0);
            return tb - ta;
          });
          setAllPosts(merged);
          setLoading(false);
        }
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, []);

  const visible = allPosts
    .filter(p => canSeePost(p, userId, false))
    .filter(p => deptTab === "all" || p._portal.id === deptTab)
    .filter(p => !search || (p.title||"").toLowerCase().includes(search.toLowerCase()) || (p.uploadedBy||"").toLowerCase().includes(search.toLowerCase()));

  function renderSec(sec, i) {
    const isPdf  = /\.pdf$/i.test(sec.name||"");
    const isDoc2 = /\.(doc|docx|odt|rtf)$/i.test(sec.name||"");
    if (sec.type==="heading") return <div key={i} style={{ fontWeight:700, fontSize:15, color:viewPost._portal.color, margin:"16px 0 6px", fontFamily:"monospace" }}>{sec.text}</div>;
    if (sec.type==="paragraph"||sec.type==="text") return <p key={i} style={{ color:C.text, lineHeight:1.8, margin:"0 0 12px" }}>{sec.content||sec.text}</p>;
    if (sec.type==="bullets") return <ul key={i} style={{ color:C.text, lineHeight:1.8, margin:"0 0 12px", paddingLeft:24 }}>{(sec.items||[]).map((it,j)=><li key={j}>{it}</li>)}</ul>;
    if (sec.type==="code") return <pre key={i} style={{ background:C.panel, border:`1px solid ${C.border}`, padding:14, borderRadius:6, fontFamily:"monospace", fontSize:12, overflowX:"auto", margin:"8px 0", color:C.text }}>{sec.text||sec.content}</pre>;
    if ((sec.type==="image"||sec.type==="graph")&&sec.url) {
      if (isPdf) return <div key={i} style={{ margin:"16px 0" }}><PdfInlineViewer url={sec.url} name={sec.name||"Document"} /></div>;
      return <div key={i} style={{ margin:"16px 0" }}><img src={sec.url} alt={sec.caption||""} style={{ maxWidth:"100%", borderRadius:8, display:"block" }} />{sec.caption&&<div style={{ fontSize:12, color:C.muted, marginTop:6 }}>{sec.caption}</div>}</div>;
    }
    if ((sec.type==="attachment"||sec.type==="pdf"||sec.type==="file")&&sec.url) {
      if (isPdf)  return <div key={i} style={{ margin:"16px 0" }}><PdfInlineViewer url={sec.url} name={sec.name||"Document"} /></div>;
      if (isDoc2) return <div key={i} style={{ margin:"16px 0" }}><DocInlineViewer url={sec.url} name={sec.name||"Document"} accentColor={viewPost._portal.color} /></div>;
      return <div key={i} style={{ margin:"8px 0" }}><a href={sec.url} target="_blank" rel="noreferrer" style={{ color:viewPost._portal.color, fontFamily:"monospace", fontSize:12 }}>📎 {sec.name||"Open file"}</a></div>;
    }
    return null;
  }

  return (
    <div>
      {viewPost && (
        <div className="smc-modal-overlay smc-ops-report-modal" style={{ position:"fixed", inset:0, background:"#00000099", zIndex:200,
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:`${NAV_H + 20}px 16px 20px`, overflowY:"auto" }}
          onClick={e => e.target===e.currentTarget && setViewPost(null)}>
          <div className="smc-modal-panel" style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12,
            width:"100%", maxWidth:760, padding:28, position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
              <span style={{ fontFamily:"monospace", fontSize:9, background:`${viewPost._portal.color}20`,
                border:`1px solid ${viewPost._portal.color}44`, color:viewPost._portal.color,
                padding:"3px 10px", borderRadius:10, letterSpacing:1 }}>
                {viewPost._portal.icon} {viewPost._portal.label}
              </span>
              <div style={{ flex:1 }} />
              <button onClick={() => setViewPost(null)} style={{ background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:24 }}>×</button>
            </div>
            <h2 style={{ fontFamily:"monospace", color:C.bright, fontSize:20, fontWeight:700, margin:"0 0 8px", overflowWrap:"anywhere", wordBreak:"break-word" }}>{viewPost.title}</h2>
            <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, marginBottom:24 }}>
              by {viewPost.uploadedBy} · {viewPost.uploadedAt?.toDate?.().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})||""}
            </div>
            {(viewPost.sections||[]).map((sec,i) => renderSec(sec,i))}
            {(!viewPost.sections?.length)&&viewPost.description&&<p style={{ color:C.text, lineHeight:1.7 }}>{viewPost.description}</p>}
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <button onClick={() => setShowOpsUpload(true)}
          style={{ padding:'8px 18px', background:`${OPS_COLOR}18`, border:`1px solid ${OPS_COLOR}`,
            color:OPS_COLOR, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6, letterSpacing:1, whiteSpace:'nowrap' }}>
          + UPLOAD REPORT
        </button>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search across all departments…"
        style={{ width:"100%", background:C.panel, border:`1px solid ${C.border}`, color:C.text,
          padding:"10px 16px", fontFamily:"monospace", fontSize:12, borderRadius:8,
          boxSizing:"border-box", marginBottom:16, outline:"none" }} />

      <div className="smc-ops-dept-pills">
        {[{id:"all",label:"All Departments",icon:"🌐",color:OPS_COLOR}, ...OPS_PORTALS].map(tab => (
          <button key={tab.id} onClick={() => setDeptTab(tab.id)}
            style={{ padding:"6px 14px", fontFamily:"monospace", fontSize:9, letterSpacing:1,
              cursor:"pointer", borderRadius:20,
              background: deptTab===tab.id ? `${tab.color}20` : "transparent",
              border:`1px solid ${deptTab===tab.id ? tab.color : C.border}`,
              color: deptTab===tab.id ? tab.color : C.muted }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign:"center", padding:60, color:C.muted, fontFamily:"monospace" }}>LOADING REPORTS…</div>}
      {!loading && visible.length === 0 && <div style={{ textAlign:"center", padding:60, color:C.muted, fontFamily:"monospace" }}>No reports found.</div>}

      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {visible.map(post => {
          const p = post._portal;
          const cover = post.coverUrl || post.sections?.find(s=>(s.type==="image"||s.type==="graph")&&s.thumbnailUrl)?.thumbnailUrl;
          return (
            <div key={`${p.id}-${post.id}`} onClick={() => setViewPost(post)}
              className="smc-ops-report-card"
              style={{ background:C.panel, border:`1px solid ${C.border}`, borderLeft:`4px solid ${p.color}`,
                borderRadius:"0 10px 10px 0", padding:"14px 16px", cursor:"pointer" }}>
              {cover && <img src={cover} alt="" style={{ borderRadius:6 }} />}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:'wrap' }}>
                  <span style={{ fontFamily:"monospace", fontSize:8, color:p.color, letterSpacing:1 }}>{p.icon} {p.label}</span>
                  {post.adminLiked && <span style={{ fontFamily:"monospace", fontSize:8, color:"#10b981", background:"#10b98115", padding:"1px 6px", borderRadius:4 }}>✓ APPROVED</span>}
                  {post.visibility === 'private'  && <span style={{ fontFamily:"monospace", fontSize:8, color:"#a855f7", background:"#a855f715", padding:"1px 6px", borderRadius:4 }}>🔒 PRIVATE</span>}
                  {post.visibility === 'unlisted' && <span style={{ fontFamily:"monospace", fontSize:8, color:"#f59e0b", background:"#f59e0b15", padding:"1px 6px", borderRadius:4 }}>🔗 UNLISTED</span>}
                </div>
                <div className="smc-ops-report-title" style={{ fontWeight:700, color:C.bright, fontSize:14, marginBottom:3 }}>{post.title}</div>
                <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted }}>
                  {post.uploadedBy} · {post.uploadedAt?.toDate?.().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})||""}
                  {post.sections?.length ? ` · ${post.sections.length} sections` : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {showOpsUpload && (
        <Suspense fallback={null}>
          <OpsUploadModal user={{ name: userName, uid: userId }} isAdmin={false} onClose={() => setShowOpsUpload(false)} />
        </Suspense>
      )}
    </div>
  );
}

const OT = {
  panel: 'var(--smc-panel)',
  border: 'var(--smc-bd)',
  text: 'var(--smc-tx)',
  muted: 'var(--smc-tx3)',
  input: 'var(--smc-el)',
  bright: 'var(--smc-tx2)',
  ac: 'var(--smc-ac)',
  acBg: 'var(--smc-ac-bg)',
  acBd: 'var(--smc-ac-border)',
  mono: "'Share Tech Mono', monospace",
};

function opsMembersForDept(members, deptId) {
  if (deptId === 'all') return members;
  return members.filter(m => (m.role || '') === deptId);
}

function opsResolveAssignees(members, deptId, pickedIds) {
  if (pickedIds?.length) {
    const set = new Set(pickedIds);
    return members.filter(m => set.has(m.uid));
  }
  return opsMembersForDept(members, deptId);
}

function opsAssigneeLabel(task, members) {
  const ids = task.assigneeIds?.length ? task.assigneeIds : (task.assigneeId ? [task.assigneeId] : []);
  if (ids.length === 1) {
    const m = members.find(x => x.uid === ids[0]);
    return m?.name || m?.email || task.assigneeName || '1 person';
  }
  if (ids.length > 1) {
    const names = ids.slice(0, 3).map(uid => {
      const m = members.find(x => x.uid === uid);
      return m?.name || m?.email || uid.slice(0, 6);
    });
    return ids.length <= 3 ? names.join(', ') : `${ids.length} people`;
  }
  if (task.portal === 'all') return 'All team';
  const p = OPS_PORTALS.find(x => x.id === task.portal);
  return p ? `All ${p.label}` : 'All department';
}

function OpsTaskAssigneePicker({ members, deptId, selectedIds, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const pool = opsMembersForDept(members, deptId);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? pool.filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    : pool;
  const selected = members.filter(m => selectedIds.includes(m.uid));
  const roleColor = { admin: '#ef4444', mp: '#6366f1', embedded: '#f59e0b', system: '#10b981', ops: '#e11d48', pe: '#8b5cf6', c_suite: '#dc2626', payload: '#0891b2' };

  useEffect(() => {
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function toggle(uid) {
    onChange(selectedIds.includes(uid) ? selectedIds.filter(x => x !== uid) : [...selectedIds, uid]);
  }

  const inp = {
    width: '100%', background: OT.input, border: `0.5px solid ${OT.border}`, color: OT.text,
    padding: '10px 14px', fontFamily: OT.mono, fontSize: 12, borderRadius: 6, boxSizing: 'border-box',
  };

  return (
    <div ref={wrapRef} className="smc-ops-assignee-picker" style={{ marginBottom: 14, position: 'relative' }}>
      <div style={{ fontFamily: OT.mono, fontSize: 9, color: OT.muted, letterSpacing: 2, marginBottom: 6 }}>
        ASSIGN INDIVIDUALS{' '}
        <span style={{ letterSpacing: 0, textTransform: 'none' }}>
          ({deptId === 'all' ? 'optional — leave empty for entire team' : 'optional — leave empty for whole department'})
        </span>
      </div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {selected.map(m => (
            <button key={m.uid} type="button" onClick={() => toggle(m.uid)}
              style={{ padding: '4px 10px', fontFamily: OT.mono, fontSize: 9, cursor: 'pointer', borderRadius: 20,
                background: OT.acBg, border: `0.5px solid ${OT.acBd}`, color: OT.ac }}>
              {m.name || m.email} ×
            </button>
          ))}
        </div>
      )}
      <input
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search team members to assign…"
        style={inp}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60, marginTop: 4,
          background: OT.panel, border: `0.5px solid ${OT.border}`, borderRadius: 8,
          maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--smc-shadow)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', fontFamily: OT.mono, fontSize: 10, color: OT.muted }}>No members found</div>
          ) : filtered.map(m => {
            const sel = selectedIds.includes(m.uid);
            const rc = roleColor[m.role] || OT.ac;
            return (
              <button key={m.uid} type="button" onClick={() => toggle(m.uid)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                  padding: '10px 14px', cursor: 'pointer', border: 'none', borderBottom: `0.5px solid ${OT.border}`,
                  background: sel ? OT.acBg : 'transparent', color: OT.text, fontFamily: OT.mono, fontSize: 11 }}>
                <span style={{ width: 16, flexShrink: 0, color: sel ? OT.ac : OT.muted }}>{sel ? '✓' : '○'}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.name || m.email}
                </span>
                <span style={{ fontSize: 8, color: rc, flexShrink: 0 }}>{(m.role || '').toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OpsTaskManager({ userName, userId }) {
  const [tasks,       setTasks]       = useState([]);
  const [members,     setMembers]     = useState([]);
  const [showForm,    setShowForm]    = useState(false);
  const [title,       setTitle]       = useState("");
  const [desc,        setDesc]        = useState("");
  const [priority,    setPriority]    = useState("medium");
  const [dueDate,     setDueDate]     = useState("");
  const [taskDept,    setTaskDept]    = useState("all");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [filter,      setFilter]      = useState("all");
  const [saving,      setSaving]      = useState(false);
  const [requiresProof, setRequiresProof] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "tasks"), orderBy("createdAt","desc"));
    return onSnapshot(q, snap => setTasks(snap.docs.map(d => ({ id:d.id, ...d.data() }))), () => {});
  }, []);

  useEffect(() => {
    return onSnapshot(collection(db, 'authorized_users'),
      snap => setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(m => m.active !== false)),
      () => {});
  }, []);

  useEffect(() => {
    if (taskDept === 'all') return;
    setAssigneeIds(prev => prev.filter(id => {
      const m = members.find(x => x.uid === id);
      return m && m.role === taskDept;
    }));
  }, [taskDept, members]);

  function resetForm() {
    setTitle(""); setDesc(""); setPriority("medium"); setDueDate("");
    setTaskDept("all"); setAssigneeIds([]); setShowForm(false); setRequiresProof(true);
  }

  async function createTask() {
    if (!title.trim()) return;
    const assignees = opsResolveAssignees(members, taskDept, assigneeIds);
    if (!assignees.length) {
      alert('No assignees found for this selection. Add team members or pick individuals.');
      return;
    }
    setSaving(true);
    try {
      const ids = assignees.map(a => a.uid);
      const names = assignees.map(a => a.name || a.email || '');
      await addDoc(collection(db, "tasks"), { ...actorStamp(),
        title: title.trim(), description: desc.trim(),
        status: "pending", priority, dueDate, portal: taskDept,
        assignmentScope: assigneeIds.length ? 'individuals' : (taskDept === 'all' ? 'org' : 'department'),
        assigneeIds: ids, assigneeNames: names,
        assigneeId: ids[0] || '', assigneeName: names[0] || '',
        assigneeCount: ids.length,
        createdBy: userName, createdByUid: userId,
        requiresProof, sourceType: 'manual',
        createdAt: serverTimestamp(),
      });
      resetForm();
    } catch(e) {}
    setSaving(false);
  }

  // Closing goes through review (two-person rule) — see utils/taskClosure.js
  async function updateStatus(id, status) {
    await setTaskStatus(id, status);
  }

  const COLS = [
    { key:"pending",     label:"PENDING",     color:"#f59e0b" },
    { key:"in_progress", label:"IN PROGRESS", color:"#0ea5e9" },
    { key:"completed",   label:"COMPLETED",   color:"#10b981" },
  ];

  const visible = tasks.filter(t => {
    if (filter === 'all') return true;
    if (t.portal === filter) return true;
    const ids = t.assigneeIds?.length ? t.assigneeIds : (t.assigneeId ? [t.assigneeId] : []);
    return ids.some(uid => members.find(m => m.uid === uid)?.role === filter);
  });

  const inp = {
    width: '100%', background: OT.input, border: `0.5px solid ${OT.border}`, color: OT.text,
    padding: '10px 14px', fontFamily: OT.mono, fontSize: 12, borderRadius: 6, boxSizing: 'border-box',
  };
  const sel = {
    background: OT.input, border: `0.5px solid ${OT.border}`, color: OT.text,
    padding: '8px 12px', fontFamily: OT.mono, fontSize: 11, borderRadius: 6, cursor: 'pointer',
  };

  return (
    <div>
      <div className="smc-ops-task-toolbar">
        <div className="smc-ops-task-toolbar-filters">
          {[{id:"all",label:"All",color:OPS_COLOR}, ...OPS_PORTALS].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              style={{ padding:"5px 12px", fontFamily:OT.mono, fontSize:9, cursor:"pointer", borderRadius:20,
                background: filter===f.id ? OT.acBg : "transparent",
                border:`0.5px solid ${filter===f.id ? OT.acBd : OT.border}`,
                color: filter===f.id ? OT.ac : OT.muted }}>
              {f.icon ? `${f.icon} ` : ""}{f.label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(s => !s)}
          style={{ padding:"8px 18px", background:OT.acBg, border:`0.5px solid ${OT.acBd}`,
            color:OT.ac, fontFamily:OT.mono, fontSize:10, cursor:"pointer", borderRadius:6, letterSpacing:1, whiteSpace:"nowrap" }}>
          + NEW TASK
        </button>
      </div>

      {showForm && (
        <div className="smc-ops-task-form" style={{ background:OT.panel, border:`0.5px solid ${OT.acBd}`, borderRadius:12, padding:20, marginBottom:20 }}>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Task title *"
            style={{ ...inp, marginBottom:10 }} />
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)" rows={2}
            style={{ ...inp, marginBottom:10, resize:"vertical" }} />
          <div className="smc-ops-task-form-row" style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14 }}>
            <select value={priority} onChange={e => setPriority(e.target.value)} style={sel}>
              <option value="low">Low Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="high">High Priority</option>
            </select>
            <select value={taskDept} onChange={e => setTaskDept(e.target.value)} style={sel}>
              <option value="all">All Departments</option>
              {OPS_PORTALS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={sel} />
          </div>
          <OpsTaskAssigneePicker
            members={members}
            deptId={taskDept}
            selectedIds={assigneeIds}
            onChange={setAssigneeIds}
          />
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", margin:"12px 0" }}>
            <input type="checkbox" checked={requiresProof} onChange={e => setRequiresProof(e.target.checked)}
              style={{ width:14, height:14, cursor:"pointer", accentColor:OT.ac }} />
            <span style={{ fontFamily:OT.mono, fontSize:9, color:OT.muted }}>
              Require proof (text or attachment) before this can be marked complete
            </span>
          </label>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button onClick={createTask} disabled={saving || !title.trim()}
              style={{ padding:"9px 20px", background:OT.acBg, border:`0.5px solid ${OT.acBd}`,
                color:OT.ac, fontFamily:OT.mono, fontSize:10, cursor:saving?"wait":"pointer", borderRadius:6, letterSpacing:1 }}>
              {saving ? "SAVING…" : "CREATE TASK"}
            </button>
            <button onClick={resetForm}
              style={{ padding:"9px 20px", background:"transparent", border:`0.5px solid ${OT.border}`,
                color:OT.muted, fontFamily:OT.mono, fontSize:10, cursor:"pointer", borderRadius:6 }}>
              CANCEL
            </button>
          </div>
        </div>
      )}

      <div className="smc-ops-kanban">
        {COLS.map(col => {
          const colTasks = visible.filter(t => boardStatus(t.status) === col.key);
          return (
            <div key={col.key}>
              <div style={{ fontFamily:"monospace", fontSize:10, letterSpacing:2, color:col.color,
                borderBottom:`2px solid ${col.color}44`, paddingBottom:8, marginBottom:14 }}>
                {col.label} ({colTasks.length})
              </div>
              {colTasks.length === 0 && <div style={{ color:OT.muted, fontSize:12, textAlign:"center", padding:"20px 0", fontFamily:OT.mono }}>— none —</div>}
              {colTasks.map(task => {
                const portal = task.portal && task.portal !== 'all' ? OPS_PORTALS.find(p => p.id === task.portal) : null;
                const assigneeLabel = opsAssigneeLabel(task, members);
                return (
                  <div key={task.id} style={{ background:OT.panel, border:`0.5px solid ${OT.border}`,
                    borderRadius:10, padding:14, marginBottom:10, borderLeft:`3px solid ${col.color}` }}>
                    {portal ? (
                      <div style={{ fontFamily:OT.mono, fontSize:8, color:portal.color, marginBottom:5, letterSpacing:1 }}>{portal.icon} {portal.label}</div>
                    ) : task.portal === 'all' ? (
                      <div style={{ fontFamily:OT.mono, fontSize:8, color:OT.ac, marginBottom:5, letterSpacing:1 }}>🌐 All Departments</div>
                    ) : null}
                    <div style={{ fontWeight:700, color:OT.bright, fontSize:13, marginBottom:5 }}>{task.title}</div>
                    <TaskReviewBadge task={task} />
                    {task.description && <div style={{ fontSize:12, color:OT.text, lineHeight:1.5, marginBottom:8 }}>{task.description}</div>}
                    <div style={{ fontSize:10, color:OT.muted, fontFamily:OT.mono, marginBottom:8 }}>👤 {assigneeLabel}</div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                      {task.priority && (
                        <span style={{ fontSize:9, padding:"2px 7px", borderRadius:4, fontFamily:OT.mono,
                          background: task.priority==="high"?"#f43f5e22":task.priority==="medium"?"#f59e0b22":"#10b98122",
                          color:      task.priority==="high"?"#f43f5e": task.priority==="medium"?"#f59e0b": "#10b981" }}>
                          {task.priority.toUpperCase()}
                        </span>
                      )}
                      {task.dueDate && <span style={{ fontSize:9, color:OT.muted, fontFamily:OT.mono }}>📅 {task.dueDate}</span>}
                      {task.createdBy && <span style={{ fontSize:9, color:OT.muted, fontFamily:OT.mono }}>by {task.createdBy}</span>}
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      {task.status !== "in_progress" && task.status !== "completed" && (
                        <button onClick={() => updateStatus(task.id,"in_progress")}
                          style={{ flex:1, padding:"5px 0", fontSize:9, fontFamily:"monospace", letterSpacing:1,
                            background:"#0ea5e915", border:"1px solid #0ea5e9", color:"#0ea5e9", cursor:"pointer", borderRadius:4 }}>
                          START →
                        </button>
                      )}
                      {task.status !== "completed" && (
                        <button onClick={() => updateStatus(task.id,"completed")}
                          style={{ flex:1, padding:"5px 0", fontSize:9, fontFamily:"monospace", letterSpacing:1,
                            background:"#10b98115", border:"1px solid #10b981", color:"#10b981", cursor:"pointer", borderRadius:4 }}>
                          ✔ DONE
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Company Data Room ─────────────────────────────────────────────────────────
const DATA_ROOM_CATS = [
  { id: 'compliance', label: 'Compliance',       icon: '✅', color: '#10b981' },
  { id: 'legal',      label: 'Legal & Agreements', icon: '⚖',  color: '#6366f1' },
  { id: 'corporate',  label: 'Corporate',        icon: '🏢', color: '#0ea5e9' },
  { id: 'financial',  label: 'Financial',        icon: '💰', color: '#f59e0b' },
  { id: 'hr',         label: 'HR & Employment',  icon: '👥', color: '#f97316' },
  { id: 'investor',   label: 'Investor Docs',    icon: '🏦', color: OPS_COLOR  },
  { id: 'ip',         label: 'IP & Patents',     icon: '🔬', color: '#8b5cf6' },
  { id: 'other',      label: 'Other',            icon: '📁', color: '#94a3b8' },
];

function dataRoomFileIcon(name, mime) {
  const n = (name || '').toLowerCase();
  const m = (mime || '').toLowerCase();
  if (m.includes('pdf') || n.endsWith('.pdf')) return '📄';
  if (m.includes('image') || /\.(png|jpe?g|gif|webp|svg)$/.test(n)) return '🖼';
  if (/\.(docx?|odt|rtf)$/.test(n)) return '📝';
  if (/\.(xlsx?|csv)$/.test(n)) return '📊';
  if (/\.(pptx?|key)$/.test(n)) return '📽';
  return '📎';
}

function fmtDataRoomBytes(n) {
  if (!n) return '';
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}

function OpsCompanyDataRoom({ userName, userId, canUpload = true }) {
  const C = useTheme();
  const isAdmin = sessionStorage.getItem('sat_admin_session') === '1';
  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch]       = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [title, setTitle]         = useState('');
  const [category, setCategory]   = useState('compliance');
  const [description, setDescription] = useState('');
  const [file, setFile]           = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'company_documents'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      setDocs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, []);

  const inp = {
    background: C.panel, border: `1px solid ${C.border}`, color: C.text,
    padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, borderRadius: 6,
    width: '100%', boxSizing: 'border-box', marginBottom: 8,
  };

  async function uploadDocument() {
    if (!title.trim() || !file) return;
    setUploading(true);
    try {
      const { getStorage, ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `company/data-room/${Date.now()}_${safeName}`;
      const task = uploadBytesResumable(ref(storage, path), file);
      await new Promise((res, rej) => task.on('state_changed', null, rej, res));
      const url = await getDownloadURL(task.snapshot.ref);
      await addDoc(collection(db, 'company_documents'), {
        title: title.trim(),
        category,
        description: description.trim(),
        fileName: file.name,
        url,
        storagePath: path,
        size: file.size,
        mimeType: file.type || '',
        uploadedBy: userName,
        uploadedByUid: userId,
        createdAt: serverTimestamp(),
      });
      setTitle(''); setDescription(''); setCategory('compliance'); setFile(null); setShowUpload(false);
    } catch (e) {
      alert('Upload failed: ' + (e.message || 'Unknown error'));
    }
    setUploading(false);
  }

  async function archiveDocument(item) {
    if (!window.confirm(`Archive "${item.title}"? It stays in the data room but is hidden from the default view. Documents cannot be deleted.`)) return;
    try {
      await updateDoc(doc(db, 'company_documents', item.id), {
        archived: true,
        archivedAt: serverTimestamp(),
        archivedByUid: userId,
        archivedByName: userName,
      });
      writeLog('company_doc_archived', { title: item.title, archivedBy: userName }).catch(() => {});
    } catch (e) {
      alert('Archive failed: ' + (e.message || 'Unknown error'));
    }
  }

  async function unarchiveDocument(item) {
    try {
      await updateDoc(doc(db, 'company_documents', item.id), {
        archived: false, archivedAt: null, archivedByUid: null, archivedByName: null,
      });
    } catch (e) {
      alert('Unarchive failed: ' + (e.message || 'Unknown error'));
    }
  }

  const q = search.trim().toLowerCase();
  const visible = docs.filter(d => {
    if (showArchived ? !d.archived : !!d.archived) return false;
    if (catFilter !== 'all' && d.category !== catFilter) return false;
    if (!q) return true;
    return (d.title || '').toLowerCase().includes(q)
      || (d.fileName || '').toLowerCase().includes(q)
      || (d.description || '').toLowerCase().includes(q);
  });
  const activeCount   = docs.filter(d => !d.archived).length;
  const archivedCount = docs.filter(d => d.archived).length;

  const catMeta = id => DATA_ROOM_CATS.find(c => c.id === id) || DATA_ROOM_CATS[DATA_ROOM_CATS.length - 1];

  return (
    <div>
      <div className="smc-ops-section-toolbar">
        <div className="smc-ops-section-toolbar-title" style={{ fontFamily: 'monospace', letterSpacing: 2, color: '#0ea5e9' }}>
          📁 COMPANY DATA ROOM
        </div>
        <div className="smc-ops-section-toolbar-actions">
          <button onClick={() => setShowArchived(a => !a)}
            style={{ padding: '5px 14px', background: showArchived ? '#64748b20' : 'transparent',
              border: '1px solid #64748b55', color: '#94a3b8', fontFamily: 'monospace', fontSize: 9,
              cursor: 'pointer', borderRadius: 4, letterSpacing: 1, marginRight: 8 }}>
            {showArchived ? `← ACTIVE (${activeCount})` : `📦 ARCHIVED (${archivedCount})`}
          </button>
          {canUpload && (
            <button onClick={() => setShowUpload(s => !s)}
              style={{ padding: '5px 14px', background: '#0ea5e920', border: '1px solid #0ea5e9',
                color: '#0ea5e9', fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', borderRadius: 4, letterSpacing: 1 }}>
              {showUpload ? 'CANCEL' : '+ UPLOAD DOCUMENT'}
            </button>
          )}
        </div>
      </div>

      <div style={{ fontFamily: 'monospace', fontSize: 10, color: C.muted, lineHeight: 1.6, marginBottom: 16,
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 16px' }}>
        Central repository for compliance filings, legal agreements, corporate records, and other company documents.
        Documents can't be deleted once uploaded{isAdmin ? ' — you can archive one to hide it from the default view.' : '.'}
      </div>

      {showUpload && canUpload && (
        <div style={{ background: C.panel, border: `1px solid #0ea5e944`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 10 }}>UPLOAD NEW DOCUMENT</div>
          <div style={{ fontFamily: 'monospace', fontSize: 9, color: C.muted, marginBottom: 3 }}>TITLE *</div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. GST Registration Certificate" style={inp} />
          <div className="smc-ops-data-room-upload-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: '1 1 180px' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: C.muted, marginBottom: 3 }}>CATEGORY</div>
              <select value={category} onChange={e => setCategory(e.target.value)} style={inp}>
                {DATA_ROOM_CATS.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div style={{ flex: '2 1 240px' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: C.muted, marginBottom: 3 }}>FILE *</div>
              <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} style={{ ...inp, padding: '6px 10px' }} />
            </div>
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 9, color: C.muted, marginBottom: 3 }}>DESCRIPTION (optional)</div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
            placeholder="Notes, expiry date, filing reference…" style={{ ...inp, resize: 'vertical' }} />
          <button onClick={uploadDocument} disabled={uploading || !title.trim() || !file}
            style={{ padding: '9px 20px', background: '#0ea5e920', border: '1px solid #0ea5e9',
              color: '#0ea5e9', fontFamily: 'monospace', fontSize: 10, cursor: uploading ? 'wait' : 'pointer',
              borderRadius: 6, letterSpacing: 1, opacity: (!title.trim() || !file) ? 0.5 : 1 }}>
            {uploading ? 'UPLOADING…' : 'SAVE TO DATA ROOM'}
          </button>
        </div>
      )}

      <div className="smc-ops-data-room-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={() => setCatFilter('all')}
          style={{ padding: '5px 12px', fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', borderRadius: 20,
            background: catFilter === 'all' ? '#0ea5e920' : 'transparent',
            border: `1px solid ${catFilter === 'all' ? '#0ea5e9' : C.border}`,
            color: catFilter === 'all' ? '#0ea5e9' : C.muted }}>
          ALL ({docs.length})
        </button>
        {DATA_ROOM_CATS.map(c => {
          const count = docs.filter(d => d.category === c.id).length;
          if (!count && catFilter !== c.id) return null;
          return (
            <button key={c.id} onClick={() => setCatFilter(c.id)}
              style={{ padding: '5px 12px', fontFamily: 'monospace', fontSize: 9, cursor: 'pointer', borderRadius: 20,
                background: catFilter === c.id ? `${c.color}20` : 'transparent',
                border: `1px solid ${catFilter === c.id ? c.color : C.border}`,
                color: catFilter === c.id ? c.color : C.muted }}>
              {c.icon} {c.label} ({count})
            </button>
          );
        })}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents…"
        style={{ ...inp, marginBottom: 16, maxWidth: 360 }} />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>LOADING…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: C.muted, fontFamily: 'monospace', fontSize: 11,
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📁</div>
          {showArchived
            ? 'No archived documents.'
            : docs.length === 0
              ? (canUpload
                ? 'No documents in the data room yet. Click + UPLOAD DOCUMENT to add compliance files, agreements, and corporate records.'
                : 'No documents in the data room yet.')
              : 'No documents match your filters.'}
        </div>
      ) : (
        <div className="smc-ops-data-room-grid">
          {visible.map(item => {
            const cat = catMeta(item.category);
            const ts = item.createdAt?.toDate?.();
            return (
              <div key={item.id} className="smc-ops-data-room-card"
                style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
                  padding: '14px 16px', borderTop: `3px solid ${cat.color}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{dataRoomFileIcon(item.fileName, item.mimeType)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: C.bright, fontSize: 13, lineHeight: 1.35, marginBottom: 4 }}>
                      {item.title}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, color: cat.color, letterSpacing: 1, marginBottom: 4 }}>
                      {cat.icon} {cat.label.toUpperCase()}
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 9, color: C.muted,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.fileName}{item.size ? ` · ${fmtDataRoomBytes(item.size)}` : ''}
                    </div>
                  </div>
                </div>
                {item.description && (
                  <div style={{ fontSize: 11, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>{item.description}</div>
                )}
                <div style={{ fontFamily: 'monospace', fontSize: 8, color: C.muted, marginBottom: 10 }}>
                  {item.uploadedBy && `by ${item.uploadedBy}`}
                  {ts && ` · ${ts.toLocaleDateString()}`}
                  {item.archived && item.archivedByName && (
                    <span style={{ color: '#94a3b8' }}> · archived by {item.archivedByName}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <a href={item.url} target="_blank" rel="noreferrer"
                    style={{ flex: 1, textAlign: 'center', padding: '6px 0', fontFamily: 'monospace', fontSize: 9,
                      letterSpacing: 1, background: `${cat.color}15`, border: `1px solid ${cat.color}`,
                      color: cat.color, textDecoration: 'none', borderRadius: 4 }}>
                    VIEW ↗
                  </a>
                  {isAdmin && (item.archived
                    ? (
                      <button onClick={() => unarchiveDocument(item)}
                        style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 9,
                          background: '#10b98110', border: '1px solid #10b98144',
                          color: '#10b981', cursor: 'pointer', borderRadius: 4 }}>
                        ↩ RESTORE
                      </button>
                    ) : (
                      <button onClick={() => archiveDocument(item)}
                        style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 9,
                          background: '#64748b10', border: '1px solid #64748b44',
                          color: '#94a3b8', cursor: 'pointer', borderRadius: 4 }}>
                        📦 ARCHIVE
                      </button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Company / Finance Dashboard ───────────────────────────────────────────────
function OpsCompanyDashboard({ userName, userId }) {
  const C = useTheme();
  const [dash, setDash]         = useState('hosting');
  const [data, setData]         = useState({});
  const [editing, setEditing]   = useState(null); // section key being edited
  const [draft, setDraft]       = useState({});
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);

  const SECTIONS = [
    { key:'hosting',   label:'☁  Hosting & Cloud',  color:'#6366f1' },
    { key:'finances',  label:'💰  Finances',          color:'#10b981' },
    { key:'team',      label:'👥  Team & HR',          color:'#f59e0b' },
    { key:'investors', label:'🏦  Investors',           color:OPS_COLOR  },
    { key:'dataroom',  label:'📁  Data Room',          color:'#0ea5e9' },
  ];

  useEffect(() => {
    const unsubs = SECTIONS.map(s => {
      return onSnapshot(doc(db, 'company_data', s.key), snap => {
        setData(prev => ({ ...prev, [s.key]: snap.exists() ? snap.data() : {} }));
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit(key) {
    setEditing(key);
    setDraft({ ...(data[key] || {}) });
  }

  async function saveEdit() {
    setSaving(true);
    try {
      await setDoc(doc(db, 'company_data', editing), { ...draft, updatedAt: serverTimestamp() }, { merge: true });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      setEditing(null);
    } catch (_) {}
    setSaving(false);
  }

  const inp = {
    background: C.panel, border:`1px solid ${C.border}`, color: C.text,
    padding:'8px 12px', fontFamily:'monospace', fontSize:11, borderRadius:6,
    width:'100%', boxSizing:'border-box', marginBottom:8,
  };

  function StatCard({ label, value, unit, color }) {
    return (
      <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:10,
        padding:'16px 18px', borderTop:`3px solid ${color}` }}>
        <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:2, marginBottom:6 }}>{label}</div>
        <div style={{ fontSize:26, fontWeight:900, color, fontFamily:'monospace' }}>{value || '—'}</div>
        {unit && <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, marginTop:2 }}>{unit}</div>}
      </div>
    );
  }

  const sec = SECTIONS.find(s => s.key === dash);
  const d   = data[dash] || {};

  return (
    <div className="smc-ops-company">
      {/* Section nav */}
      <div className="smc-ops-section-nav">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setDash(s.key)}
            style={{ padding:'9px 18px', fontFamily:'monospace', fontSize:10, letterSpacing:1,
              cursor:'pointer', borderRadius:6, border:`1px solid ${dash===s.key ? s.color : C.border}`,
              background: dash===s.key ? `${s.color}18` : 'transparent',
              color: dash===s.key ? s.color : C.muted }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── HOSTING ── */}
      {dash === 'hosting' && (
        <div>
          <div className="smc-ops-section-toolbar">
            <div className="smc-ops-section-toolbar-title" style={{ fontFamily:'monospace', letterSpacing:2, color:'#6366f1' }}>
              ☁ FIREBASE HOSTING & CLOUD USAGE
            </div>
            <div className="smc-ops-section-toolbar-actions">
            <a href="https://console.firebase.google.com/project/satellite-operating-procedure/usage" target="_blank" rel="noreferrer"
              style={{ fontFamily:'monospace', fontSize:9, color:'#6366f1', border:'1px solid #6366f133',
                padding:'5px 12px', borderRadius:4, textDecoration:'none', letterSpacing:1 }}>
              ↗ FIREBASE CONSOLE
            </a>
            <button onClick={() => startEdit('hosting')}
              style={{ padding:'5px 14px', background:'#6366f120', border:'1px solid #6366f1',
                color:'#6366f1', fontFamily:'monospace', fontSize:9, cursor:'pointer', borderRadius:4, letterSpacing:1 }}>
              {editing==='hosting' ? 'EDITING…' : '✎ UPDATE'}
            </button>
            </div>
          </div>

          {editing === 'hosting' ? (
            <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:24, marginBottom:20 }}>
              {[
                ['storageCost','Monthly Storage Cost ($)'],['bandwidthCost','Monthly Bandwidth Cost ($)'],
                ['hostingCost','Monthly Hosting Cost ($)'],['storageGB','Storage Used (GB)'],
                ['bandwidthGB','Bandwidth This Month (GB)'],['requests','Requests / Day (avg)'],
                ['totalMonthlyCost','Total Cloud Cost / Month ($)'],['notes','Notes'],
              ].map(([k,label]) => (
                <div key={k}>
                  <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:1, marginBottom:3 }}>{label}</div>
                  <input value={draft[k]||''} onChange={e => setDraft(p=>({...p,[k]:e.target.value}))}
                    placeholder={label} style={inp} />
                </div>
              ))}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button onClick={saveEdit} disabled={saving}
                  style={{ padding:'8px 20px', background:'#6366f120', border:'1px solid #6366f1',
                    color:'#6366f1', fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  {saved ? 'SAVED ✓' : saving ? 'SAVING…' : 'SAVE'}
                </button>
                <button onClick={() => setEditing(null)}
                  style={{ padding:'8px 16px', background:'transparent', border:`1px solid ${C.border}`,
                    color:C.muted, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  CANCEL
                </button>
              </div>
            </div>
          ) : (
            <div className="smc-stat-grid" style={{ marginBottom:24 }}>
              <StatCard label="TOTAL / MONTH"    value={d.totalMonthlyCost ? `$${d.totalMonthlyCost}` : null} color="#6366f1" />
              <StatCard label="STORAGE COST"     value={d.storageCost ? `$${d.storageCost}` : null}          color="#8b5cf6" />
              <StatCard label="BANDWIDTH COST"   value={d.bandwidthCost ? `$${d.bandwidthCost}` : null}      color="#a78bfa" />
              <StatCard label="HOSTING COST"     value={d.hostingCost ? `$${d.hostingCost}` : null}          color="#7c3aed" />
              <StatCard label="STORAGE USED"     value={d.storageGB}   unit="GB"                              color="#6366f1" />
              <StatCard label="BANDWIDTH / MO"   value={d.bandwidthGB} unit="GB"                              color="#6366f1" />
              <StatCard label="REQUESTS / DAY"   value={d.requests}    unit="avg"                             color="#6366f1" />
            </div>
          )}
          {d.notes && !editing && (
            <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, background:C.panel,
              border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 16px' }}>
              📝 {d.notes}
            </div>
          )}
          {!d.totalMonthlyCost && !editing && (
            <div style={{ textAlign:'center', padding:'40px 0', color:C.muted, fontFamily:'monospace', fontSize:11 }}>
              No hosting data yet. Click ✎ UPDATE to enter values from the Firebase console.
            </div>
          )}
          <div style={{ marginTop:24, background:C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 20px' }}>
            <div style={{ fontFamily:'monospace', fontSize:9, color:'#6366f1', letterSpacing:2, marginBottom:10 }}>PROJECT INFO</div>
            {[
              ['Project ID',    'satellite-operating-procedure'],
              ['Auth Domain',   'satellite-operating-procedure.firebaseapp.com'],
              ['Hosting URL',   'satellite-operating-procedure.web.app'],
              ['Storage Bucket','satellite-operating-procedure.firebasestorage.app'],
            ].map(([k,v]) => (
              <div key={k} className="smc-ops-info-row" style={{ borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontFamily:'monospace', fontSize:10, color:C.muted }}>{k}</span>
                <span style={{ fontFamily:'monospace', fontSize:10, color:C.bright, wordBreak:'break-all' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FINANCES ── */}
      {dash === 'finances' && (
        <div>
          <div className="smc-ops-section-toolbar">
            <div className="smc-ops-section-toolbar-title" style={{ fontFamily:'monospace', letterSpacing:2, color:'#10b981' }}>COMPANY FINANCIALS</div>
            <div className="smc-ops-section-toolbar-actions">
            <button onClick={() => startEdit('finances')}
              style={{ padding:'5px 14px', background:'#10b98120', border:'1px solid #10b981',
                color:'#10b981', fontFamily:'monospace', fontSize:9, cursor:'pointer', borderRadius:4, letterSpacing:1 }}>
              {editing==='finances' ? 'EDITING…' : '✎ UPDATE'}
            </button>
            </div>
          </div>
          {editing === 'finances' ? (
            <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:24, marginBottom:20 }}>
              {[
                ['period','Period (e.g. Q1 2025)'],['revenue','Revenue (₹)'],['expenses','Total Expenses (₹)'],
                ['netIncome','Net Income (₹)'],['runwayMonths','Runway (months)'],
                ['mrr','Monthly Recurring Revenue (₹)'],['arr','Annual Recurring Revenue (₹)'],['notes','Notes'],
              ].map(([k,label]) => (
                <div key={k}>
                  <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:1, marginBottom:3 }}>{label}</div>
                  <input value={draft[k]||''} onChange={e => setDraft(p=>({...p,[k]:e.target.value}))}
                    placeholder={label} style={inp} />
                </div>
              ))}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button onClick={saveEdit} disabled={saving}
                  style={{ padding:'8px 20px', background:'#10b98120', border:'1px solid #10b981',
                    color:'#10b981', fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  {saved ? 'SAVED ✓' : saving ? 'SAVING…' : 'SAVE'}
                </button>
                <button onClick={() => setEditing(null)}
                  style={{ padding:'8px 16px', background:'transparent', border:`1px solid ${C.border}`,
                    color:C.muted, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  CANCEL
                </button>
              </div>
            </div>
          ) : (
            <>
              {d.period && <div style={{ fontFamily:'monospace', fontSize:10, color:C.muted, marginBottom:16 }}>Period: {d.period}</div>}
              <div className="smc-stat-grid" style={{ marginBottom:20 }}>
                <StatCard label="REVENUE"    value={d.revenue   ? `₹${d.revenue}`   : null} color="#10b981" />
                <StatCard label="EXPENSES"   value={d.expenses  ? `₹${d.expenses}`  : null} color="#f43f5e" />
                <StatCard label="NET INCOME" value={d.netIncome ? `₹${d.netIncome}` : null} color="#10b981" />
                <StatCard label="RUNWAY"     value={d.runwayMonths} unit="months"             color="#f59e0b" />
                <StatCard label="MRR"        value={d.mrr ? `₹${d.mrr}` : null}              color="#06b6d4" />
                <StatCard label="ARR"        value={d.arr ? `₹${d.arr}` : null}              color="#6366f1" />
              </div>
              {d.notes && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 16px' }}>📝 {d.notes}</div>}
              {!d.revenue && <div style={{ textAlign:'center', padding:'40px 0', color:C.muted, fontFamily:'monospace', fontSize:11 }}>No financial data yet. Click ✎ UPDATE to enter data.</div>}
            </>
          )}
        </div>
      )}

      {/* ── TEAM ── */}
      {dash === 'team' && (
        <div>
          <div className="smc-ops-section-toolbar">
            <div className="smc-ops-section-toolbar-title" style={{ fontFamily:'monospace', letterSpacing:2, color:'#f59e0b' }}>TEAM & HR</div>
            <div className="smc-ops-section-toolbar-actions">
            <button onClick={() => startEdit('team')}
              style={{ padding:'5px 14px', background:'#f59e0b20', border:'1px solid #f59e0b',
                color:'#f59e0b', fontFamily:'monospace', fontSize:9, cursor:'pointer', borderRadius:4, letterSpacing:1 }}>
              {editing==='team' ? 'EDITING…' : '✎ UPDATE'}
            </button>
            </div>
          </div>
          {editing === 'team' ? (
            <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:24, marginBottom:20 }}>
              {[
                ['headcount','Total Headcount'],['fullTime','Full-Time'],['partTime','Part-Time / Interns'],
                ['openPositions','Open Positions'],['hiringFor','Hiring For (roles)'],
                ['avgSalary','Avg. Monthly Salary (₹)'],['totalPayroll','Total Monthly Payroll (₹)'],['notes','Notes'],
              ].map(([k,label]) => (
                <div key={k}>
                  <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:1, marginBottom:3 }}>{label}</div>
                  <input value={draft[k]||''} onChange={e => setDraft(p=>({...p,[k]:e.target.value}))}
                    placeholder={label} style={inp} />
                </div>
              ))}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button onClick={saveEdit} disabled={saving}
                  style={{ padding:'8px 20px', background:'#f59e0b20', border:'1px solid #f59e0b',
                    color:'#f59e0b', fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  {saved ? 'SAVED ✓' : saving ? 'SAVING…' : 'SAVE'}
                </button>
                <button onClick={() => setEditing(null)}
                  style={{ padding:'8px 16px', background:'transparent', border:`1px solid ${C.border}`,
                    color:C.muted, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  CANCEL
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="smc-stat-grid" style={{ marginBottom:20 }}>
                <StatCard label="HEADCOUNT"       value={d.headcount}                                 color="#f59e0b" />
                <StatCard label="FULL-TIME"        value={d.fullTime}                                  color="#f59e0b" />
                <StatCard label="PART-TIME"        value={d.partTime}                                  color="#fbbf24" />
                <StatCard label="OPEN POSITIONS"   value={d.openPositions}                             color="#ef4444" />
                <StatCard label="MONTHLY PAYROLL"  value={d.totalPayroll ? `₹${d.totalPayroll}` : null} color="#f59e0b" />
              </div>
              {d.hiringFor && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, marginBottom:10 }}>🔍 Hiring for: {d.hiringFor}</div>}
              {d.notes && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 16px' }}>📝 {d.notes}</div>}
              {!d.headcount && <div style={{ textAlign:'center', padding:'40px 0', color:C.muted, fontFamily:'monospace', fontSize:11 }}>No team data yet. Click ✎ UPDATE to enter data.</div>}
            </>
          )}
        </div>
      )}

      {/* ── DATA ROOM ── */}
      {dash === 'dataroom' && (
        <OpsCompanyDataRoom userName={userName} userId={userId} />
      )}

      {/* ── INVESTORS ── */}
      {dash === 'investors' && (
        <div>
          <div className="smc-ops-section-toolbar">
            <div className="smc-ops-section-toolbar-title" style={{ fontFamily:'monospace', letterSpacing:2, color:OPS_COLOR }}>INVESTOR DETAILS</div>
            <div className="smc-ops-section-toolbar-actions">
            <button onClick={() => startEdit('investors')}
              style={{ padding:'5px 14px', background:`${OPS_COLOR}20`, border:`1px solid ${OPS_COLOR}`,
                color:OPS_COLOR, fontFamily:'monospace', fontSize:9, cursor:'pointer', borderRadius:4, letterSpacing:1 }}>
              {editing==='investors' ? 'EDITING…' : '✎ UPDATE'}
            </button>
            </div>
          </div>
          {editing === 'investors' ? (
            <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:24, marginBottom:20 }}>
              {[
                ['totalRaised','Total Funding Raised (₹)'],['fundingStage','Funding Stage (e.g. Pre-Seed)'],
                ['lastRoundDate','Last Round Date'],['lastRoundAmount','Last Round Amount (₹)'],
                ['leadInvestor','Lead Investor'],['otherInvestors','Other Investors (comma-separated)'],
                ['equityDiluted','Equity Diluted (%)'],['valuation','Company Valuation (₹)'],['notes','Notes'],
              ].map(([k,label]) => (
                <div key={k}>
                  <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, letterSpacing:1, marginBottom:3 }}>{label}</div>
                  <input value={draft[k]||''} onChange={e => setDraft(p=>({...p,[k]:e.target.value}))}
                    placeholder={label} style={inp} />
                </div>
              ))}
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button onClick={saveEdit} disabled={saving}
                  style={{ padding:'8px 20px', background:`${OPS_COLOR}20`, border:`1px solid ${OPS_COLOR}`,
                    color:OPS_COLOR, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  {saved ? 'SAVED ✓' : saving ? 'SAVING…' : 'SAVE'}
                </button>
                <button onClick={() => setEditing(null)}
                  style={{ padding:'8px 16px', background:'transparent', border:`1px solid ${C.border}`,
                    color:C.muted, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6 }}>
                  CANCEL
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="smc-stat-grid" style={{ marginBottom:20 }}>
                <StatCard label="TOTAL RAISED"  value={d.totalRaised  ? `₹${d.totalRaised}`  : null} color={OPS_COLOR} />
                <StatCard label="VALUATION"      value={d.valuation    ? `₹${d.valuation}`    : null} color={OPS_COLOR} />
                <StatCard label="EQUITY DILUTED" value={d.equityDiluted ? `${d.equityDiluted}%` : null} color="#f43f5e" />
                <StatCard label="LAST ROUND"     value={d.lastRoundAmount ? `₹${d.lastRoundAmount}` : null} color="#fb923c" />
              </div>
              {d.fundingStage && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, marginBottom:6 }}>Stage: {d.fundingStage} · {d.lastRoundDate}</div>}
              {d.leadInvestor && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, marginBottom:6 }}>Lead: {d.leadInvestor}</div>}
              {d.otherInvestors && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, marginBottom:10 }}>Others: {d.otherInvestors}</div>}
              {d.notes && <div style={{ fontFamily:'monospace', fontSize:11, color:C.muted, background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 16px' }}>📝 {d.notes}</div>}
              {!d.totalRaised && <div style={{ textAlign:'center', padding:'40px 0', color:C.muted, fontFamily:'monospace', fontSize:11 }}>No investor data yet. Click ✎ UPDATE to enter data.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function OpsReminderSettings({ C }) {
  const [enabled,   setEnabled]   = useState(true);
  const [hours,     setHours]     = useState(24);
  const [saved,     setSaved]     = useState(false);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'app', 'reminder_settings')).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        setEnabled(d.enabled !== false);
        setHours(d.intervalHours || 24);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function save() {
    await setDoc(doc(db, 'app', 'reminder_settings'), { enabled, intervalHours: hours }, { merge: true });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    // Also request notification permission
    if (notifPermission() === 'default') requestNotifPermission().catch(() => {});
  }

  async function triggerNow() {
    // Force immediate notification
    if (notifPermission() !== 'granted') {
      await requestNotifPermission();
    }
    // Update Firestore to trigger all clients (they poll on load)
    await setDoc(doc(db, 'app', 'reminder_trigger'), { triggeredAt: serverTimestamp() }, { merge: true });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  if (loading) return null;

  const selectStyle = {
    background: C.panel, border:`1px solid ${C.border}`, color:C.text,
    padding:'8px 12px', fontFamily:'monospace', fontSize:11, borderRadius:6,
  };

  return (
    <div className="smc-ops-reminder-panel" style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, padding:20, maxWidth:420 }}>
      <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, cursor:'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        <span style={{ fontFamily:'monospace', fontSize:11, color:C.text }}>Enable daily task reminders</span>
      </label>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <span style={{ fontFamily:'monospace', fontSize:11, color:C.muted }}>Send every</span>
        <select value={hours} onChange={e => setHours(Number(e.target.value))} style={selectStyle}>
          {[1,2,4,6,8,12,24,48].map(h => <option key={h} value={h}>{h}h</option>)}
        </select>
      </div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        <button onClick={save}
          style={{ padding:'8px 18px', background:`${OPS_COLOR}20`, border:`1px solid ${OPS_COLOR}`,
            color:OPS_COLOR, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6, letterSpacing:1 }}>
          {saved ? 'SAVED ✓' : 'SAVE SETTINGS'}
        </button>
        <button onClick={triggerNow}
          style={{ padding:'8px 18px', background:'transparent', border:`1px solid ${C.border}`,
            color:C.muted, fontFamily:'monospace', fontSize:10, cursor:'pointer', borderRadius:6, letterSpacing:1 }}>
          SEND REMINDER NOW
        </button>
      </div>
    </div>
  );
}

function QuickNotesPanel({ userId, userName, onClose }) {
  const C = useTheme();
  const [tab,      setTab]      = useState('notes');
  const [notes,    setNotes]    = useState('');
  const [saved,    setSaved]    = useState(true);
  const [fbText,   setFbText]   = useState('');
  const [fbCat,    setFbCat]    = useState('general');
  const [sending,  setSending]  = useState(false);
  const [fbSent,   setFbSent]   = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'ops_notes', userId), snap => {
      if (snap.exists()) setNotes(snap.data().text || '');
    }, () => {});
    return unsub;
  }, [userId]);

  function handleNotesChange(val) {
    setNotes(val);
    setSaved(false);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'ops_notes', userId), { text: val, updatedAt: serverTimestamp(), updatedBy: userName });
        setSaved(true);
      } catch(_) {}
    }, 1200);
  }

  async function sendFeedback(e) {
    e.preventDefault();
    if (!fbText.trim()) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'feedback'), {
        text: fbText.trim(), category: fbCat,
        submittedBy: userName, submittedByUid: userId,
        submittedAt: serverTimestamp(), status: 'new',
      });
      setFbSent(true);
      setFbText('');
      setTimeout(() => setFbSent(false), 3000);
    } catch(_) {}
    setSending(false);
  }

  return (
    <div className="smc-ops-notes-panel" style={{ position:'fixed', top:0, right:0, bottom:0, zIndex:200,
      background: C.panel, borderLeft:`1px solid ${C.border}`,
      display:'flex', flexDirection:'column', boxShadow:'-4px 0 24px #00000040' }}>
      {/* Header */}
      <div style={{ padding:'14px 16px', borderBottom:`1px solid ${C.border}`,
        display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontFamily:'monospace', fontSize:10, color:OPS_COLOR, letterSpacing:2, fontWeight:700, flex:1 }}>📝 QUICK NOTES</span>
        <button onClick={onClose}
          style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:18 }}>×</button>
      </div>
      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}` }}>
        {[['notes','NOTES'],['feedback','FEEDBACK']].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ flex:1, padding:'10px 0', fontFamily:'monospace', fontSize:9, letterSpacing:1,
              background:'transparent', border:'none', cursor:'pointer',
              borderBottom:`2px solid ${tab===v ? OPS_COLOR : 'transparent'}`,
              color: tab===v ? OPS_COLOR : C.muted }}>
            {l}
          </button>
        ))}
      </div>
      {/* Content */}
      {tab === 'notes' && (
        <div style={{ flex:1, display:'flex', flexDirection:'column', padding:14 }}>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:6 }}>
            <span style={{ fontFamily:'monospace', fontSize:8, color: saved ? C.muted : OPS_COLOR }}>
              {saved ? 'AUTO-SAVED' : 'SAVING…'}
            </span>
          </div>
          <textarea value={notes} onChange={e => handleNotesChange(e.target.value)}
            placeholder="Your notes…"
            style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`,
              color:C.text, fontFamily:'monospace', fontSize:12, padding:12,
              resize:'none', outline:'none', borderRadius:8, lineHeight:1.6 }} />
        </div>
      )}
      {tab === 'feedback' && (
        <div style={{ flex:1, overflowY:'auto', padding:14 }}>
          <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, marginBottom:14, lineHeight:1.6 }}>
            Share feedback about reports, progress, the website or any suggestions.
          </div>
          {fbSent && (
            <div style={{ padding:'10px 14px', background:'#10b98118', border:'1px solid #10b98144',
              borderRadius:8, fontFamily:'monospace', fontSize:10, color:'#10b981', marginBottom:12 }}>
              ✓ Feedback sent! Thank you.
            </div>
          )}
          <form onSubmit={sendFeedback}>
            <div style={{ marginBottom:10 }}>
              <div style={{ fontFamily:'monospace', fontSize:8, color:C.muted, letterSpacing:1, marginBottom:6 }}>CATEGORY</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {[['general','General'],['progress','Progress'],['website','Website'],['report','Reports']].map(([v,l]) => (
                  <button key={v} type="button" onClick={() => setFbCat(v)}
                    style={{ padding:'4px 10px', fontFamily:'monospace', fontSize:8,
                      background: fbCat===v ? `${OPS_COLOR}20` : 'transparent',
                      border:`1px solid ${fbCat===v ? OPS_COLOR : C.border}`,
                      color: fbCat===v ? OPS_COLOR : C.muted, cursor:'pointer', borderRadius:4 }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <textarea value={fbText} onChange={e => setFbText(e.target.value)}
              placeholder="Write your feedback…" rows={6}
              style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                color:C.text, fontFamily:'monospace', fontSize:12, padding:10,
                resize:'vertical', outline:'none', borderRadius:8, boxSizing:'border-box', marginBottom:10 }} />
            <button type="submit" disabled={sending || !fbText.trim()}
              style={{ width:'100%', padding:'10px 0', fontFamily:'monospace', fontSize:10, letterSpacing:1,
                background:`${OPS_COLOR}18`, border:`1px solid ${OPS_COLOR}`,
                color:OPS_COLOR, cursor:sending?'wait':'pointer', borderRadius:6 }}>
              {sending ? 'SENDING…' : 'SEND FEEDBACK →'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function OpsView({ userName, userId, themeId, onThemeChange, onSignOut, onSignOutAll, appVersion, appVersionDate, notifications = [], onMarkRead, onMarkOneRead, portals, onChoosePortal }) {
  const C = useTheme();
  const designV2 = isV2('shell');
  // Tab lives in the URL (/app/<tab>); legacy ?tab=attendance links still land on it
  const [tab,            setTab]            = useRoutedTab('reports', OPS_TAB_IDS, useState('reports'));
  const sectionBadges = useSectionBadges({ userId, role: 'ops', activeTab: tab, scope: 'ops', alias: { tasks: 'mytasks' } });
  const [allPosts,       setAllPosts]       = useState([]);
  const [tasks,          setTasks]          = useState([]);
  const [incomingCall,   setIncomingCall]   = useState(null);
  const [overviewFilter, setOverviewFilter] = useState(null);
  const [showNotes,      setShowNotes]      = useState(false);

  const handleIncomingCall = useCallback((call) => setIncomingCall(call), []);
  useCallListener(userId, handleIncomingCall);

  useEffect(() => {
    const bucket = {};
    const unsubs = OPS_PORTALS.map(p => {
      const q = query(collection(db, p.col), orderBy("uploadedAt","desc"));
      return onSnapshot(q, snap => {
        bucket[p.id] = snap.docs.map(d => ({ id:d.id, ...d.data(), _portal:p }));
        setAllPosts(Object.values(bucket).flat().sort((a,b) => {
          const ta = a.uploadedAt?.toDate?.() || new Date(a.uploadedAt||0);
          const tb = b.uploadedAt?.toDate?.() || new Date(b.uploadedAt||0);
          return tb - ta;
        }));
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, []);

  useEffect(() => {
    const q = query(collection(db, "tasks"), orderBy("createdAt","desc"));
    return onSnapshot(q, snap => setTasks(snap.docs.map(d => ({ id:d.id,...d.data() }))), () => {});
  }, []);

  const pendingTasks    = tasks.filter(t => t.status === "pending").length;
  const approvedReports = allPosts.filter(p => p.adminLiked).length;

  const TABS = [
    ["overview",  "🏛  OVERVIEW" ],
    ["daily-log", "📝  MISSION LOG"],
    ["reports",   "📡  REPORTS"  ],
    ["tasks",     "📋  TASKS"    ],
    ["mytasks",   "📅  MY TASKS" ],
    ["mr1",       "🚀  MR-1"     ],
    ["my-day",    "🗓  MY DAY"   ],
    ["calendar",  "📅  CALENDAR" ],
    ["meetings",  "📹  MEETINGS" ],
    ["company",   "📊  COMPANY"  ],
    ["attendance","📅  ATTENDANCE"],
    ["interns",   "🎓  INTERNS"  ],
    ["mail",      "✉  MAIL"     ],
    ["chat",      "💬  CHAT"     ],
  ];

  return (
    <div className="smc-ops-dashboard" style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:C.font }}>
      <Stars visible={C.dark} />

      {/* Incoming call overlay */}
      {incomingCall && (
        <IncomingCallOverlay
          call={incomingCall}
          onDecline={async () => {
            try { await updateDoc(doc(db,'calls',incomingCall.id), { status:'declined' }); } catch(_) {}
            setIncomingCall(null);
          }}
          onAccept={() => { setTab('meetings'); setIncomingCall(null); }}
        />
      )}
      <TaskReminderService userId={userId} role="ops" />

      {/* Header */}
      {designV2 ? (
        <ShellHeaderV2
          label="🏛 OPERATIONS OFFICER"
          accent={OPS_COLOR}
          tabs={TABS}
          dots={sectionBadges}
          activeTab={tab}
          onTab={setTab}
          onLogo={() => setTab('overview')}
          actions={<>
            {portals && onChoosePortal && <PortalSwitcher portals={portals} activeId="ops" onChoose={onChoosePortal} />}
            <span className="smc2-chip smc-hide-tablet" title={`Build v${APP_VERSION}`}>v{APP_VERSION}</span>
            <button type="button" onClick={() => setShowNotes(s => !s)} title="Quick Notes"
              className={`smc2-icon-btn${showNotes ? ' is-active' : ''}`} aria-label="Quick notes">📝</button>
            <NotificationBell notifications={notifications} onMarkRead={onMarkRead} onMarkOneRead={onMarkOneRead} role="ops" userId={userId} />
            <ThemeDropdown themeId={themeId} onChange={onThemeChange} />
            <span className="smc2-hud smc2-hide-sm" style={{ color:'var(--smc-tx2)', letterSpacing:'0.1em' }}>{userName}</span>
            <SignOutMenu onSignOut={onSignOut} onSignOutAll={onSignOutAll} />
          </>}
        />
      ) : (
      <div className="smc-portal-header" style={{ position:"sticky", top:0, zIndex:90, background:`${C.bg}f2`,
        backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
        borderBottom:`1px solid ${C.border}`,
        display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
        <div style={{ background:C.dark?"#000":"transparent", borderRadius:C.dark?8:0,
          padding:C.dark?"3px 8px":"0", display:"flex", alignItems:"center",
          border:C.dark?"1px solid #1e2d4540":"none", flexShrink:0 }}>
          <img src={C.dark ? "/logo-dark.png" : "/logo-horizontal.jpg"} alt="Spacemancraft"
            style={{ height:C.dark?32:26, width:"auto", objectFit:"contain", display:"block" }} />
        </div>
        <span className="smc-portal-label" style={{ fontFamily:"monospace", fontSize:10, color:OPS_COLOR, letterSpacing:2, fontWeight:700 }}>
          🏛 OPERATIONS OFFICER
        </span>
        <div style={{ flex:1 }} />
        <div className="smc-portal-header-end" style={{ display:'flex', alignItems:'center', gap:10 }}>
        {portals && onChoosePortal && <PortalSwitcher portals={portals} activeId="ops" onChoose={onChoosePortal} />}
        <ThemePicker themeId={themeId} onChange={onThemeChange} inline />
        <span className="smc-hide-tablet" style={{ fontFamily:"monospace", fontSize:9, color:C.muted,
          border:`1px solid ${C.border}`, padding:"2px 7px", borderRadius:4,
          userSelect:"none" }} title={`Build v${APP_VERSION}`}>
          v{APP_VERSION}
        </span>
        <span className="smc-user-name" style={{ fontFamily:"monospace", fontSize:10, color:C.muted }}>👤 {userName}</span>
        <NotificationBell notifications={notifications} onMarkRead={onMarkRead} onMarkOneRead={onMarkOneRead} role="ops" userId={userId} />
        <SignOutMenu onSignOut={onSignOut} onSignOutAll={onSignOutAll} />
        <button onClick={() => setShowNotes(s => !s)}
          title="Quick Notes"
          style={{ fontFamily:'monospace', fontSize:8, letterSpacing:1, padding:'5px 10px',
            background: showNotes ? `${OPS_COLOR}20` : 'transparent',
            border:`1px solid ${showNotes ? OPS_COLOR : C.border}`,
            color: showNotes ? OPS_COLOR : C.muted, cursor:'pointer', borderRadius:4 }}>
          📝 NOTES
        </button>
        </div>
      </div>
      )}

      <AnnouncementBanner userId={userId} userRole="ops" />

      {/* Hero (classic) / compact page title (v2) */}
      {designV2 ? (
        <div className="smc2-page-title" style={{ maxWidth:1280, margin:'0 auto', padding:'20px 16px 0' }}>
          <div>
            <div className="smc2-hud" style={{ color:OPS_COLOR }}>SPACEMANCRAFT — OPERATIONS</div>
            <h1>Operations Dashboard</h1>
          </div>
          <span className="smc2-hud" style={{ color:'var(--smc-tx3)', fontSize:9 }}>
            Cross-department oversight · Tasks · Calendar
          </span>
        </div>
      ) : (
      <div className="smc-hero" style={{ textAlign:"center", position:"relative" }}>
        <div className="smc-hero-eyebrow" style={{ fontFamily:"monospace", fontSize:10, color:`${OPS_COLOR}88`, letterSpacing:6, marginBottom:14 }}>
          SPACEMANCRAFT — OPERATIONS
        </div>
        <div className="smc-hero-icon" style={{ fontSize:48, marginBottom:10 }}>🏛</div>
        <h1 style={{ fontSize:38, fontWeight:900, margin:"0 0 10px", lineHeight:1,
          background:`linear-gradient(135deg, ${C.bright} 0%, ${OPS_COLOR} 100%)`,
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", letterSpacing:-1 }}>
          Operations Dashboard
        </h1>
        <p style={{ fontFamily:"monospace", fontSize:11, color:C.muted, letterSpacing:1, maxWidth:520, margin:"0 auto" }}>
          Cross-department oversight · Task management · Calendar & scheduling
        </p>
      </div>
      )}

      {/* Tabs (classic only — v2 tabs live in the header) */}
      {!designV2 && (
      <div className="smc-tab-bar smc-tab-bar-sticky smc-ops-tab-bar" style={{ borderBottom:`1px solid ${C.border}` }}>
        {TABS.map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            style={{ padding:"13px 22px", fontFamily:"monospace", fontSize:10, letterSpacing:1,
              cursor:"pointer", background:"transparent", border:"none",
              borderBottom:`2px solid ${tab===v ? OPS_COLOR : "transparent"}`,
              color: tab===v ? OPS_COLOR : C.muted, transition:"color 0.15s", whiteSpace:"nowrap" }}>
            {l}
          </button>
        ))}
      </div>
      )}

      {/* Content */}
      <div className="smc-page-container-md smc-ops-content">

        {/* Check-in prompt — ops members otherwise only check in from the Attendance tab */}
        <CheckInStrip userId={userId} userName={userName} role="ops"
          onNavigateToAttendance={() => setTab('attendance')} />
        {tab === "overview" && (
          <div>
            {designV2 ? (
              <div className="smc2-stats">
                <V2Stat label="TOTAL REPORTS"    value={allPosts.length} color="var(--smc-pu)" onClick={() => setOverviewFilter('all')} />
                <V2Stat label="APPROVED REPORTS" value={approvedReports} color="var(--smc-gn)" onClick={() => setOverviewFilter('approved')} />
                <V2Stat label="PENDING TASKS"    value={pendingTasks}    color="var(--smc-am)" onClick={() => setOverviewFilter('tasks')} />
                <V2Stat label="DEPARTMENTS"      value={OPS_PORTALS.length} color={OPS_COLOR} onClick={() => setOverviewFilter('departments')} />
              </div>
            ) : (
            <div className="smc-stat-grid smc-ops-overview-stats" style={{ marginBottom:36 }}>
              {[
                { label:"Total Reports",    value:allPosts.length,  color:"#6366f1", icon:"📋", filterKey:"all" },
                { label:"Approved Reports", value:approvedReports,  color:"#10b981", icon:"✅", filterKey:"approved" },
                { label:"Pending Tasks",    value:pendingTasks,     color:"#f59e0b", icon:"⏳", filterKey:"tasks" },
                { label:"Departments",      value:4,                color:OPS_COLOR,  icon:"🏛", filterKey:"departments" },
              ].map(stat => (
                <div key={stat.label} onClick={() => setOverviewFilter(stat.filterKey)}
                  style={{ background:C.panel, border:`1px solid ${overviewFilter===stat.filterKey ? stat.color : C.border}`,
                    borderRadius:12, padding:"20px 18px", borderTop:`3px solid ${stat.color}`,
                    cursor:'pointer', transition:'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: overviewFilter===stat.filterKey ? `0 0 0 2px ${stat.color}33` : 'none' }}>
                  <div className="smc-ops-stat-icon" style={{ fontSize:28, marginBottom:6 }}>{stat.icon}</div>
                  <div className="smc-ops-stat-value" style={{ fontSize:28, fontWeight:900, color:stat.color, fontFamily:"monospace" }}>{stat.value}</div>
                  <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, letterSpacing:1, marginTop:4 }}>{stat.label}</div>
                </div>
              ))}
            </div>
            )}
            <div style={{ fontFamily:"monospace", fontSize:10, letterSpacing:2, color:OPS_COLOR,
              borderBottom:`2px solid ${OPS_COLOR}44`, paddingBottom:8, marginBottom:16 }}>
              DEPARTMENT OVERVIEW
            </div>
            {designV2 ? (
              <div className="smc2-portalgrid">
                {OPS_PORTALS.map(p => {
                  const depts = allPosts.filter(x => x._portal.id === p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => setOverviewFilter(p.id)}
                      className="smc2-portalcard" style={{ '--c': p.color, textAlign:'left', cursor:'pointer', font:'inherit',
                        borderColor: overviewFilter === p.id ? p.color : undefined }}>
                      <span style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                        <span className="smc2-hud" style={{ color:p.color }}>{portalCode(p.id)}</span>
                        <V2RecordId>{depts.length} RPT</V2RecordId>
                      </span>
                      <span style={{ display:'block', marginTop:8, fontSize:14, fontWeight:500, color:'var(--smc-tx)' }}>{p.label}</span>
                      <span className="smc2-hud" style={{ display:'block', marginTop:10, fontSize:9, letterSpacing:'0.12em', color:'var(--smc-tx3)' }}>
                        {depts.filter(x=>x.adminLiked).length} APPROVED · {depts.filter(x=>x.reportStatus==='completed').length} COMPLETED
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
            <div className="smc-card-grid smc-ops-dept-grid">
              {OPS_PORTALS.map(p => {
                const depts = allPosts.filter(x => x._portal.id === p.id);
                return (
                  <div key={p.id} onClick={() => setOverviewFilter(p.id)}
                    style={{ background:C.panel, border:`1px solid ${overviewFilter===p.id ? p.color : C.border}`,
                      borderRadius:12, padding:18, borderLeft:`4px solid ${p.color}`,
                      cursor:'pointer', transition:'border-color 0.15s',
                      boxShadow: overviewFilter===p.id ? `0 0 0 2px ${p.color}33` : 'none' }}>
                    <div style={{ fontFamily:"monospace", fontSize:11, fontWeight:700, color:p.color, marginBottom:10 }}>
                      {p.icon} {p.label}
                    </div>
                    <div className="smc-ops-dept-count" style={{ fontSize:22, fontWeight:900, color:C.bright, fontFamily:"monospace" }}>{depts.length}</div>
                    <div style={{ fontFamily:"monospace", fontSize:10, color:C.muted, marginTop:2 }}>
                      reports · {depts.filter(x=>x.adminLiked).length} approved · {depts.filter(x=>x.reportStatus==='completed').length} completed
                    </div>
                  </div>
                );
              })}
            </div>
            )}
            {overviewFilter && overviewFilter !== 'departments' && (
              <div style={{ marginTop:24, background:C.panel, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
                <div className="smc-ops-overview-filter-header" style={{ borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ fontFamily:'monospace', fontSize:10, letterSpacing:2, color:OPS_COLOR, flex:1 }}>
                    {overviewFilter === 'all' ? 'ALL REPORTS'
                      : overviewFilter === 'approved' ? 'APPROVED REPORTS'
                      : overviewFilter === 'tasks' ? 'PENDING TASKS'
                      : `${OPS_PORTALS.find(p=>p.id===overviewFilter)?.label || ''} REPORTS`}
                  </div>
                  <button onClick={() => setOverviewFilter(null)}
                    style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:18 }}>×</button>
                </div>
                <div style={{ padding:'12px 18px', maxHeight:400, overflowY:'auto' }}>
                  {overviewFilter === 'tasks' ? (
                    tasks.filter(t => t.status === 'pending').length === 0 ? (
                      <div style={{ textAlign:'center', padding:'24px 0', fontFamily:'monospace', fontSize:11, color:C.muted }}>No pending tasks</div>
                    ) : tasks.filter(t => t.status === 'pending').map(task => {
                      const portalObj = OPS_PORTALS.find(p => p.id === task.portal);
                      return (
                        <div key={task.id} style={{ padding:'10px 12px', marginBottom:8, borderRadius:8,
                          background: C.bg, border:`1px solid ${C.border}`, borderLeft:'3px solid #f59e0b' }}>
                          <div style={{ fontWeight:600, color:C.bright, fontSize:13, marginBottom:4 }}>{task.title}</div>
                          {portalObj && <span style={{ fontFamily:'monospace', fontSize:8, color:portalObj.color }}>{portalObj.icon} {portalObj.label}</span>}
                          {task.description && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{task.description}</div>}
                          <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted, marginTop:4 }}>by {task.createdBy}</div>
                        </div>
                      );
                    })
                  ) : (() => {
                    const filtered = overviewFilter === 'all' ? allPosts
                      : overviewFilter === 'approved' ? allPosts.filter(p => p.adminLiked)
                      : allPosts.filter(p => p._portal.id === overviewFilter);
                    return filtered.length === 0 ? (
                      <div style={{ textAlign:'center', padding:'24px 0', fontFamily:'monospace', fontSize:11, color:C.muted }}>No reports found</div>
                    ) : filtered.map(post => {
                      const p = post._portal;
                      return (
                        <div key={post.id} style={{ padding:'10px 12px', marginBottom:8, borderRadius:8,
                          background: C.bg, border:`1px solid ${C.border}`, borderLeft:`3px solid ${p.color}` }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                            <span style={{ fontFamily:'monospace', fontSize:8, color:p.color }}>{p.icon} {p.label}</span>
                            {post.reportStatus && post.reportStatus !== 'active' && (
                              <span style={{ fontFamily:'monospace', fontSize:8,
                                color: post.reportStatus==='completed'?'#6366f1':post.reportStatus==='processing'?'#f59e0b':'#10b981',
                                background: post.reportStatus==='completed'?'#6366f118':post.reportStatus==='processing'?'#f59e0b18':'#10b98118',
                                border:`1px solid ${post.reportStatus==='completed'?'#6366f133':post.reportStatus==='processing'?'#f59e0b33':'#10b98133'}`,
                                padding:'1px 6px', borderRadius:4 }}>
                                {post.reportStatus === 'completed' ? '✔ COMPLETED' : post.reportStatus === 'processing' ? '⚙ PROCESSING' : '✓ VALIDATED'}
                              </span>
                            )}
                            {post.adminLiked && <span style={{ fontFamily:'monospace', fontSize:8, color:'#f59e0b' }}>★ APPROVED</span>}
                          </div>
                          <div style={{ fontWeight:600, color:C.bright, fontSize:13, marginBottom:2 }}>{post.title}</div>
                          <div style={{ fontFamily:'monospace', fontSize:9, color:C.muted }}>
                            by {post.uploadedBy} · {post.uploadedAt?.toDate?.().toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})||''}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Task reminder settings */}
            <div style={{ marginTop:32 }}>
              <div style={{ fontFamily:"monospace", fontSize:10, letterSpacing:2, color:OPS_COLOR,
                borderBottom:`2px solid ${OPS_COLOR}44`, paddingBottom:8, marginBottom:16 }}>
                TASK REMINDER SETTINGS
              </div>
              <OpsReminderSettings C={C} />
            </div>
          </div>
        )}

        {tab === "daily-log" && (
          <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
            <DailyLogPage userId={userId} userName={userName} userRole="ops" />
          </Suspense>
        )}
        {tab === "reports"  && <OpsFeed           userName={userName} userId={userId} />}
        {tab === "tasks"    && <OpsTaskManager   userName={userName} userId={userId} />}
        {tab === "mytasks"  && <DailyTasksPage   user={{ uid: userId, name: userName }} />}
        {tab === "mr1"      && (
          <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
            <MR1TrackerPage userId={userId} userName={userName} role="ops" />
          </Suspense>
        )}
        {tab === "my-day"   && (
          <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
            <MyDayPage userId={userId} userName={userName} role="ops" onNavigate={setTab} />
          </Suspense>
        )}
        {tab === "calendar" && <OpsCalendar      userName={userName} />}
        {tab === "meetings" && <MeetingsPage     user={{ uid: userId, name: userName }} />}
        {tab === "company"  && <OpsCompanyDashboard userName={userName} userId={userId} />}
        {tab === "interns"  && (
          <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
            <InternsHub userId={userId} userName={userName} role="ops" />
          </Suspense>
        )}
        {tab === "attendance" && (
          <Suspense fallback={<div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:C.muted }}>LOADING…</div>}>
            <AttendancePage role="ops" userId={userId} userName={userName} />
          </Suspense>
        )}
        {tab === "mail"     && <MailboxPage       user={{ uid: userId, name: userName }} />}
        {tab === "chat"     && <ChatPage          user={{ uid: userId, name: userName, role: "ops" }} />}
      </div>
      {showNotes && <QuickNotesPanel userId={userId} userName={userName} onClose={() => setShowNotes(false)} />}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
//  TASK REMINDER SERVICE — browser notifications for daily task reminders
// ══════════════════════════════════════════════════════════════════════════════
function TaskReminderService({ userId, role }) {
  useEffect(() => {
    if (!userId) return;
    if (notifPermission() === 'default') {
      requestNotifPermission().catch(() => {});
    }

    let timer = null;

    async function loadAndNotify() {
      try {
        if (notifPermission() !== 'granted') return;
        const settingsSnap = await getDoc(doc(db, 'app', 'reminder_settings'));
        const settings = settingsSnap.exists() ? settingsSnap.data() : {};
        if (settings.enabled === false) return;

        const snap = await getDocs(query(
          collection(db, 'tasks'),
          where('status', 'in', ['pending', 'in_progress'])
        ));
        const tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const mine = (role === 'admin' || role === 'ops')
          ? tasks
          : tasks.filter(t =>
              (t.assigneeIds || []).includes(userId) ||
              t.assigneeId === userId ||
              t.assignedTo === userId
            );

        if (mine.length === 0) return;
        const body = mine.slice(0, 5).map(t => `• ${t.title || t.id}`).join('\n')
          + (mine.length > 5 ? `\n…and ${mine.length - 5} more` : '');
        new Notification(`📋 You have ${mine.length} pending task${mine.length > 1 ? 's' : ''}`, {
          body, icon: '/logo192.png', tag: 'task-reminder',
        });
      } catch (_) {}
    }

    async function scheduleReminder() {
      try {
        const settingsSnap = await getDoc(doc(db, 'app', 'reminder_settings'));
        const intervalHours = settingsSnap.exists() ? (settingsSnap.data().intervalHours || 24) : 24;
        loadAndNotify();
        timer = setInterval(loadAndNotify, intervalHours * 3600 * 1000);
      } catch (_) {
        loadAndNotify();
        timer = setInterval(loadAndNotify, 24 * 3600 * 1000);
      }
    }

    scheduleReminder();

    // Also listen for ops-triggered immediate reminders
    const unsubTrigger = onSnapshot(doc(db, 'app', 'reminder_trigger'), snap => {
      if (snap.exists()) loadAndNotify();
    }, () => {});

    return () => { if (timer) clearInterval(timer); unsubTrigger(); };
  }, [userId, role]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ═══════════════════════════════════════════════════════════
//  ROOT — React Router v6
// ═══════════════════════════════════════════════════════════
// ── Scroll position restoration on back navigation ───────────────────────────
const scrollPositions = new Map();
function ScrollRestorer() {
  const location = useLocation();
  useEffect(() => {
    const mainEl = document.getElementById('main-content');
    const saved  = scrollPositions.get(location.pathname) || 0;
    if (mainEl) requestAnimationFrame(() => { mainEl.scrollTop = saved; });
    return () => {
      const el = document.getElementById('main-content');
      if (el) scrollPositions.set(location.pathname, el.scrollTop);
    };
  }, [location.pathname]);
  return null;
}

// ── Dev-only cache status badge ───────────────────────────────────────────────
function CacheDevBadge() {
  const [size, setSize] = useState(0);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const id = setInterval(() => setSize(SessionCache.memSize()), 2000);
    return () => clearInterval(id);
  }, []);
  if (process.env.NODE_ENV !== 'development') return null;
  return (
    <div style={{
      position: 'fixed', bottom: 8, left: 8, zIndex: 9999,
      background: 'rgba(0,0,0,0.75)', color: '#0e9cc0',
      fontFamily: 'monospace', fontSize: 10,
      padding: '3px 7px', borderRadius: 4, pointerEvents: 'none',
      border: '0.5px solid rgba(14,156,192,0.35)',
    }}>
      CACHE · {size} entries
    </div>
  );
}

// ── PortalThemeProvider ───────────────────────────────────────────────────────
// Provides ThemeContext to all routes (including portals rendered outside MainApp).
// Reads the initial theme from localStorage and updates when the main app changes it.
// When MainApp renders (/*), its own inner ThemeContext.Provider takes precedence.
function safeLocalStorage(key, fallback = null) {
  try { return localStorage.getItem(key); } catch (_) { return fallback; }
}
function safeLocalStorageSet(key, val) {
  try { localStorage.setItem(key, val); } catch (_) {}
}
function safeLocalStorageRemove(key) {
  try { localStorage.removeItem(key); } catch (_) {}
}

function PortalThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => getTheme(safeLocalStorage('smc_theme') || DEFAULT_THEME_ID));

  useEffect(() => {
    // Same-tab: dispatched by MainApp.handleThemeChange
    const onSameTab = (e) => setTheme(getTheme(e.detail || DEFAULT_THEME_ID));
    // Cross-tab: standard storage event
    const onStorage  = (e) => { if (e.key === 'smc_theme') setTheme(getTheme(e.newValue || DEFAULT_THEME_ID)); };
    window.addEventListener('smc-theme-change', onSameTab);
    window.addEventListener('storage',          onStorage);
    return () => {
      window.removeEventListener('smc-theme-change', onSameTab);
      window.removeEventListener('storage',          onStorage);
    };
  }, []);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

export default function App() {
  return (
    <ErrorBoundary>
    <PortalThemeProvider>
    <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--smc-bg)' }} />}>
      <SpaLinkInterceptor />
      <TaskHost />
      <ScrollRestorer />
      <CacheDevBadge />
      <Routes>
        {/* PUBLIC — no auth required */}
        <Route path="/track/:id"             element={<Suspense fallback={null}><TrackRedirect /></Suspense>} />
        <Route path="/r/*"                   element={<Suspense fallback={null}><GuestReportViewer /></Suspense>} />
        <Route path="/dept/:id"              element={<DynamicPortal />} />
        <Route path="/c_suite/*"             element={<DynamicPortal portalId="c_suite" />} />
        <Route path="/payload/*"             element={<DynamicPortal portalId="payload" />} />
        <Route path="/embedded_system/*"     element={<EmbeddedSystemPortal />} />
        <Route path="/system_engineering/*"  element={<SystemEngineeringPortal />} />
        <Route path="/mission_planning/*"    element={<MissionPlanningPortal />} />
        <Route path="/obc_development/*"     element={<OBCDevelopmentPage />} />
        <Route path="/operations/*"          element={<OperationsPortal />} />
        <Route path="/prompt-engineering/*"  element={<PromptEngineeringPortal />} />
        <Route path="/experimental_results/:expId" element={<Suspense fallback={null}><ExperimentWorkspace /></Suspense>} />
        <Route path="/experimental_results/*" element={<ExperimentalResultsPortal />} />
        <Route path="/interns/*"             element={<InternsPortal />} />
        {/* /team/* aliases → canonical routes */}
        <Route path="/team/embedded/*"      element={<Navigate to="/embedded_system" replace />} />
        <Route path="/team/systems/*"       element={<Navigate to="/system_engineering" replace />} />
        <Route path="/team/mission-planning/*" element={<Navigate to="/mission_planning" replace />} />
        <Route path="/team/obc/*"           element={<Navigate to="/obc_development" replace />} />
        <Route path="/team/operations/*"    element={<Navigate to="/operations" replace />} />
        <Route path="/team/pe/*"            element={<Navigate to="/prompt-engineering" replace />} />
        <Route path="/team/experimental/*"  element={<Navigate to="/experimental_results" replace />} />
        <Route path="/team/finance/*"       element={<Navigate to="/finance_admin" replace />} />
        <Route path="/finance_admin/*"       element={<FinanceAdminPortal />} />
        <Route path="/mentor_dashboard/*"    element={<Suspense fallback={null}><MentorDashboard /></Suspense>} />
        <Route path="/mr1"                   element={<Suspense fallback={null}><MR1TrackerPublicView /></Suspense>} />
        <Route path="/mission_timeline/*"   element={<Suspense fallback={null}><MissionTimeline /></Suspense>} />
        <Route path="/daily-log/compile"    element={<Suspense fallback={null}><ReportCompilerPage /></Suspense>} />
        <Route path="/daily-log/reports"    element={<Suspense fallback={null}><ReportsArchivePage /></Suspense>} />
        <Route path="/daily-updates"        element={<Suspense fallback={null}><DailyUpdatesPage /></Suspense>} />
        {DesignPreview && <Route path="/__design-preview" element={<Suspense fallback={null}><DesignPreview /></Suspense>} />}
        <Route path="/*"                     element={<MainApp />} />
      </Routes>
    </Suspense>
    </PortalThemeProvider>
    </ErrorBoundary>
  );
}

// ── Notification Bell ─────────────────────────────────────────────────────────
// ── Portal ID → SPA route mapping (mirrors functions/notificationRouter.js) ──
const NOTIF_PORTAL_ROUTES = {
  system:       '/system_engineering',
  ops:          '/operations',
  c_suite:      '/c_suite',
  payload:      '/payload',
  mp:           '/mission_planning',
  embedded:     '/embedded_system',
  obc:          '/obc_development',
  pe:           '/prompt-engineering',
  experimental: '/experimental_results',
  finance:      '/finance_admin',
};
// Bare tab ids written by older notifications (link: 'tasks', 'chat', …)
const NOTIF_TAB_ALIASES = { tasks: 'my-day', main: 'home' };
function resolveNotifRoute(raw) {
  if (!raw || raw === '/') return null;
  if (raw.startsWith('?')) return `/${raw}`;               // e.g. ?task=<id> — opens over the home page
  if (!raw.startsWith('/') && !/^[a-z]+:/i.test(raw)) {
    return tabPath(NOTIF_TAB_ALIASES[raw] || raw);
  }
  // Legacy /{portalId} patterns → correct route
  for (const [id, route] of Object.entries(NOTIF_PORTAL_ROUTES)) {
    if (raw === `/${id}` || raw.startsWith(`/${id}/`) || raw.startsWith(`/${id}?`)) {
      return raw.replace(`/${id}`, route);
    }
  }
  // /admin/* is a virtual route — handled specially; don't navigate there
  if (raw.startsWith('/admin')) return null;
  return raw;
}

function notifIcon(type) {
  if (type === 'ACCESS_REQUEST_SUBMITTED') return '👤';
  if (type === 'share_invite')             return '↗';
  if (type === 'PORTAL_REPORT_APPROVED')   return '✓';
  if (type === 'PORTAL_REPORT_RETURNED')   return '↩';
  if (type === 'PORTAL_REPORT_POSTED')     return '📋';
  if (type === 'TASK_ASSIGNED' || type === 'TASK_DUE_TOMORROW') return '📌';
  if (type === 'TASK_UPDATED')             return '🔄';
  if (type === 'TASK_REVIEW_REQUESTED')    return '⏳';
  if (type === 'TASK_APPROVED' || type === 'SUBTASK_COMPLETED') return '✅';
  if (type === 'TASK_RETURNED')            return '↩';
  if (type === 'REPORT_COMMENT')           return '💬';
  if (type === 'REPORT_THREAD_ADDED')      return '🧵';
  if (type === 'TEAM_REQUEST_SUBMITTED' || type === 'TEAM_REQUEST_ANSWERED') return '🙋';
  if (type === 'MEMBER_JOINED')            return '👋';
  if (type === 'MEETING_SCHEDULED' || type === 'MEETING_CANCELLED') return '📹';
  if (type === 'INTERN_REPORT_SUBMITTED')  return '🎓';
  if (type === 'LEAVE_REQUEST_SUBMITTED' || type?.startsWith('LEAVE_')) return '🌴';
  if (type === 'BADGE_EARNED')             return '🏅';
  if (type === 'MENTION')                  return '🚀';
  if (type === 'ANNOUNCEMENT')             return '📣';
  if (type === 'ACCESS_APPROVED')          return '✅';
  if (type === 'message')                  return '💬';
  if (type?.startsWith('EXPERIMENT'))      return '🧪';
  if (type?.startsWith('INVOICE') || type?.startsWith('CA_') || type?.startsWith('SALARY') || type?.startsWith('COMPLIANCE')) return '💰';
  if (type === 'CROSS_TEAM_REPORT_SHARED') return '⟺';
  return '🔔';
}

function notifAccent(type) {
  if (type === 'ACCESS_REQUEST_SUBMITTED') return '#f59e0b';
  if (type === 'ANNOUNCEMENT')             return '#f59e0b';
  if (type === 'PORTAL_REPORT_APPROVED')   return '#10b981';
  if (type === 'PORTAL_REPORT_RETURNED')   return '#f43f5e';
  if (type === 'BADGE_EARNED')             return '#BA7517';
  if (type === 'MENTION')                  return '#6366f1';
  if (type === 'ACCESS_APPROVED')          return '#10b981';
  if (type === 'TASK_ASSIGNED' || type === 'TASK_DUE_TOMORROW') return '#f59e0b';
  if (type === 'TASK_REVIEW_REQUESTED')    return '#a855f7';
  if (type === 'TASK_APPROVED' || type === 'SUBTASK_COMPLETED') return '#10b981';
  if (type === 'TASK_RETURNED')            return '#f59e0b';
  if (type === 'CROSS_TEAM_REPORT_SHARED') return '#0ea5e9';
  if (type?.startsWith('EXPERIMENT'))      return '#a855f7';
  return '#6366f1';
}

function NotificationBell({ notifications, onMarkRead, onMarkOneRead, role, userId }) {
  const [soundOn, setSoundOn] = useState(notifSoundEnabled);
  const navigate = useNavigate();
  const [open,           setOpen]          = useState(false);
  const [showHistory,    setShowHistory]   = useState(false);
  const [history,        setHistory]       = useState([]);
  const [historyLoading, setHistoryLoading]= useState(false);
  const unread = notifications.filter(n => !n.read).length;
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  async function loadHistory() {
    if (!userId || historyLoading) return;
    setHistoryLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'notifications'),
        where('recipientId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(50)
      ));
      const now = new Date();
      setHistory(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(n => !n.expiresAt || (n.expiresAt.toDate?.() ?? new Date(n.expiresAt)) > now)
      );
    } catch (_) {}
    setHistoryLoading(false);
  }

  function toggleHistory() {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history.length === 0) loadHistory();
  }

  function handleNotifClick(n) {
    onMarkOneRead?.(n.id);
    setOpen(false);

    const type = n.type;

    // Access request → navigate to requests tab
    if (type === 'ACCESS_REQUEST_SUBMITTED') {
      window.dispatchEvent(new CustomEvent('openRequestsTab'));
      return;
    }

    // Announcement → switch to announcements tab in-app (no page reload)
    if (type === 'ANNOUNCEMENT') {
      window.dispatchEvent(new CustomEvent('openAnnouncementsTab'));
      return;
    }

    // Resolve navTarget to a real route
    const raw = n.navTarget || n.link;
    const target = resolveNotifRoute(raw);
    if (!target) return;
    if (/^https?:/i.test(target)) window.location.href = target;   // share links etc.
    else navigate(target);
  }

  function renderNotifItem(n) {
    const accent = notifAccent(n.type);
    const isClickable = n.type === 'ACCESS_REQUEST_SUBMITTED' || n.type === 'ANNOUNCEMENT'
      ? true
      : !!resolveNotifRoute(n.navTarget || n.link);
    return (
      <div key={n.id}
        onClick={() => isClickable && handleNotifClick(n)}
        style={{
          padding: '12px 14px 11px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          display: 'flex', gap: 10, alignItems: 'flex-start',
          background: n.read ? 'transparent' : `${accent}0e`,
          cursor: isClickable ? 'pointer' : 'default',
          borderLeft: !n.read ? `3px solid ${accent}` : '3px solid transparent',
          transition: 'background 0.12s',
          position: 'relative',
        }}
        onMouseEnter={e => { if (isClickable) e.currentTarget.style.background = `${accent}18`; }}
        onMouseLeave={e => { e.currentTarget.style.background = n.read ? 'transparent' : `${accent}0e`; }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: 8, flexShrink: 0,
          background: `${accent}18`, border: `1px solid ${accent}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, lineHeight: 1,
        }}>{notifIcon(n.type)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: n.read ? '#8892a4' : '#d9e2ff',
            marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            letterSpacing: '-0.01em' }}>
            {n.title}
          </div>
          {n.body && (
            <div style={{ fontFamily: "'Inter',system-ui,sans-serif", fontSize: 11,
              color: '#4a6080', lineHeight: 1.45,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {n.body}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 5 }}>
            {n.createdAt && (
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 8,
                color: '#2a3a58', letterSpacing: '0.08em' }}>
                {n.createdAt.toDate?.().toLocaleDateString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
              </div>
            )}
            {isClickable && (
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 8,
                color: accent, letterSpacing: '0.1em', opacity: 0.8 }}>
                VIEW →
              </div>
            )}
          </div>
        </div>
        {!n.read && (
          <div style={{
            width: 6, height: 6, borderRadius: '50%', background: accent,
            flexShrink: 0, marginTop: 4,
          }} />
        )}
      </div>
    );
  }

  const displayList = showHistory ? history : notifications;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 20, padding: '4px 8px', lineHeight: 1, color: '#c8d4e8',
          display: 'flex', alignItems: 'center',
        }}>
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: 0,
            minWidth: 16, height: 16, borderRadius: 8, padding: '0 4px',
            background: '#f43f5e', color: '#fff', fontSize: 9,
            fontFamily: 'monospace', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #111827',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: -4, zIndex: 900,
          width: 360, maxHeight: 500, display: 'flex', flexDirection: 'column',
          background: 'rgba(7, 11, 22, 0.97)',
          border: '1px solid rgba(101,212,250,0.18)',
          borderTop: '2px solid rgba(101,212,250,0.35)',
          borderRadius: 10,
          boxShadow: '0 16px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        }}>
          {/* Header */}
          <div style={{
            padding: '10px 14px 9px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'rgba(101,212,250,0.04)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 9,
                color: '#65d4fa', letterSpacing: '0.18em', fontWeight: 700 }}>
                NOTIFICATIONS
              </span>
              {/* Tab switcher */}
              <div style={{ display: 'flex', gap: 2 }}>
                {['INBOX', 'HISTORY'].map(tab => {
                  const active = tab === 'INBOX' ? !showHistory : showHistory;
                  return (
                    <button key={tab} type="button"
                      onClick={() => tab === 'HISTORY' ? toggleHistory() : setShowHistory(false)}
                      style={{
                        background: active ? 'rgba(101,212,250,0.12)' : 'transparent',
                        border: `1px solid ${active ? 'rgba(101,212,250,0.3)' : 'transparent'}`,
                        fontFamily: "'Share Tech Mono',monospace", fontSize: 7,
                        color: active ? '#65d4fa' : '#4a6080',
                        cursor: 'pointer', padding: '2px 7px', borderRadius: 3, letterSpacing: '0.1em',
                      }}>
                      {tab}{tab === 'INBOX' && unread > 0 && (
                        <span style={{ color: '#f43f5e', marginLeft: 3 }}>{unread}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button type="button" onClick={() => { const on = !soundOn; setNotifSoundEnabled(on); setSoundOn(on); }}
                title={soundOn ? 'Notification sound on — click to mute' : 'Notification sound off — click to turn on'}
                aria-label={soundOn ? 'Mute notification sound' : 'Turn on notification sound'} aria-pressed={soundOn}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '0 4px',
                  opacity: soundOn ? 0.9 : 0.45 }}>
                {soundOn ? '🔔' : '🔕'}
              </button>
              {!showHistory && unread > 0 && (
                <button type="button" onClick={() => { onMarkRead(); setOpen(false); }}
                  style={{ background: 'none', border: 'none', fontFamily: "'Share Tech Mono',monospace",
                    fontSize: 7, color: '#4a6080', cursor: 'pointer', letterSpacing: '0.1em',
                    padding: '2px 6px', borderRadius: 3, transition: 'color 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#65d4fa'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#4a6080'; }}>
                  MARK ALL READ
                </button>
              )}
              {showHistory && (
                <button type="button" onClick={loadHistory}
                  style={{ background: 'none', border: 'none', fontFamily: "'Share Tech Mono',monospace",
                    fontSize: 7, color: '#4a6080', cursor: 'pointer', letterSpacing: '0.1em' }}>
                  ↺ REFRESH
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', color: '#4a6080',
                  cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 2px' }}>
                ×
              </button>
            </div>
          </div>
          {/* Body */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {historyLoading ? (
              <div style={{ padding: '32px 14px', textAlign: 'center',
                fontFamily: "'Share Tech Mono',monospace", fontSize: 10, color: '#2a3a58',
                letterSpacing: '0.12em' }}>
                LOADING…
              </div>
            ) : displayList.length === 0 ? (
              <div style={{ padding: '36px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 10, opacity: 0.4 }}>
                  {showHistory ? '📭' : '🛸'}
                </div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 9,
                  color: '#2a3a58', letterSpacing: '0.15em' }}>
                  {showHistory ? 'NO PAST NOTIFICATIONS' : 'ALL CLEAR · NO NEW ALERTS'}
                </div>
              </div>
            ) : displayList.map(n => renderNotifItem(n))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toast notifications ───────────────────────────────────────────────────────
function ToastContainer({ toasts, onDismiss }) {
  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8,
      pointerEvents: 'none',
    }}>
      {toasts.map((t, i) => (
        <Toast key={t.id || i} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function Toast({ toast, onDismiss }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => { setVisible(false); setTimeout(onDismiss, 300); }, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const accent = notifAccent(toast.type);
  const target = toast.type === 'ACCESS_REQUEST_SUBMITTED'
    ? null // handled by openRequestsTab event
    : resolveNotifRoute(toast.navTarget || toast.link);

  function handleToastClick() {
    if (toast.type === 'ACCESS_REQUEST_SUBMITTED') {
      window.dispatchEvent(new CustomEvent('openRequestsTab'));
      setVisible(false); setTimeout(onDismiss, 300);
      return;
    }
    if (toast.type === 'ANNOUNCEMENT') {
      window.dispatchEvent(new CustomEvent('openAnnouncementsTab'));
      setVisible(false); setTimeout(onDismiss, 300);
      return;
    }
    // Client-side navigation (TaskHost listens) — no full page reload
    if (target && /^https?:/i.test(target)) window.location.href = target;
    else if (target) window.dispatchEvent(new CustomEvent('smc:navigate', { detail: { path: target } }));
    setVisible(false); setTimeout(onDismiss, 300);
  }

  const isClickable = toast.type === 'ACCESS_REQUEST_SUBMITTED' || toast.type === 'ANNOUNCEMENT' || !!target;

  return (
    <div
      onClick={isClickable ? handleToastClick : undefined}
      style={{
        pointerEvents: 'auto',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: '#111827', border: '1px solid #1e2d45',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 12, padding: '12px 14px',
        boxShadow: '0 4px 24px #00000070',
        minWidth: 280, maxWidth: 340,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(16px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        cursor: isClickable ? 'pointer' : 'default',
      }}>
      <div style={{ fontSize: 20, lineHeight: 1.2, flexShrink: 0 }}>
        {notifIcon(toast.type)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#eef2ff', marginBottom: 2 }}>
          {toast.title}
        </div>
        <div style={{ fontSize: 11, color: '#6b7fa0', lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {toast.body}
        </div>
        {isClickable && (
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: accent, marginTop: 3, letterSpacing: 1 }}>
            TAP TO VIEW →
          </div>
        )}
      </div>
      <button type="button" onClick={e => { e.stopPropagation(); setVisible(false); setTimeout(onDismiss, 300); }}
        style={{ background: 'none', border: 'none', color: '#4a5a78',
          cursor: 'pointer', fontSize: 16, flexShrink: 0, lineHeight: 1, padding: '0 2px' }}>
        ×
      </button>
    </div>
  );
}

// ── Session token helpers (multi-device logout) ───────────────────────────────
const SESSION_KEY = 'smc_session_token';
function genToken() { return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now(); }
async function writeSessionToken(uid, token) {
  try { await updateDoc(doc(db, 'authorized_users', uid), { sessionToken: token }); } catch(_) {}
}
async function invalidateAllSessions(uid) {
  const fresh = genToken();
  await writeSessionToken(uid, fresh);
  // Current device gets the new token too so it stays logged in
  try { localStorage.setItem(SESSION_KEY, fresh); } catch(_) {}
}

// ── Pull-to-refresh ───────────────────────────────────────────────────────────
function usePullToRefresh(enabled) {
  useEffect(() => {
    if (!enabled) return;
    let startY = 0;
    let pulling = false;
    let indicator = null;

    function showIndicator() {
      indicator = document.createElement('div');
      indicator.id = 'ptr-indicator';
      Object.assign(indicator.style, {
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '10px', background: '#6366f120', backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #6366f144', fontFamily: 'monospace', fontSize: 11,
        color: '#6366f1', letterSpacing: 2, transition: 'transform 0.2s',
        transform: 'translateY(-100%)',
      });
      indicator.textContent = '↓  RELEASE TO REFRESH';
      document.body.appendChild(indicator);
      requestAnimationFrame(() => { if (indicator) indicator.style.transform = 'translateY(0)'; });
    }
    function removeIndicator() {
      if (indicator) { indicator.style.transform = 'translateY(-100%)'; setTimeout(() => indicator?.remove(), 220); indicator = null; }
    }

    function onTouchStart(e) { startY = e.touches[0].clientY; pulling = false; }
    function onTouchMove(e) {
      if (window.scrollY > 0) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 60 && !pulling) { pulling = true; showIndicator(); }
    }
    function onTouchEnd() {
      if (pulling) {
        removeIndicator();
        clearAllCache();
        window.dispatchEvent(new CustomEvent('smc-soft-refresh'));
      }
      pulling = false;
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove',  onTouchMove,  { passive: true });
    document.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove',  onTouchMove);
      document.removeEventListener('touchend',   onTouchEnd);
      removeIndicator();
    };
  }, [enabled]);
}

// ── CheckInStrip — compact today's check-in nudge shown on home tab ──────────
function todayIST() { return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); }

function CheckInStrip({ userId, userName, role, onNavigateToAttendance }) {
  const theme   = useContext(ThemeContext);
  const today   = todayIST();
  const { session, loading } = useWorkSession(userId);
  const [dismissed, setDismissed] = useState(() => safeLocalStorage(`ci_dismiss_${today}`) === '1');
  const [busy,      setBusy]      = useState(false);

  if (!userId || loading || session.isCheckedIn || session.isComplete || dismissed) return null;

  async function quickCheckIn() {
    setBusy(true);
    try {
      let deviceType = 'web';
      try { if (window.Capacitor?.isNativePlatform?.()) deviceType = 'android'; } catch(_) {}
      await setDoc(doc(db, 'attendance', userId, 'records', today), {
        date: today, uid: userId, name: userName, role,
        checkedInAt: serverTimestamp(),
        checkedInAtClient: new Date().toISOString(),
        note: '', location: 'Office', locationName: 'Office',
        locationSource: 'manual',
        lat: null, lng: null,
        checkinLat: null, checkinLng: null,
        checkinAccuracy_m: null, locationAccuracy_m: null,
        deviceType, workType: 'office',
        companyAllottedHours: 8, sessionReloads: 0,
        lastReloadAt: null,
        loggedHours_minutes: null, actualDuration_minutes: null,
        cappingApplied: false, cappingNote: null,
        checkoutTime: null, checkoutLat: null, checkoutLng: null,
        checkoutLocationName: null, checkoutAccuracy_m: null,
        checkoutGeoRule: null, checkoutMethod: null,
        duration_minutes: null,         isOpen: true, isDeleted: false,
      });
    } catch (err) {
      console.error('[CheckInStrip]', err);
    } finally { setBusy(false); }
  }

  return (
    <div style={{
      margin:'12px 0 0', background:theme.panel,
      border:`1px solid ${theme.border}`, borderLeft:'3px solid #22c55e',
      borderRadius:8, padding:'10px 16px',
      display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
    }}>
      <span>📋</span>
      <span style={{ fontFamily:'monospace', fontSize:11, color:theme.text, flex:1 }}>
        You haven't checked in today.
      </span>
      <button onClick={quickCheckIn} disabled={busy} style={{
        fontFamily:'monospace', fontSize:10, letterSpacing:1,
        background:'#22c55e', color:'#fff', border:'none',
        padding:'6px 14px', borderRadius:5, cursor:busy?'not-allowed':'pointer',
        opacity:busy?0.6:1, whiteSpace:'nowrap',
      }}>{busy ? 'CHECKING IN…' : 'CHECK IN NOW'}</button>
      <button onClick={() => onNavigateToAttendance?.()} style={{
        fontFamily:'monospace', fontSize:10, letterSpacing:1, background:'transparent',
        color:theme.accent, border:`1px solid ${theme.accent}`,
        padding:'6px 12px', borderRadius:5, cursor:'pointer', whiteSpace:'nowrap',
      }}>OPEN ATTENDANCE</button>
      <button onClick={() => { safeLocalStorageSet(`ci_dismiss_${today}`, '1'); setDismissed(true); }}
        style={{ background:'none', border:'none', color:theme.muted, cursor:'pointer', fontSize:18, padding:'0 2px' }}>×</button>
    </div>
  );
}

// ── Shared Reports Admin Page ─────────────────────────────────────────────────
function SharedReportsAdminPage({ userId, userName, role }) {
  const [posts,       setPosts]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [title,       setTitle]       = useState('');
  const [summary,     setSummary]     = useState('');
  const [content,     setContent]     = useState('');
  const [department,  setDepartment]  = useState('');
  const [busy,        setBusy]        = useState(false);
  const [err,         setErr]         = useState('');
  const [showForm,    setShowForm]    = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'public_shared_reports'), orderBy('createdAt', 'desc')),
      snap => { setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  async function publish() {
    if (!title.trim()) { setErr('Title required'); return; }
    setBusy(true); setErr('');
    try {
      await addDoc(collection(db, 'public_shared_reports'), {
        title: title.trim(),
        summary: summary.trim() || null,
        content: content.trim() || null,
        department: department.trim() || null,
        active: true,
        createdAt: serverTimestamp(),
        createdBy: userName, createdByUid: userId,
      });
      setTitle(''); setSummary(''); setContent(''); setDepartment(''); setShowForm(false);
    } catch (ex) { setErr(ex.message || 'Failed to publish'); }
    setBusy(false);
  }

  async function toggle(id, current) {
    try { await updateDoc(doc(db, 'public_shared_reports', id), { active: !current }); } catch (_) {}
  }

  const isAdminOrOps = role === 'admin' || role === 'ops';
  const S = {
    bg: 'var(--smc-bg,#070c1a)', panel: 'var(--smc-panel,#0d1526)',
    border: 'var(--smc-bd,rgba(255,255,255,0.08))', input: 'var(--smc-deep,#060b18)',
    text: 'var(--smc-tx,#d9e2ff)', muted: 'var(--smc-tx2,#8892a4)', dim: 'var(--smc-tx3,#4d5a6e)',
    primary: 'var(--smc-ac,#65d4fa)', green: 'var(--smc-gn,#34d399)', red: 'var(--smc-rd,#f87171)',
    mono: "'Share Tech Mono','Courier New',monospace", sans: "'Inter',system-ui,sans-serif",
  };

  return (
    <div style={{ padding: '24px', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: S.mono, fontSize: 10, color: S.primary, letterSpacing: 2, marginBottom: 4 }}>
            PUBLIC · SHARED REPORTS
          </div>
          <div style={{ fontFamily: S.sans, fontSize: 13, color: S.muted }}>
            Reports published here are visible to anyone — no login required.
          </div>
        </div>
        {isAdminOrOps && (
          <button onClick={() => setShowForm(f => !f)}
            style={{ fontFamily: S.mono, fontSize: 10, padding: '9px 18px', cursor: 'pointer', letterSpacing: 1,
              background: showForm ? 'transparent' : S.primary, border: `1px solid ${S.primary}`,
              color: showForm ? S.primary : '#060b18' }}>
            {showForm ? '× CANCEL' : '+ PUBLISH'}
          </button>
        )}
      </div>

      {showForm && isAdminOrOps && (
        <div style={{ background: S.panel, border: `1px solid ${S.border}`, padding: '20px', marginBottom: 24 }}>
          <div style={{ fontFamily: S.mono, fontSize: 9, color: S.dim, letterSpacing: 2, marginBottom: 14 }}>NEW PUBLIC REPORT</div>
          {err && <div style={{ fontFamily: S.mono, fontSize: 9, color: S.red, marginBottom: 10, padding: '6px 10px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>{err}</div>}
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title *"
            style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: S.text,
              fontFamily: S.sans, fontSize: 13, padding: '10px 14px', marginBottom: 10, boxSizing: 'border-box', outline: 'none' }} />
          <input value={department} onChange={e => setDepartment(e.target.value)} placeholder="Department / tag (optional)"
            style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: S.text,
              fontFamily: S.sans, fontSize: 13, padding: '10px 14px', marginBottom: 10, boxSizing: 'border-box', outline: 'none' }} />
          <textarea value={summary} onChange={e => setSummary(e.target.value)} placeholder="Short summary (shown in card preview)"
            rows={2} style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: S.text,
              fontFamily: S.sans, fontSize: 13, padding: '10px 14px', marginBottom: 10, boxSizing: 'border-box', outline: 'none', resize: 'vertical' }} />
          <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Full content (optional — visible on expand)"
            rows={5} style={{ width: '100%', background: S.input, border: `1px solid ${S.border}`, color: S.text,
              fontFamily: S.sans, fontSize: 13, padding: '10px 14px', marginBottom: 14, boxSizing: 'border-box', outline: 'none', resize: 'vertical' }} />
          <button onClick={publish} disabled={busy}
            style={{ fontFamily: S.mono, fontSize: 10, padding: '9px 20px', cursor: busy ? 'wait' : 'pointer', letterSpacing: 1,
              background: busy ? 'transparent' : S.green, border: `1px solid ${busy ? S.border : S.green}`,
              color: busy ? S.dim : '#060b18' }}>
            {busy ? 'PUBLISHING…' : 'PUBLISH NOW'}
          </button>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: '40px', fontFamily: S.mono, fontSize: 11, color: S.dim }}>LOADING…</div>}

      {!loading && posts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', fontFamily: S.mono, fontSize: 11, color: S.dim }}>
          No public reports yet. Publish one above.
        </div>
      )}

      {posts.map(p => (
        <div key={p.id} style={{ background: S.panel, border: `1px solid ${p.active ? S.border : 'rgba(255,255,255,0.03)'}`,
          padding: '16px 20px', marginBottom: 12, opacity: p.active ? 1 : 0.5,
          display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: S.sans, fontSize: 14, color: S.text, fontWeight: 600 }}>{p.title}</span>
              {p.department && (
                <span style={{ fontFamily: S.mono, fontSize: 9, color: S.primary, background: 'rgba(101,212,250,0.1)',
                  border: '1px solid rgba(101,212,250,0.25)', padding: '1px 8px', letterSpacing: 1 }}>
                  {p.department.toUpperCase()}
                </span>
              )}
              <span style={{ fontFamily: S.mono, fontSize: 8, color: p.active ? S.green : S.red,
                border: `1px solid ${p.active ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)'}`,
                padding: '1px 7px', letterSpacing: 1 }}>
                {p.active ? 'LIVE' : 'HIDDEN'}
              </span>
            </div>
            {p.summary && <div style={{ fontFamily: S.sans, fontSize: 12, color: S.muted, lineHeight: 1.6 }}>{p.summary}</div>}
          </div>
          {isAdminOrOps && (
            <button onClick={() => toggle(p.id, p.active)}
              style={{ fontFamily: S.mono, fontSize: 8, padding: '5px 10px', cursor: 'pointer', flexShrink: 0,
                background: 'transparent', border: `1px solid ${p.active ? 'rgba(248,113,113,0.3)' : 'rgba(52,211,153,0.3)'}`,
                color: p.active ? S.red : S.green, letterSpacing: 1 }}>
              {p.active ? 'HIDE' : 'SHOW'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Admin REQUESTS tab: access requests + team requests / grievances
function AdminRequestsTabs({ userId, userName, role }) {
  const [sub, setSub] = useState('access');
  return (
    <>
      <div className="smc2-subtabs" style={{ display:'flex', gap:4, padding:'10px 16px 0', maxWidth:1180, margin:'0 auto', flexWrap:'wrap' }}>
        {[['access','ACCESS REQUESTS'],['team','TEAM REQUESTS']].map(([v,l]) => (
          <button key={v} type="button" className={`smc2-tab${sub===v ? ' is-active' : ''}`}
            style={{ '--dept':'var(--smc-ac)' }} onClick={() => setSub(v)}>{l}</button>
        ))}
      </div>
      {sub === 'access'
        ? <AccessRequestsPage userId={userId} role={role} />
        : <Suspense fallback={null}><MemberRequestsPage userId={userId} userName={userName} admin /></Suspense>}
    </>
  );
}

let splashSeenThisSession = false;

function MainApp() {
  const [current,      setCurrent]      = useState(null);
  const [themeId,      setThemeId]      = useState(DEFAULT_THEME_ID);
  const [role,         setRole]         = useState(null);
  const [userPortals,  setUserPortals]  = useState([]);
  const { depts: departments, loaded: departmentsLoaded } = useDepartmentsState();
  // Portal picked by a multi-portal member, as "<uid>:<portalId>" in
  // sessionStorage so a refresh keeps it but a new session asks again.
  const [portalChoice, setPortalChoice] = useState(() => {
    try { return sessionStorage.getItem('smc_portal_choice') || ''; } catch (_) { return ''; }
  });
  const [dataRoomAllowedUids, setDataRoomAllowedUids] = useState([]);
  const [userName,     setUserName]     = useState("");
  const [userId,       setUserId]       = useState("");
  // Clean tab routes: /app/<tab> (legacy ?tab=<tab> still understood)
  const [mainTab,      setMainTab]      = useState(() => {
    try { return tabFromLocation(window.location) || "home"; }
    catch (_) { return "home"; }
  });
  const [authChecked,  setAuthChecked]  = useState(false);
  // Full splash only on the first load of a session; coming back from a
  // standalone page (experiment workspace, portals…) shouldn't replay it.
  const [showLoader,   setShowLoader]   = useState(() => !splashSeenThisSession);
  const [showAuth,     setShowAuth]     = useState(() => {
    try { return new URLSearchParams(window.location.search).get('showAuth') === '1'; }
    catch (_) { return false; }
  });
  const [incomingCall, setIncomingCall] = useState(null);
  const [notifications,  setNotifications]  = useState([]);
  const [toasts,         setToasts]         = useState([]);
  const [locked,         setLocked]         = useState(false);
  const [visitedTabs,    setVisitedTabs]    = useState(() => new Set(['home']));
  const [searchParams, setSearchParams]     = useSearchParams();
  const location       = useLocation();
  const routerNavigate = useNavigate();
  const [appVersion,          setAppVersion]          = useState(null);
  const [appVersionDate,      setAppVersionDate]      = useState(null);
  const [appVersionUpdatedBy, setAppVersionUpdatedBy] = useState(null);
  const [changelogEntries,    setChangelogEntries]    = useState([]);
  const [settingsOpen,        setSettingsOpen]        = useState(false);
  const [navMenuOpen,         setNavMenuOpen]         = useState(false);
  const [teamSubTab,     setTeamSubTab]     = useState(() => (isV2('shell') ? 'crew' : 'portal'));
  const [showQuickPost,  setShowQuickPost]  = useState(false);
  const [viewAs,         setViewAs]         = useState(null); // null | portal id | 'ops_role'
  const [viewAsUserId,   setViewAsUserId]   = useState('');
  const viewAsPortalId = viewAs && VIEW_AS_PORTAL_IDS.has(viewAs) ? viewAs : null;
  const viewAsMembers = useViewAsMembers(viewAsPortalId);
  const viewAsPreviewLabel = useMemo(() => {
    if (!viewAsPortalId) return '';
    const member = viewAsUserId ? viewAsMembers.find(m => m.uid === viewAsUserId) : null;
    return buildPreviewUser(member, viewAsPortalId).name;
  }, [viewAsPortalId, viewAsUserId, viewAsMembers]);

  const viewAsDepartmentPreview = useMemo(() => {
    if (!viewAsPortalId) return null;
    const member = viewAsUserId ? viewAsMembers.find(m => m.uid === viewAsUserId) : null;
    const preview = buildPreviewUser(member, viewAsPortalId);
    const portal = PORTAL_CARDS.find(p => p.id === viewAsPortalId) || PORTAL_CARDS[0];
    return {
      portal,
      userName: preview.name,
      userId: member?.uid || userId,
      role: member?.role || preview.role,
      profileUserId: member?.uid ?? null,
      allowProfileEdit: !member,
    };
  }, [viewAsPortalId, viewAsUserId, viewAsMembers, userId]);

  const handleViewAs = useCallback((portalId) => {
    setViewAs(portalId);
    setViewAsUserId('');
    setSettingsOpen(false);
  }, []);

  const exitViewAs = useCallback(() => {
    setViewAs(null);
    setViewAsUserId('');
  }, []);
  const [myExperiments,   setMyExperiments]   = useState([]);
  const [notifyTestOpen,  setNotifyTestOpen]  = useState(false);
  const lastActiveRef = useRef(Date.now());
  const appConfig = useAppConfig();

  // ── Cache: warm on login ──────────────────────────────────────────────────
  const warmCache = useCallback(async (uid) => {
    if (!uid) return;
    const collections = [
      { name: 'authorized_users', q: collection(db, 'authorized_users') },
      { name: 'app_config',       q: collection(db, 'app_config') },
      { name: 'announcements',    q: query(collection(db, 'announcements'), orderBy('createdAt','desc'), limit(20)) },
      { name: 'tasks',            q: query(collection(db, 'tasks'), orderBy('createdAt','desc'), limit(50)) },
    ];
    for (const c of collections) {
      try {
        const snap = await getDocs(c.q);
        await SessionCache.set(c.name, uid, snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch {} // never block auth on cache failure
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cache: periodic expired-entry sweep (every 30 min) ───────────────────
  useEffect(() => {
    const id = setInterval(() => SessionCache.sweepExpired(), 30 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // ── Restore Firebase Auth session on startup ──────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setAuthChecked(true); return; }
      try {
        const snap = await getDoc(doc(db, "authorized_users", user.uid));
        if (snap.exists()) {
          const ud = snap.data();
          if (ud.active !== false) {
            // ── Session token check (multi-device logout) ────────────────
            const storedToken = localStorage.getItem(SESSION_KEY);
            if (ud.sessionToken && storedToken && ud.sessionToken !== storedToken) {
              // Another session invalidated this one — force sign out
              await fbSignOut(auth);
              localStorage.removeItem(SESSION_KEY);
              setAuthChecked(true);
              return;
            }
            sessionStorage.setItem("sat_admin_session", ud.role === "admin" ? "1" : "");
            setRole(ud.role);
            setUserPortals(ud.portals && ud.portals.length ? ud.portals : (ud.role ? [ud.role] : []));
            const restoredName = ud.name || user.displayName || user.email || "";
            setUserName(restoredName);
            setUserId(user.uid);
            // Register ops/admin in team_members for CrossTeamLinker
            if ((ud.role === 'ops' || ud.role === 'admin') && restoredName) {
              registerMember(db, restoredName, ud.role, user.uid).catch(() => {});
            }
            // Restore saved theme
            if (ud.theme) {
              setThemeId(ud.theme);
              applyThemeCSSVars(ud.theme);
            }
          }
        }
      } catch (_) {}
      setAuthChecked(true);
    });
    return () => unsub();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save theme to Firestore when user changes it ──────────────────────────
  const handleThemeChange = useCallback(async (tid) => {
    setThemeId(tid);
    applyThemeCSSVars(tid);
    try { localStorage.setItem('smc_theme', tid); } catch(_) {}
    // Notify portal routes (rendered outside this ThemeContext.Provider) to update
    window.dispatchEvent(new CustomEvent('smc-theme-change', { detail: tid }));
    if (userId) {
      try { await updateDoc(doc(db, "authorized_users", userId), { theme: tid }); } catch(_) {}
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── App version (Firestore is single source of truth) ───────────────────
  useEffect(() => {
    const vref = doc(db, 'app_config', 'version');
    const unsub = onSnapshot(vref, snap => {
      if (snap.exists()) {
        const d = snap.data();
        setAppVersion(d.number || 1);
        setAppVersionDate(d.updatedAt?.toDate?.() || null);
        setAppVersionUpdatedBy(d.updatedBy || null);
      } else {
        setAppVersion(1);
        setAppVersionDate(null);
        if (role === 'admin') {
          setDoc(vref, { number: 1, updatedAt: serverTimestamp() }).catch(() => {});
        }
      }
    }, () => setAppVersion(1));
    return () => unsub();
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── App changelog (admin only, last 10) ──────────────────────────────────
  useEffect(() => {
    if (role !== 'admin') return;
    const unsub = onSnapshot(
      query(
        collection(db, 'app_config', 'version', 'changelog'),
        orderBy('updatedAt', 'desc'),
        limit(10)
      ),
      snap => setChangelogEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {}
    );
    return () => unsub();
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── My experiments — real-time, used to show conditional nav tab ─────────
  useEffect(() => {
    if (!userId || !userName) return;
    // Listen for experiments where user is an assigned team member (UID-based, new experiments)
    const qUid = query(
      collection(db, 'experiments'),
      where('conductedByUids', 'array-contains', userId)
    );
    const unsubUid = onSnapshot(qUid,
      snap => {
        const fromUids = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMyExperiments(fromUids);
      },
      () => {}
    );
    return () => unsubUid();
  }, [userId, userName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Global notification listener ──────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', userId),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    let initial = true;
    const unsub = onSnapshot(q, snap => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (!initial) {
        const added = snap.docChanges()
          .filter(c => c.type === 'added')
          .map(c => ({ id: c.doc.id, ...c.doc.data() }));
        if (added.length > 0) {
          playNotificationSound();
          setToasts(t => [...added.slice(0, 3), ...t].slice(0, 5));
        }
      }
      initial = false;
    }, () => {});
    return () => unsub();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pull-to-refresh (only when logged in) ────────────────────────────────
  usePullToRefresh(!!role);

  // FCM token refresh is now handled by StartupChecks when permission is already granted

  // ── FCM foreground message → in-app toast ───────────────────────────────
  useEffect(() => {
    if (!role) return;
    const unsub = onForegroundMessage(payload => {
      const { title, body } = payload.notification || {};
      setToasts(t => [{
        id:    `fcm-${Date.now()}`,
        title: title || 'Spacemancraft OS',
        body:  body  || '',
        type:  payload.data?.type || 'general',
      }, ...t].slice(0, 5));
    });
    return unsub;
  }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Inactivity lock + visibility-change lock ─────────────────────────────
  // Admin can switch it off / change the delay (app_config/lock: enabled, timeoutMin)
  const lockCfgRef = useRef({ enabled: true, timeoutMin: 10 });
  useEffect(() => {
    if (!role) return undefined;
    return onSnapshot(doc(db, 'app_config', 'lock'), snap => {
      const d = snap.exists() ? snap.data() : {};
      lockCfgRef.current = { enabled: d.enabled !== false, timeoutMin: Number(d.timeoutMin) || 10 };
      if (d.enabled === false) setLocked(false);
    }, () => {});
  }, [role]);

  useEffect(() => {
    if (!role) return;
    function resetTimer() { lastActiveRef.current = Date.now(); }
    const events = ['touchstart', 'touchend', 'mousemove', 'keydown', 'click'];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
    const interval = setInterval(() => {
      const { enabled, timeoutMin } = lockCfgRef.current;
      if (enabled && Date.now() - lastActiveRef.current > timeoutMin * 60 * 1000) setLocked(true);
    }, 30000);
    function onVisibility() {
      if (document.hidden) lastActiveRef.current = Date.now() - (lockCfgRef.current.timeoutMin + 1) * 60 * 1000;
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      events.forEach(e => document.removeEventListener(e, resetTimer));
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [role]);

  // ── openAnnouncementsTab event → navigate to announcements tab in-app ───
  useEffect(() => {
    function handler() {
      setMainTab('announcements');
      setCurrent(null);
      setVisitedTabs(v => new Set([...v, 'announcements']));
      routerNavigate(tabPath('announcements'), { replace: true });
    }
    window.addEventListener('openAnnouncementsTab', handler);
    return () => window.removeEventListener('openAnnouncementsTab', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── openRequestsTab event → navigate to requests tab in-app ─────────────
  useEffect(() => {
    function handler() {
      setMainTab('requests');
      setCurrent(null);
      setVisitedTabs(v => new Set([...v, 'requests']));
      routerNavigate(tabPath('requests'), { replace: true });
    }
    window.addEventListener('openRequestsTab', handler);
    return () => window.removeEventListener('openRequestsTab', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!navMenuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setNavMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navMenuOpen]);

  useEffect(() => {
    document.body.classList.toggle('smc-nav-menu-open', navMenuOpen);
    return () => document.body.classList.remove('smc-nav-menu-open');
  }, [navMenuOpen]);

  // ── Tab keep-alive: track visited tabs so we render once + keep mounted ──
  function switchTab(tab) {
    const resolved = tab === 'main' ? 'home' : tab;
    setMainTab(resolved);
    setCurrent(null);
    setVisitedTabs(v => new Set([...v, resolved]));
    routerNavigate(tabPath(resolved), { replace: true });
    setNavMenuOpen(false);
  }

  function renderMainTab(tab) {
    const loading = (
      <div style={{ textAlign:'center', padding:'40px', fontFamily:'monospace', fontSize:11, color:theme.muted }}>
        LOADING…
      </div>
    );
    switch (tab) {
      case 'team': {
        const teamTabs = [
          ...(designV2 ? [['crew','👥  CREW']] : []),
          ['portal','🏢  TEAM PORTALS'],
          ...(appConfig.features.attendanceEnabled !== false ? [['attendance','📅  ATTENDANCE']] : []),
          ...(appConfig.features.achievementsEnabled !== false ? [['achievements','🏅  ACHIEVEMENTS']] : []),
          ...(role === 'admin' ? [['manage','👥  MANAGE TEAM']] : []),
        ];
        const subTab = teamSubTab === 'crew' && !designV2 ? 'portal' : teamSubTab;
        return (
          <div>
            {designV2 ? (
              <div className="smc2-subtabs" style={{ '--dept': theme.accent }}>
                <div className="smc2-subtabs-inner">
                  {teamTabs.map(([v,l]) => (
                    <button key={v} type="button" onClick={() => setTeamSubTab(v)}
                      className={`smc2-tab${subTab === v ? ' is-active' : ''}`}>
                      {l.replace(/^[^A-Za-z0-9]+/, '')}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
            <div className="smc-tab-bar smc-tab-bar-sticky" style={{ display:'flex', justifyContent:'center', gap:0,
              borderBottom:`1px solid ${theme.border}`, background:`${theme.bg}f0`,
              position:'sticky', top:'var(--smc-nav-h, 46px)', zIndex:80, backdropFilter:'blur(10px)' }}>
              {teamTabs.map(([v,l]) => (
                <button key={v} onClick={() => setTeamSubTab(v)}
                  style={{ padding:'11px 32px', fontFamily:'monospace', fontSize:10,
                    letterSpacing:1, cursor:'pointer', background:'transparent', border:'none',
                    borderBottom:`2px solid ${subTab===v ? theme.accent : 'transparent'}`,
                    color: subTab===v ? theme.accent : theme.muted, transition:'color 0.15s',
                    whiteSpace:'nowrap' }}>{l}</button>
              ))}
            </div>
            )}
            {subTab === 'crew'
              ? <Suspense fallback={loading}><CrewDirectory onManage={role === 'admin' ? () => setTeamSubTab('manage') : undefined} /></Suspense>
              : subTab === 'attendance'
              ? <Suspense fallback={loading}><AttendancePage role={role} userId={userId} userName={userName} /></Suspense>
              : subTab === 'achievements'
              ? <AchievementsPage userId={userId} userName={userName} role={role} />
              : subTab === 'manage' && role === 'admin'
              ? <Suspense fallback={loading}><AdminTeamPage currentUserId={userId} /></Suspense>
              : <TeamPortalsPage role={role} userId={userId} userName={userName} />
            }
          </div>
        );
      }
      case 'reports':
        return <Suspense fallback={loading}><ReportsRegisterPage userId={userId} userName={userName} role={role} /></Suspense>;
      case 'my-day':
        return <Suspense fallback={loading}><MyDayPage userId={userId} userName={userName} role={role} onNavigate={switchTab} /></Suspense>;
      case 'missions':
        return (
          <Suspense fallback={loading}>
            <div className="smc-missions-frame" style={{ height:'calc(100vh - 46px)', display:'flex', flexDirection:'column' }}>
              <MissionPortalPage userId={userId} userName={userName} role={role} />
            </div>
          </Suspense>
        );
      case 'mr1-tracker':
        return (
          <Suspense fallback={loading}>
            <div style={{ height:'calc(100vh - 46px)' }}>
              <MR1TrackerPage userId={userId} userName={userName} role={role} />
            </div>
          </Suspense>
        );
      case 'physics':
        return <PhysicsPage onNavigate={navigate} visits={visits} role={role} />;
      case 'simulations':
        return <SimulationsPage onNavigate={navigate} visits={visits} role={role} />;
      case 'dashboard':
        return <DashboardPage onNavigate={navigate} visits={visits} role={role} />;
      case 'daily-log':
        return <Suspense fallback={loading}><DailyLogPage userId={userId} userName={userName} userRole={role} /></Suspense>;
      case 'attendance':
        return <Suspense fallback={loading}><AttendancePage role={role} userId={userId} userName={userName} /></Suspense>;
      case 'interns':
        return <Suspense fallback={loading}><InternsHub userId={userId} userName={userName} role={role} /></Suspense>;
      case 'backup':
        return <Suspense fallback={loading}><BackupStoragePage userId={userId} userName={userName} role={role} /></Suspense>;
      case 'data-room':
        return <Suspense fallback={loading}><DataRoomPortal userId={userId} userName={userName} role={role} /></Suspense>;
      case 'announcements':
        return <AnnouncementsPage userId={userId} role={role} />;
      case 'requests':
        return <AdminRequestsTabs userId={userId} userName={userName} role={role} />;
      case 'shared':
        return <SharedReportsAdminPage userId={userId} userName={userName} role={role} />;
      case 'meetings':
        return <MeetingsPage user={{ uid: userId, name: userName }} />;
      case 'mytasks':
        return (
          <div className="smc-page-container-sm" style={{ maxWidth:900, margin:'0 auto', padding:'0 24px 80px' }}>
            <DailyTasksPage user={{ uid: userId, name: userName }} />
          </div>
        );
      case 'mail':
        return <MailboxPage user={{ uid: userId, name: userName }} />;
      case 'chat':
        return <ChatPage user={{ uid: userId, name: userName, role }} />;
      case 'my-experiments':
        return <Suspense fallback={loading}><MyExperimentsPanel userId={userId} userName={userName} experiments={myExperiments} /></Suspense>;
      case 'notifications':
        return role === 'admin'
          ? <Suspense fallback={loading}><NotificationRulesPage userId={userId} userName={userName} /></Suspense>
          : null;
      case 'lock-settings':
        return role === 'admin'
          ? <div style={{ maxWidth:480, margin:'40px auto', padding:'0 16px' }}><PinSetupPanel userId={userId} /></div>
          : null;
      case 'home':
      default:
        return (
          <>
            <div style={designV2 ? { maxWidth:1280, margin:'0 auto', padding:'16px 16px 0' } : { maxWidth:1100, margin:'0 auto', padding:'0 24px' }}>
              <CheckInStrip
                userId={userId} userName={userName} role={role}
                onNavigateToAttendance={() => { switchTab('team'); setTeamSubTab('attendance'); }}
              />
            </div>
            <DashboardHome
              onTabChange={(t, opts) => {
                switchTab(t);
                if (opts?.teamSubTab) setTeamSubTab(opts.teamSubTab);
              }}
              visits={visits} role={role} userName={userName} userId={userId} themeId={themeId} onThemeChange={handleThemeChange}
            />
          </>
        );
    }
  }

  const { visits, recordVisit, syncStatus } = useCloudSync();

  function dismissToast(id) {
    setToasts(t => t.filter(x => x.id !== id));
  }

  async function markAllRead() {
    notifications.forEach(n => {
      try { updateDoc(doc(db, 'notifications', n.id), { read: true }); } catch(_) {}
    });
    setNotifications([]);
  }

  function markOneRead(notifId) {
    try { updateDoc(doc(db, 'notifications', notifId), { read: true }); } catch(_) {}
    setNotifications(ns => ns.filter(n => n.id !== notifId));
  }

  // ── Global incoming call listener ──────────────────────────────────────────
  const handleIncomingCall = useCallback((call) => {
    setIncomingCall(call);
  }, []);
  useCallListener(userId, handleIncomingCall);

  // Apply CSS vars whenever theme changes (SMC + legacy app themes)
  useEffect(() => {
    applyThemeCSSVars(themeId);
  }, [themeId]);

  const theme = getTheme(themeId);
  const page  = PAGES.find(p => p.id === current);
  const designV2 = isV2('shell');
  const sectionBadges = useSectionBadges({ userId, role, activeTab: current ? null : mainTab, alias: { tasks: 'my-day' } });

  function navigate(id) {
    const allowed = ROLE_PAGES[role];
    if (allowed !== null && allowed !== undefined && !allowed.includes(id)) return;
    if (role === "embedded") { setCurrent("emmscoildriver"); return; }
    recordVisit(id);
    setCurrent(id);
    setMainTab("home");
    setSearchParams({ page: id }, { replace: false });
  }


  // Legacy links (/?tab=x, /admin?tab=x — notifications, bookmarks) → /app/x,
  // keeping any other params (e.g. view=approvals).
  useEffect(() => {
    if (!role || location.pathname.startsWith('/app/')) return;
    const legacy = searchParams.get('tab');
    if (!legacy) return;
    const rest = new URLSearchParams(searchParams);
    rest.delete('tab');
    const qs = rest.toString();
    routerNavigate(tabPath(legacy === 'main' ? 'home' : legacy, qs ? `?${qs}` : ''), { replace: true });
  }, [role, location.pathname, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync main tab / sub-page from URL (back/forward via React Router)
  useEffect(() => {
    if (!role) return;
    const page = searchParams.get('page');
    const tab  = tabFromLocation(location);
    if (page) {
      setCurrent(page);
      setMainTab('home');
      setVisitedTabs(v => new Set([...v, 'home']));
      return;
    }
    setCurrent(null);
    if (tab) {
      const resolved = tab === 'main' ? 'home' : tab;
      setMainTab(resolved);
      setVisitedTabs(v => new Set([...v, resolved]));
    }
  }, [searchParams, location.pathname, role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (role === "embedded" && !current) setCurrent("emmscoildriver");
  }, [role, current]);

  // ── Shared sign-out helpers ───────────────────────────────────────────────
  async function doSignOut() {
    clearAllCache();
    safeLocalStorageRemove(SESSION_KEY);
    await fbSignOut(auth);
    try { sessionStorage.removeItem("sat_admin_session"); } catch (_) {}
    setRole(null); setUserName(""); setUserId(""); setCurrent(null); setLocked(false);
  }

  async function doSignOutAll() {
    if (!window.confirm('This will sign out from ALL devices. Continue?')) return;
    clearAllCache();
    await invalidateAllSessions(userId);
    safeLocalStorageRemove(SESSION_KEY);
    await fbSignOut(auth);
    try { sessionStorage.removeItem("sat_admin_session"); } catch (_) {}
    setRole(null); setUserName(""); setUserId(""); setCurrent(null); setLocked(false);
  }

  // ── Data Room access allowlist (admin-only until explicitly granted) ───────
  useEffect(() => {
    if (!userId) { setDataRoomAllowedUids([]); return; }
    const unsub = onSnapshot(doc(db, 'data_room_access', 'config'), snap => {
      setDataRoomAllowedUids(snap.exists() ? (snap.data().allowedUids || []) : []);
    }, () => {});
    return unsub;
  }, [userId]);

  const canAccessDataRoom = hasDataRoomNavAccess(role, userId, dataRoomAllowedUids);

  useEffect(() => {
    if (mainTab === 'data-room' && !canAccessDataRoom) {
      setMainTab('home');
      routerNavigate(tabPath('home'), { replace: true });
    }
  }, [mainTab, canAccessDataRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep loader visible until Firebase auth fully resolves (prevents login-page flash on reload)
  if (showLoader || !authChecked) {
    if (!showLoader) {
      // returning visit — quiet themed placeholder while auth resolves
      return <div style={{ minHeight:'100vh', background:'var(--smc-bg)' }} />;
    }
    const SplashScreen = isV2('splash') ? SplashScreenV2 : SplashScreenV1;
    return <SplashScreen loadingComplete={authChecked} onComplete={() => { splashSeenThisSession = true; setShowLoader(false); }} />;
  }

  // ── App lock screen ───────────────────────────────────────────────────────
  if (locked && role) {
    return <AppLock onUnlock={() => { setLocked(false); lastActiveRef.current = Date.now(); }} />;
  }

  if (!role) {
    const hasInviteParam = !!searchParams.get('invite');
    if (!showAuth && !hasInviteParam) {
      return (
        <ErrorBoundary>
          <Suspense fallback={<div style={{ minHeight:'100vh', background:'#070c1a' }} />}>
            <PublicPage onSignIn={() => setShowAuth(true)} />
          </Suspense>
        </ErrorBoundary>
      );
    }
    return <AuthGate onSuccess={({ role: r, name: n, portals: p }) => {
      if (r === "__needs_bootstrap__") {
        setRole("__needs_bootstrap__");
      } else {
        const uid = auth.currentUser?.uid || "";
        setRole(r); setUserPortals(p && p.length ? p : (r ? [r] : [])); setUserName(n || ""); setUserId(uid);
        setShowAuth(false);
        // Register ops/admin in team_members so CrossTeamLinker can find them
        if ((r === 'ops' || r === 'admin') && n) {
          registerMember(db, n, r, uid).catch(() => {});
        }
        // Write session token for multi-device logout tracking
        if (uid) {
          const token = genToken();
          try { localStorage.setItem(SESSION_KEY, token); } catch(_) {}
          writeSessionToken(uid, token).catch(() => {});
          warmCache(uid); // fire-and-forget cache warm
        }
        // Clear any stale search params so back button doesn't return to login screen
        setSearchParams({}, { replace: true });
      }
    }} onBack={!hasInviteParam ? () => setShowAuth(false) : undefined} />;
  }
  if (role === "__needs_bootstrap__") {
    return <AuthGate onSuccess={({ role: r, name: n }) => { setRole(r); setUserName(n || ""); }} forceBootstrap />;
  }

  // ── Maintenance mode ──────────────────────────────────────────────────────
  if (appConfig.maintenance?.enabled && !(appConfig.maintenance?.allowedRoles || ['admin']).includes(role)) {
    return (
      <div style={{ minHeight: '100vh', background: '#070c1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
        <div style={{ fontSize: 72, marginBottom: 28 }}>🛰️</div>
        <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 28, color: '#65d4fa', letterSpacing: 3, marginBottom: 20 }}>
          WE'LL BE RIGHT BACK
        </div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 15, color: '#bdc8ce', maxWidth: 440, lineHeight: 1.8, marginBottom: 14 }}>
          {appConfig.maintenance.message || "We're performing scheduled maintenance. We'll be back shortly."}
        </div>
        {appConfig.maintenance.estimatedEndTime && (
          <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 11, color: '#64748b' }}>
            Estimated return: {appConfig.maintenance.estimatedEndTime}
          </div>
        )}
      </div>
    );
  }

  // ── Portal membership (used by Ops + department members) ───────────────────
  // Admin-created departments (departments/{id}) count as portals too, so their
  // members get the same department workspace as built-in portal members.
  const deptCard = pid => {
    const d = departments.find(x => x.id === pid);
    return d && {
      id: d.id, label: d.name || d.id, icon: '🏢', color: d.color || '#6366f1',
      path: `/dept/${d.id}`, collection: 'dept_posts', deptId: d.id,
      desc: d.description || d.subtitle || '',
    };
  };
  const myPortalIds = (userPortals && userPortals.length ? userPortals : [role]).filter(Boolean);
  const myPortalCards = myPortalIds.map(pid => PORTAL_CARDS.find(p => p.id === pid) || deptCard(pid)).filter(Boolean);
  const choiceUid = portalChoice.split(':')[0];
  const choicePid = portalChoice.slice(choiceUid.length + 1);
  const chosenPortalId = choiceUid === userId && myPortalCards.some(p => p.id === choicePid) ? choicePid : null;
  const multiPortal = role !== 'admin' && role !== 'guest' && role !== 'investor' && myPortalCards.length > 1;
  const choosePortal = pid => {
    const v = pid ? `${userId}:${pid}` : '';
    setPortalChoice(v);
    try { if (v) sessionStorage.setItem('smc_portal_choice', v); else sessionStorage.removeItem('smc_portal_choice'); } catch (_) {}
  };

  // Hold routing until departments load when the user belongs to one, so a
  // dept member isn't dropped into a portal and then bounced to the chooser.
  const portalsPending = !departmentsLoaded && role !== 'admin'
    && myPortalIds.some(pid => !PORTAL_CARDS.some(p => p.id === pid));
  if (portalsPending) {
    return (
      <div style={{ minHeight:'100vh', background:theme.bg, display:'flex', alignItems:'center', justifyContent:'center',
        fontFamily:'monospace', fontSize:11, color:theme.muted, letterSpacing:3 }}>LOADING…</div>
    );
  }

  // Members of 2+ portals pick which one to enter first
  if (multiPortal && !chosenPortalId) {
    return (
      <ThemeContext.Provider value={{ ...theme }}>
        <PortalChooser portals={myPortalCards} onChoose={choosePortal} userName={userName}
          themeId={themeId} onThemeChange={handleThemeChange}
          onSignOut={doSignOut} onSignOutAll={doSignOutAll} />
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </ThemeContext.Provider>
    );
  }

  // ── Operations Officer ────────────────────────────────────────────────────────
  if (role === "ops" && (!multiPortal || chosenPortalId === "ops")) {
    return (
      <ThemeContext.Provider value={{ ...theme }}>
        <OpsView
          userName={userName}
          userId={userId}
          themeId={themeId}
          onThemeChange={handleThemeChange}
          onSignOut={doSignOut}
          onSignOutAll={doSignOutAll}
          appVersion={appVersion}
          appVersionDate={appVersionDate}
          notifications={notifications}
          onMarkRead={markAllRead}
          onMarkOneRead={markOneRead}
          portals={multiPortal ? myPortalCards : undefined}
          onChoosePortal={multiPortal ? choosePortal : undefined}
        />
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </ThemeContext.Provider>
    );
  }

  // ── Guest / Investor / Reviewer: read + comment access to approved reports ──
  if (role === 'guest' || role === 'investor') {
    return (
      <Suspense fallback={<div style={{ minHeight:'100vh', background:'#070c1a', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'monospace', fontSize:11, color:'#65d4fa', letterSpacing:3 }}>LOADING…</div>}>
        <GuestPortal userId={userId} userName={userName} role={role} onSignOut={doSignOut} />
      </Suspense>
    );
  }

  // ── Portal team member: show department-only view ───────────────────────────
  const deptPortal = myPortalCards.find(p => p.id === chosenPortalId) || myPortalCards[0] || PORTAL_CARDS.find(p => p.id === role);
  // Admins keep the full app even if they're listed as a member of a portal
  if (deptPortal && role !== 'admin') {
    return (
      <ThemeContext.Provider value={{ ...theme }}>
        <DepartmentView
          portal={deptPortal}
          portals={myPortalCards.length > 1 ? myPortalCards : undefined}
          activePortalId={multiPortal ? chosenPortalId : undefined}
          onChoosePortal={multiPortal ? choosePortal : undefined}
          userName={userName}
          userId={userId}
          role={role}
          themeId={themeId}
          onThemeChange={handleThemeChange}
          onSignOut={doSignOut}
          onSignOutAll={doSignOutAll}
          appVersion={appVersion}
          appVersionDate={appVersionDate}
          notifications={notifications}
          onMarkRead={markAllRead}
          onMarkOneRead={markOneRead}
        />
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={{ ...theme }}>
      <div className={`smc-app-shell${userId && role ? ' has-att-bar' : ''}`} style={{
        background: theme.bg, minHeight: "100vh",
        color: theme.text, position: "relative",
        fontFamily: theme.font,
      }}>
        <Stars visible={theme.dark} />

        {/* Main nav tabs (only when not in a sub-page) */}
        {!current && role !== "embedded" && (() => {
          const navItems = [
            ["home",        "HOME"],
            ...(appConfig.features.socialFeedEnabled !== false ? [["daily-log", "MISSION LOG"]] : []),
            ["team",        "TEAM"],
            ["reports",     "📑 REPORTS"],
            ["requests",    "REQUESTS"],
            ["my-experiments", "🧪 EXPERIMENTS"],
            ["shared",      "SHARED"],
            ["my-day",      "MY DAY"],
            ["mytasks",     "📅 DAILY TASKS"],
            ["missions",    "🛰 MISSIONS"],
            ["mr1-tracker", "🚀 MR-1"],
            ["physics",     "PHYSICS"],
            ["simulations", "SIMULATIONS"],
            ["dashboard",   "DASHBOARD"],
            ...(appConfig.features.attendanceEnabled !== false ? [["attendance", "ATTENDANCE"]] : []),
            ["announcements", "ANNOUNCEMENTS"],
            ["interns",       "🎓 INTERNS"],
            ["backup",        "🗄 BACKUP"],
            ...((canAccessDataRoom) ? [["data-room", "🗄 DATA ROOM"]] : []),
            ...(appConfig.features.meetingsEnabled !== false ? [["meetings", "MEETINGS"]] : []),
            ["mail",        "MAIL"],
            ...(appConfig.features.chatEnabled !== false ? [["chat", "CHAT"]] : []),
            ...(role === "admin" ? [["notifications", "🔔 NOTIFICATIONS"], ["lock-settings", "LOCK"]] : []),
          ];
          const unacked = myExperiments.filter(e => !(e.acknowledgedByUids||[]).includes(userId)).length;
          const currentLabel = mainTab === 'my-experiments'
            ? '🧪 EXPERIMENTS'
            : (navItems.find(([v]) => v === mainTab)?.[1] || 'MENU');

          const renderNavButton = ([v, l], opts = {}) => {
            const isExp = v === 'my-experiments';
            const active = mainTab === v;
            const accent = isExp ? '#D85A30' : theme.accent;
            return (
              <button key={v} onClick={() => switchTab(v)}
                className={opts.drawer ? 'smc-nav-drawer-item' : undefined}
                style={{
                  padding: opts.drawer ? '12px 14px' : '0 11px',
                  fontFamily:'monospace', fontSize: opts.drawer ? 10 : 9,
                  cursor:'pointer', letterSpacing:1, whiteSpace:'nowrap',
                  background: opts.drawer
                    ? (active ? `${accent}18` : 'transparent')
                    : 'transparent',
                  border: opts.drawer
                    ? `0.5px solid ${active ? accent : theme.border}`
                    : 'none',
                  borderRadius: opts.drawer ? 8 : 0,
                  flexShrink:0, textAlign: opts.drawer ? 'left' : 'center',
                  width: opts.drawer ? '100%' : undefined,
                  borderBottom: opts.drawer ? undefined : `2px solid ${active ? accent : 'transparent'}`,
                  color: active ? accent : theme.muted,
                  position: isExp && !opts.drawer ? 'relative' : undefined,
                }}>
                {l}
                {isExp && unacked > 0 && !opts.drawer && (
                  <span style={{
                    position:'absolute', top:8, right:4,
                    width:6, height:6, borderRadius:'50%',
                    background:'#fbbf24', display:'block',
                  }} />
                )}
                {isExp && unacked > 0 && opts.drawer && (
                  <span style={{
                    marginLeft:8, fontSize:8, padding:'1px 6px', borderRadius:4,
                    background:'#fbbf2420', color:'#fbbf24', border:'1px solid #fbbf2444',
                  }}>{unacked}</span>
                )}
              </button>
            );
          };

          const navActions = (
            <>
              {/* Admin NOTIFY TEST shortcut */}
              {role === 'admin' && (
                <button
                  className="smc-hide-mobile"
                  onClick={() => setNotifyTestOpen(o => !o)}
                  title="Notification Test Panel"
                  style={{
                    padding:'3px 8px', fontFamily:'monospace', fontSize:8, cursor:'pointer',
                    letterSpacing:1, whiteSpace:'nowrap', background:'#BA751710',
                    border:`0.5px solid ${notifyTestOpen ? '#BA7517' : '#BA751740'}`,
                    color:'#BA7517', borderRadius:4, flexShrink:0,
                  }}
                >🔔 TEST</button>
              )}

              {/* Version badge — build version is source of truth */}
              <span className="smc-hide-tablet" style={{
                fontFamily:'monospace', fontSize:9, letterSpacing:1,
                color: theme.muted, padding:'2px 6px',
                border:`1px solid ${theme.border}`, borderRadius:4,
                userSelect:'none', whiteSpace:'nowrap',
              }} title={appVersionDate ? `Updated ${appVersionDate.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}` : `Build v${APP_VERSION}`}>
                v{APP_VERSION}
              </span>

              <SyncDot status={syncStatus} />

              <NotificationBell notifications={notifications} onMarkRead={markAllRead} onMarkOneRead={markOneRead} role={role} userId={userId} />

              {/* Theme quick-switch */}
              <ThemeDropdown themeId={themeId} onChange={handleThemeChange} />

              {/* ⚙ Settings button (v2: avatar + name, same menu) */}
              {designV2 ? (
                <button type="button"
                  className={`smc2-user-btn${settingsOpen ? ' is-active' : ''}`}
                  onClick={() => { setSettingsOpen(o => !o); setNavMenuOpen(false); }}
                  title="Settings & account" aria-expanded={settingsOpen}>
                  <span className="smc2-avatar" style={{ background:`${theme.accent}22`, color:theme.accent }}>
                    {(userName || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                  </span>
                  <span className="smc2-user-name">
                    <b>{userName}</b>
                    <small>{role === 'admin' ? 'ADMIN' : role}</small>
                  </span>
                </button>
              ) : (
              <button
                onClick={() => { setSettingsOpen(o => !o); setNavMenuOpen(false); }}
                style={{
                  background: settingsOpen ? `${theme.accent}20` : 'transparent',
                  border:`1px solid ${settingsOpen ? theme.accent : theme.border}`,
                  borderRadius:6, padding:'4px 8px', cursor:'pointer',
                  color: settingsOpen ? theme.accent : theme.muted,
                  fontFamily:'monospace', fontSize:11, lineHeight:1,
                }}
                title="Settings"
              >⚙</button>

              )}

              {/* Settings dropdown */}
              {settingsOpen && (
                <div style={{
                  position:'absolute', top:'calc(100% + 8px)', right:0,
                  background:`${theme.panel}f8`, backdropFilter:'blur(16px)',
                  border:`1px solid ${theme.border}`, borderRadius:12,
                  padding:16, minWidth:220, zIndex:200,
                  boxShadow:'0 8px 32px #00000055',
                }}
                  onMouseLeave={() => setSettingsOpen(false)}
                >
                  {/* Appearance */}
                  <div style={{ fontFamily:'monospace', fontSize:8, color:theme.muted,
                    letterSpacing:2, marginBottom:10 }}>APPEARANCE</div>
                  {/* Quick 3-card SMC theme grid */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginBottom:10 }}>
                    {SMC_THEME_LIST.map(tid => {
                      const sw = THEME_SWATCHES[tid];
                      const t  = SMC_THEMES[tid];
                      const isActive = tid === themeId;
                      return (
                        <div key={tid}
                          onClick={() => { handleThemeChange(tid); setSettingsOpen(false); }}
                          style={{
                            cursor:'pointer', borderRadius:8, overflow:'hidden',
                            border:`2px solid ${isActive ? theme.accent : theme.border}`,
                            transition:'border-color 0.15s',
                          }}>
                          <div style={{ display:'flex', height:28 }}>
                            {sw.map((c,i) => <div key={i} style={{ flex:1, background:c }} />)}
                          </div>
                          <div style={{ padding:'4px 6px', background:isActive ? `${theme.accent}18` : 'transparent' }}>
                            <div style={{ fontFamily:'monospace', fontSize:8,
                              color: isActive ? theme.accent : theme.muted, letterSpacing:1 }}>
                              {t.name}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Classic theme picker for all themes */}
                  <ThemePicker themeId={themeId} onChange={handleThemeChange} inline={true} />

                  {/* Design version — new (v2) or classic (v1) look; data is unaffected */}
                  <button type="button"
                    onClick={() => setDesignVersion(designV2 ? 'v1' : 'v2')}
                    style={{ width:'100%', marginTop:10, padding:'7px 12px', fontFamily:'monospace', fontSize:9,
                      letterSpacing:1, cursor:'pointer', background:'transparent', borderRadius:6, textAlign:'left',
                      border:`1px solid ${theme.border}`, color:theme.muted }}>
                    {designV2 ? '↺ Switch to classic design' : '✦ Switch to new design'}
                  </button>

                  {/* View As — admin + ops */}
                  {(role === 'admin' || role === 'ops') && (
                    <>
                      <div style={{ fontFamily:'monospace', fontSize:8, color:theme.muted,
                        letterSpacing:2, margin:'16px 0 10px' }}>
                        {viewAs ? '👁 PREVIEW ACTIVE' : 'VIEW AS'}
                      </div>
                      {viewAs && (
                        <button onClick={() => { exitViewAs(); setSettingsOpen(false); }}
                          style={{ width:'100%', padding:'7px 12px', marginBottom:6,
                            fontFamily:'monospace', fontSize:9, letterSpacing:1, cursor:'pointer',
                            background:'#7c3aed20', border:'1px solid #7c3aed',
                            color:'#c4b5fd', borderRadius:6, textAlign:'left' }}>
                          ✕ Exit Preview
                        </button>
                      )}
                      {/* Admin: all portals. Ops: team portals only (exclude ops/finance/mentor) */}
                      {(role === 'admin'
                        ? PORTAL_CARDS
                        : PORTAL_CARDS.filter(p => !['ops','finance','mentor'].includes(p.id))
                      ).map(p => (
                        <button key={p.id} onClick={() => handleViewAs(p.id)}
                          style={{ width:'100%', padding:'7px 12px', marginBottom:4,
                            fontFamily:'monospace', fontSize:9, letterSpacing:1, cursor:'pointer',
                            background: viewAs===p.id ? `${p.color}20` : 'transparent',
                            border:`1px solid ${viewAs===p.id ? p.color : theme.border}`,
                            color: viewAs===p.id ? p.color : theme.muted, borderRadius:6, textAlign:'left' }}>
                          {p.icon} {role === 'ops' ? '👁 View as — ' : 'Team Member — '}{p.label}
                        </button>
                      ))}
                      {/* Admin only: can also view as Ops / C-Suite */}
                      {role === 'admin' && (
                        <button onClick={() => handleViewAs('ops_role')}
                          style={{ width:'100%', padding:'7px 12px', marginBottom:4,
                            fontFamily:'monospace', fontSize:9, letterSpacing:1, cursor:'pointer',
                            background: viewAs==='ops_role' ? '#e11d4820' : 'transparent',
                            border:`1px solid ${viewAs==='ops_role' ? '#e11d48' : theme.border}`,
                            color: viewAs==='ops_role' ? '#e11d48' : theme.muted, borderRadius:6, textAlign:'left' }}>
                          🚀 Ops / C-Suite
                        </button>
                      )}
                      {role === 'ops' && (
                        <div style={{ fontFamily:'monospace', fontSize:8, color:theme.muted,
                          marginTop:6, padding:'4px 4px', letterSpacing:0.5, lineHeight:1.5 }}>
                          Read-only. No edits or posts in preview.
                        </div>
                      )}
                    </>
                  )}

                  {/* Account */}
                  <div style={{ fontFamily:'monospace', fontSize:8, color:theme.muted,
                    letterSpacing:2, margin:'16px 0 10px' }}>ACCOUNT · {userName}</div>
                  <button onClick={() => { setSettingsOpen(false); doSignOut(); }}
                    style={{ width:'100%', padding:'8px 12px', marginBottom:6,
                      fontFamily:'monospace', fontSize:9, letterSpacing:1, cursor:'pointer',
                      background:'transparent', border:`1px solid ${theme.border}`,
                      color:theme.muted, borderRadius:6, textAlign:'left' }}>
                    Sign Out
                  </button>
                  <button onClick={() => { setSettingsOpen(false); doSignOutAll(); }}
                    style={{ width:'100%', padding:'8px 12px',
                      fontFamily:'monospace', fontSize:9, letterSpacing:1, cursor:'pointer',
                      background:'#f43f5e10', border:'1px solid #f43f5e44',
                      color:'#f43f5e', borderRadius:6, textAlign:'left' }}>
                    Sign Out All Devices
                  </button>

                  {/* Version info */}
                  <div style={{ fontFamily:'monospace', fontSize:8, color:theme.muted,
                    letterSpacing:2, margin:'16px 0 6px' }}>VERSION</div>
                  <div style={{ fontFamily:'monospace', fontSize:10, color:theme.accent,
                    background:`${theme.accent}10`, border:`1px solid ${theme.border}`,
                    padding:'6px 10px', borderRadius:6 }}>
                    v{appVersion ?? '…'}
                    {appVersionDate
                      ? ` · updated ${appVersionDate.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`
                      : ''}
                    {appVersionUpdatedBy && (
                      <span style={{ color:theme.muted, fontSize:9 }}> by {appVersionUpdatedBy}</span>
                    )}
                  </div>

                  {/* Admin: changelog entries */}
                  {role === 'admin' && changelogEntries.length > 0 && (
                    <>
                      <div style={{ fontFamily:'monospace', fontSize:8, color:theme.muted,
                        letterSpacing:1, margin:'10px 0 5px' }}>RECENT CHANGES</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4,
                        maxHeight:180, overflowY:'auto' }}>
                        {changelogEntries.map(entry => (
                          <div key={entry.id} style={{ fontFamily:'monospace', fontSize:9,
                            background:theme.bg, border:`1px solid ${theme.border}`,
                            padding:'6px 8px', borderRadius:4 }}>
                            <span style={{ color:theme.accent, fontWeight:700 }}>v{entry.version}</span>
                            <span style={{ color:theme.muted }}> — </span>
                            <span style={{ color:theme.text }}>{entry.reason}</span>
                            <div style={{ fontSize:8, color:theme.muted, marginTop:3 }}>
                              {entry.updatedBy}
                              {entry.updatedAt?.toDate
                                ? ` · ${entry.updatedAt.toDate().toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`
                                : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          );

          if (designV2) {
            const MORE_IDS = ['my-experiments','shared','my-day','physics','simulations','dashboard','interns','backup','notifications','lock-settings'];
            return (
              <ShellHeaderV2
                label={role === 'admin' ? 'ADMIN · MISSION CONTROL' : 'MISSION CONTROL'}
                accent={theme.accent}
                tabs={navItems.filter(([v]) => !MORE_IDS.includes(v))}
                moreTabs={navItems.filter(([v]) => MORE_IDS.includes(v))}
                activeTab={mainTab}
                onTab={switchTab}
                onLogo={() => switchTab('home')}
                dots={{ ...sectionBadges, 'my-experiments': unacked }}
                actions={navActions}
              />
            );
          }

          return (
          <>
          <div className="smc-main-nav" style={{ position:"sticky", top:0, zIndex:90, background:`${theme.bg}f0`,
            backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",
            borderBottom:`1px solid ${theme.border}`,
            display:"flex", alignItems:"stretch", minHeight:46 }}>

            {/* Logo — always visible, left-pinned */}
            <div
              className="smc-nav-logo"
              onClick={() => switchTab("home")}
              style={{
                cursor:"pointer", flexShrink:0, padding:"0 12px",
                display:"flex", alignItems:"center",
                background: theme.dark ? "#000" : "transparent",
                borderRight:`1px solid ${theme.border}`,
              }}>
              <img
                src={theme.dark ? "/logo-dark.png" : "/logo-horizontal.jpg"}
                alt="Spacemancraft"
                style={{ height: theme.dark ? 30 : 26, width:"auto", objectFit:"contain", display:"block" }}
              />
            </div>

            {/* Mobile menu toggle + current page label */}
            <button
              type="button"
              className="smc-nav-menu-btn"
              aria-expanded={navMenuOpen}
              aria-label={navMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={() => { setNavMenuOpen(o => !o); setSettingsOpen(false); }}
              style={{
                borderRight:`1px solid ${theme.border}`,
                color: theme.text,
              }}>
              <span style={{ fontSize:16, lineHeight:1, color: theme.accent }}>{navMenuOpen ? '✕' : '☰'}</span>
              <span className="smc-nav-current-label" style={{
                fontSize:9, letterSpacing:1, color: theme.accent,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              }}>{currentLabel}</span>
            </button>

            {/* Desktop tab strip */}
            <div className="smc-nav-tabs smc-nav-tabs-desktop">
              <style>{`.nav-scroll::-webkit-scrollbar{display:none}`}</style>
              <div className="nav-scroll smc-nav-tabs-inner" style={{ display:"flex", alignItems:"stretch", gap:0 }}>
                {navItems.map(item => renderNavButton(item))}
              </div>
            </div>

            {/* Right side — version + bell + settings, always visible */}
            <div className="smc-nav-actions" style={{ flexShrink:0, display:'flex', alignItems:'center', gap:6,
              padding:'0 10px', borderLeft:`1px solid ${theme.border}`, position:'relative' }}>

              {navActions}
            </div>
          </div>

          {navMenuOpen && (
            <>
              <button
                type="button"
                className="smc-nav-drawer-backdrop"
                aria-label="Close navigation menu"
                onClick={() => setNavMenuOpen(false)}
              />
              <div className="smc-nav-drawer" style={{
                background: `${theme.panel}fa`,
                borderBottom: `1px solid ${theme.border}`,
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
              }}>
                <div className="smc-nav-drawer-grid">
                  {navItems.map(item => renderNavButton(item, { drawer: true }))}
                </div>
              </div>
            </>
          )}
          </>
          );
        })()}

        {/* Global incoming call overlay — shown in all tabs */}
        {incomingCall && (
          <IncomingCallOverlay
            call={incomingCall}
            onDecline={async () => {
              try {
                const { updateDoc, doc: fsDoc } = await import('firebase/firestore');
                await updateDoc(fsDoc(db, 'calls', incomingCall.id), { status: 'declined' });
              } catch (_) {}
              setIncomingCall(null);
            }}
            onAccept={() => {
              switchTab('meetings');
              setIncomingCall(null);
            }}
          />
        )}

        {/* Task reminder service */}
        <TaskReminderService userId={userId} role={role} />

        {/* "View As" preview banner */}
        {viewAs && (
          <div style={{ position:'sticky', top:'var(--smc-nav-h, 46px)', zIndex:95,
            background: role === 'ops' ? '#0e7490' : '#7c3aed', padding:'6px 16px',
            display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
            fontFamily:'monospace', fontSize:9, letterSpacing:1.5 }}>
            <span style={{ color:'#e0f2fe' }}>
              👁 {role === 'ops' ? 'READ-ONLY PREVIEW' : 'ADMIN PREVIEW'}
              {' · '}
              {viewAs === 'ops_role'
                ? 'OPS / C-SUITE'
                : (PORTAL_CARDS.find(p=>p.id===viewAs)?.label || viewAs).toUpperCase()}
              {viewAsPortalId && (
                <> · viewing as <strong style={{ color:'#fff' }}>{viewAsPreviewLabel}</strong></>
              )}
              {role === 'ops' && ' · No edits allowed'}
            </span>
            {viewAsPortalId && viewAsMembers.length > 0 && (
              <select
                value={viewAsUserId}
                onChange={e => setViewAsUserId(e.target.value)}
                style={{ fontFamily:'monospace', fontSize:9, padding:'4px 8px',
                  background:'#ffffff18', border:'1px solid #ffffff40', color:'#e0f2fe',
                  borderRadius:4, cursor:'pointer', maxWidth:220 }}>
                <option value="">Generic team member</option>
                {viewAsMembers.map(m => (
                  <option key={m.uid} value={m.uid}>{m.name || m.email || m.uid}</option>
                ))}
              </select>
            )}
            <button onClick={exitViewAs}
              style={{ marginLeft:'auto', background:'#ffffff20', border:'1px solid #ffffff40',
                color:'#e0f2fe', fontFamily:'monospace', fontSize:9, padding:'3px 10px',
                cursor:'pointer', borderRadius:4, letterSpacing:1 }}>
              EXIT PREVIEW ✕
            </button>
          </div>
        )}

        {/* Startup checks — install prompt + notification permission */}
        <StartupChecks userId={userId} role={role} />

        {/* Announcement banners */}
        {role && userId && (
          <AnnouncementBanner userId={userId} userRole={role} />
        )}

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* "View As" preview rendering */}
          {viewAs && role === 'admin'
            ? viewAs === 'ops_role'
              ? <OpsView userName={userName} userId={userId} themeId={themeId}
                  onThemeChange={handleThemeChange} onSignOut={doSignOut} onSignOutAll={doSignOutAll}
                  appVersion={appVersion} appVersionDate={appVersionDate} />
              : viewAsDepartmentPreview
                ? <DepartmentView
                    portal={viewAsDepartmentPreview.portal}
                    userName={viewAsDepartmentPreview.userName}
                    userId={viewAsDepartmentPreview.userId}
                    role={viewAsDepartmentPreview.role}
                    profileUserId={viewAsDepartmentPreview.profileUserId}
                    allowProfileEdit={viewAsDepartmentPreview.allowProfileEdit}
                    themeId={themeId}
                    onThemeChange={handleThemeChange}
                    onSignOut={doSignOut}
                    onSignOutAll={doSignOutAll}
                    appVersion={appVersion}
                    appVersionDate={appVersionDate}
                  />
                : <DepartmentView portal={PORTAL_CARDS.find(p=>p.id===viewAs) || PORTAL_CARDS[0]}
                    userName={userName} userId={userId} role={role} themeId={themeId}
                    onThemeChange={handleThemeChange} onSignOut={doSignOut} onSignOutAll={doSignOutAll}
                    appVersion={appVersion} appVersionDate={appVersionDate} />
          : viewAs && role === 'ops'
            ? viewAsDepartmentPreview
              ? <DepartmentView
                  portal={viewAsDepartmentPreview.portal}
                  userName={viewAsDepartmentPreview.userName}
                  userId={viewAsDepartmentPreview.userId}
                  role={viewAsDepartmentPreview.role}
                  profileUserId={viewAsDepartmentPreview.profileUserId}
                  allowProfileEdit={false}
                  themeId={themeId}
                  onThemeChange={handleThemeChange}
                  onSignOut={doSignOut}
                  onSignOutAll={doSignOutAll}
                  appVersion={appVersion}
                  appVersionDate={appVersionDate}
                  readOnly={true}
                />
              : <DepartmentView
                  portal={PORTAL_CARDS.find(p=>p.id===viewAs) || PORTAL_CARDS[0]}
                  userName={userName} userId={userId} role={role} themeId={themeId}
                  onThemeChange={handleThemeChange} onSignOut={doSignOut} onSignOutAll={doSignOutAll}
                  appVersion={appVersion} appVersionDate={appVersionDate}
                  readOnly={true} />
          : current
            ? <PageShell page={page} visits={visits[current] || 1} onBack={() => { if (role !== "embedded") window.history.back(); }} userName={userName} role={role} />
            : [...visitedTabs].map(tab => (
                <TabKeepAlive key={tab} show={mainTab === tab}>
                  {renderMainTab(tab)}
                </TabKeepAlive>
              ))
          }
        </div>
        {/* Global app footer */}
        <div className="smc-app-footer" style={{ textAlign:'center', padding:'12px 16px',
          borderTop:`1px solid ${theme.border}`, marginTop:40,
          fontFamily:'monospace', fontSize:9, color:theme.muted,
          letterSpacing:1, userSelect:'none' }}>
          SPACEMANCRAFT SATELLITE-CLASS OS &nbsp;·&nbsp;
          {appVersion != null
            ? `v${appVersion}${appVersionDate ? ` · updated ${appVersionDate.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}` : ''}`
            : 'loading…'}
        </div>

        <ToastContainer toasts={toasts} onDismiss={dismissToast} />

        {/* Admin Notify Test slide-over */}
        {notifyTestOpen && role === 'admin' && (
          <NotifyTestPanel
            userId={userId}
            userName={userName}
            onClose={() => setNotifyTestOpen(false)}
          />
        )}

        {/* Daily Log FAB — always visible when authenticated */}
        {userId && role && (
          <DailyLogFAB userId={userId} onOpenComposer={() => setShowQuickPost(true)} />
        )}

        {/* Global Attendance Bar — fixed bottom, all pages */}
        {userId && role && (
          <GlobalAttendanceBar
            currentUser={{ uid: userId, name: userName, role }}
            onGoToAttendance={() => switchTab('attendance')}
          />
        )}

        {/* Quick Post Drawer — global overlay */}
        {showQuickPost && (
          <Suspense fallback={null}>
            <QuickPostDrawer
              userId={userId} userName={userName} userRole={role}
              onClose={() => setShowQuickPost(false)}
              onPost={() => { setShowQuickPost(false); if (mainTab === 'daily-log') switchTab('daily-log'); }}
            />
          </Suspense>
        )}
      </div>
    </ThemeContext.Provider>
  );
}

// ── Admin: Notify Test slide-over panel ────────────────────────────────────────
const TEST_EVENT_TYPES = [
  'PORTAL_THREAD_MESSAGE',
  'PORTAL_REPORT_POSTED',
  'PORTAL_REPORT_APPROVED',
  'EXPERIMENT_RESULT_PUBLISHED',
  'EXPERIMENT_STATUS_CHANGED',
  'TASK_ASSIGNED',
  'SALARY_SLIP_GENERATED',
  'ANNOUNCEMENT',
  'BADGE_EARNED',
  'ACCESS_REQUEST_SUBMITTED',
  'ACCESS_APPROVED',
];

function NotifyTestPanel({ userId, userName, onClose }) {
  const [eventType,  setEventType]  = useState(TEST_EVENT_TYPES[0]);
  const [sending,    setSending]    = useState(false);
  const [lastResult, setLastResult] = useState('');

  async function sendTest() {
    setSending(true);
    setLastResult('');
    try {
      await pushNotificationEvent({
        eventType,
        sourceId:   'TEST-' + Date.now(),
        sourceType: 'test',
        payload:    { testMode: true, sentBy: userName },
        actorUid:   userId,
        actorName:  userName + ' (test)',
      });
      setLastResult('✓ Queued — check bell & push');
    } catch (err) {
      setLastResult('✗ ' + err.message);
    } finally { setSending(false); }
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:2000, display:'flex', justifyContent:'flex-end' }}>
      <div onClick={onClose} style={{ flex:1, background:'rgba(0,0,0,0.5)' }} />
      <div style={{
        width:320, background:'var(--smc-panel, #0d1526)', borderLeft:'1px solid var(--smc-bd, rgba(255,255,255,0.08))',
        display:'flex', flexDirection:'column', boxShadow:'-8px 0 32px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 16px', borderBottom:'1px solid rgba(255,255,255,0.08)', flexShrink:0,
        }}>
          <div style={{ fontFamily:'monospace', fontSize:9, color:'#BA7517', letterSpacing:2 }}>
            🔔 NOTIFY TEST PANEL
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:18 }}>
            ×
          </button>
        </div>

        <div style={{ padding:16, flex:1, overflowY:'auto' }}>
          <div style={{ fontFamily:'monospace', fontSize:8, color:'#64748b', letterSpacing:1, marginBottom:8 }}>
            EVENT TYPE
          </div>
          <select
            value={eventType}
            onChange={e => setEventType(e.target.value)}
            style={{
              width:'100%', background:'var(--smc-deep, #060b18)', border:'1px solid var(--smc-bd, rgba(255,255,255,0.08))',
              color:'var(--smc-tx, #d9e2ff)', padding:'8px 10px', fontFamily:'monospace', fontSize:10,
              marginBottom:16, boxSizing:'border-box', borderRadius:4,
            }}
          >
            {TEST_EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <div style={{
            fontFamily:'monospace', fontSize:8, color:'var(--smc-tx3, #64748b)', lineHeight:1.7,
            background:'var(--smc-hov, rgba(255,255,255,0.02))', border:'1px solid var(--smc-bd, rgba(255,255,255,0.06))',
            padding:'10px 12px', borderRadius:4, marginBottom:16,
          }}>
            <div>Actor: <span style={{ color:'#65d4fa' }}>{userName}</span></div>
            <div>Payload: <span style={{ color:'#65d4fa' }}>{'{ testMode: true }'}</span></div>
            <div style={{ color:'#fbbf24', marginTop:4 }}>
              The Cloud Function router will resolve recipients from the event type.
              Check your notification bell and browser push after sending.
            </div>
          </div>

          <button
            onClick={sendTest}
            disabled={sending}
            style={{
              width:'100%', padding:'10px', fontFamily:'monospace', fontSize:9,
              letterSpacing:1, cursor:sending ? 'wait' : 'pointer',
              background:'#BA751715', border:'1px solid #BA7517',
              color:'#BA7517', borderRadius:6,
            }}
          >
            {sending ? 'QUEUING…' : 'SEND TEST NOTIFICATION →'}
          </button>

          {lastResult && (
            <div style={{
              marginTop:12, fontFamily:'monospace', fontSize:10,
              color: lastResult.startsWith('✓') ? '#34d399' : '#f87171',
              textAlign:'center',
            }}>
              {lastResult}
            </div>
          )}
        </div>

        <div style={{
          padding:'10px 16px', borderTop:'1px solid rgba(255,255,255,0.06)',
          fontFamily:'monospace', fontSize:8, color:'#4d5a6e', lineHeight:1.6,
        }}>
          Events go to notifications_queue → Cloud Function routes to recipients → in-app bell + FCM push.
          Allow ~5s for the function to process.
        </div>
      </div>
    </div>
  );
}

